// build: 2026-09-02T14:48:00
import { useState, useEffect, useRef, memo, useCallback } from "react";
import { createPortal } from "react-dom";
import { supabase } from "./supabase";
import {
  badgeKey,
  badgesForOwner,
  BADGE_RIBBONS,
  backfillVouchBadges,
  itemBadgesForOwner,
  loadBadgesForUser,
  loadBadgesForUsers,
  primaryBadge,
  revokeBoardItemClaims,
  revokeClaimsForBoard,
  syncClaimsAfterPublish,
  enrichGroupVouchItems,
} from "./badges";
import {
  loadUserBadges,
  userBadgeExplain,
  userBadgeRibbon,
  userBadgeTailColor,
} from "./userBadges";
import {
  justWatchSearchUrl,
  pickBookIsbn,
  fetchJustWatchTitleUrl,
  fetchBookStoreUrl,
  fetchAppleMusicUrl,
  isMusicCategory,
  openTileLink,
  tileIsClickable,
  tileActionHint,
  normalizeTileItem,
} from "./tileLinks";
import {
  stopMusicPreview,
  fetchMusicPreview,
  getCachedMusicPreview,
  getMusicPreviewState,
  previewItemKey,
  subscribeMusicPreview,
  toggleMusicPreview,
} from "./musicPreview";
import {
  fetchDisplayVouchBoard,
  healDuplicateActiveBoards,
  pickDisplayVouchBoard,
} from "./vouchBoards";
import {
  fetchTrailer,
  getCachedTrailer,
  isFilmCategory,
  tileMediaActionStyle,
  trailerItemKey,
} from "./trailerPreview";

function TrailerModal({ youtubeKey, title, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="trailer-overlay" onClick={onClose}>
      <div className="trailer-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="trailer-close" onClick={onClose} aria-label="Close trailer">×</button>
        <div className="trailer-frame-wrap">
          <iframe
            title={`${title || "Film"} trailer`}
            src={`https://www.youtube-nocookie.com/embed/${youtubeKey}?autoplay=1&rel=0&modestbranding=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {title && <div className="trailer-caption">{title}</div>}
      </div>
    </div>,
    document.body
  );
}

function PreviewIcon({ playing, size = 8 }) {
  if (playing) {
    return (
      <svg width={size} height={size} viewBox="0 0 8 8" fill="currentColor" aria-hidden style={{ flexShrink: 0 }}>
        <rect x="1" y="0.5" width="2" height="7" />
        <rect x="5" y="0.5" width="2" height="7" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 8 8" fill="currentColor" aria-hidden style={{ flexShrink: 0 }}>
      <path d="M1.5 0.5 L7 4 L1.5 7.5 Z" />
    </svg>
  );
}

function TileInlineButton({ size = "md", className = "", icon, label, onClick, stacked = false, ...props }) {
  const { fontSize, iconSize, padY, padX, gap } = tileMediaActionStyle(size);
  return (
    <button
      type="button"
      className={`tile-inline-btn${stacked ? " tile-inline-btn-stacked" : ""}${className ? ` ${className}` : ""}`}
      style={{ fontSize, padding: `${padY}px ${padX}px`, gap }}
      onClick={onClick}
      {...props}
    >
      {icon}
      {!icon && !stacked && <span className="tile-inline-btn-spacer" style={{ width: iconSize, height: iconSize }} aria-hidden />}
      <span>{label}</span>
    </button>
  );
}

function TilePlayButton({ item, catKey, size = "md", stacked = false }) {
  const key = catKey || item?.category || item?._cat;
  const itemKey = previewItemKey(item, catKey);
  const cached = getCachedMusicPreview(item, catKey);
  const [hasPreview, setHasPreview] = useState(cached === undefined ? null : !!cached);
  const [, tick] = useState(0);

  useEffect(() => subscribeMusicPreview(() => tick((n) => n + 1)), []);

  useEffect(() => {
    if (!isMusicCategory(key) || !item?.title) return;
    const hit = getCachedMusicPreview(item, catKey);
    if (hit !== undefined) {
      setHasPreview(!!hit);
      return;
    }
    let cancelled = false;
    fetchMusicPreview(item, catKey).then((url) => {
      if (!cancelled) setHasPreview(!!url);
    });
    return () => { cancelled = true; };
  }, [item, catKey, key, itemKey]);

  if (!isMusicCategory(key) || !item?.title || hasPreview !== true) return null;

  const { currentKey, loadingKey, playing } = getMusicPreviewState();
  const isLoading = loadingKey === itemKey;
  const isPlaying = playing && currentKey === itemKey;
  const { iconSize } = tileMediaActionStyle(size);

  return (
    <TileInlineButton
      size={size}
      className={isPlaying ? "is-playing" : ""}
      aria-label={isPlaying ? "Pause preview" : "Play 30-second preview"}
      title={isPlaying ? "Pause preview" : "Play preview"}
      label="Preview"
      icon={isLoading ? "…" : <PreviewIcon playing={isPlaying} size={iconSize} />}
      onClick={async (e) => {
        e.stopPropagation();
        await toggleMusicPreview(item, catKey);
      }}
      stacked={stacked}
    />
  );
}

function TileTrailerButton({ item, catKey, size = "md", stacked = false }) {
  const key = catKey || item?.category || item?._cat;
  const itemKey = trailerItemKey(item, catKey);
  const cached = getCachedTrailer(item, catKey);
  const [hasTrailer, setHasTrailer] = useState(cached === undefined ? null : !!cached);
  const [open, setOpen] = useState(false);
  const [youtubeKey, setYoutubeKey] = useState(cached || null);

  useEffect(() => {
    if (!isFilmCategory(key) || !item?.title) return;
    const hit = getCachedTrailer(item, catKey);
    if (hit !== undefined) {
      setHasTrailer(!!hit);
      setYoutubeKey(hit);
      return;
    }
    let cancelled = false;
    fetchTrailer(item, catKey).then((key) => {
      if (!cancelled) {
        setHasTrailer(!!key);
        setYoutubeKey(key);
      }
    });
    return () => { cancelled = true; };
  }, [item, catKey, key, itemKey]);

  if (!isFilmCategory(key) || !item?.title || hasTrailer !== true || !youtubeKey) return null;

  const { iconSize } = tileMediaActionStyle(size);

  return (
    <>
      <TileInlineButton
        size={size}
        aria-label="Watch trailer"
        title="Watch trailer"
        label="Trailer"
        icon={<PreviewIcon playing={false} size={iconSize} />}
        onClick={(e) => {
          e.stopPropagation();
          stopMusicPreview();
          setOpen(true);
        }}
        stacked={stacked}
      />
      {open && <TrailerModal youtubeKey={youtubeKey} title={item.title} onClose={() => setOpen(false)} />}
    </>
  );
}

function TileActionBadge({ item, catKey, onClick, size = "md", stacked = false }) {
  const key = catKey || item?.category || item?._cat;
  if (!tileIsClickable(item, key)) return null;
  const hint = tileActionHint(key);
  if (!hint) return null;
  return (
    <TileInlineButton
      size={size}
      label={hint}
      stacked={stacked}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    />
  );
}

function TileMediaActions({ item, catKey, onOpen, badgeSize = "md", horizontal = false }) {
  const key = catKey || item?.category || item?._cat;
  const { inset, gap } = tileMediaActionStyle(badgeSize);
  return (
    <div
      className={`tile-media-actions${horizontal ? " tile-media-actions-row" : ""}`}
      style={{ bottom: inset, left: inset, ...(horizontal ? { right: inset } : {}), gap }}
    >
      <TilePlayButton item={item} catKey={key} size={badgeSize} stacked={!horizontal} />
      <TileTrailerButton item={item} catKey={key} size={badgeSize} stacked={!horizontal} />
      <TileActionBadge item={item} catKey={key} onClick={onOpen} size={badgeSize} stacked={!horizontal} />
    </div>
  );
}

function TileMedia({ item, catKey, onOpen, poster, title, className, style, placeholderStyle, badgeSize = "md", actionsHorizontal = false, children }) {
  const key = catKey || item?.category || item?._cat;
  const clickable = tileIsClickable(item, key);
  const open = () => { if (clickable) onOpen?.(); };
  return (
    <div className="tile-media" style={style} onClick={() => open()}>
      {children || (poster
        ? <img src={poster} alt={title || ""} className={className} style={{ cursor: clickable ? "pointer" : "default" }} onError={e => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} />
        : <div className={className || "card-poster-placeholder"} style={{ display: "flex", cursor: clickable ? "pointer" : "default", ...placeholderStyle }}>{title}</div>)}
      <TileMediaActions item={item} catKey={key} onOpen={open} badgeSize={badgeSize} horizontal={actionsHorizontal} />
    </div>
  );
}

const AVATAR_OPTIONS = [
  { file: "book",               label: "Book" },
  { file: "cassette",           label: "Cassette" },
  { file: "cassette-deck",      label: "Cassette Deck" },
  { file: "cd",                 label: "CD" },
  { file: "clapper",            label: "Clapper" },
  { file: "drums",              label: "Drums" },
  { file: "dvds",               label: "DVDs" },
  { file: "earbuds",            label: "Earbuds" },
  { file: "film-camera",        label: "Film Camera" },
  { file: "gameboy",            label: "Game Boy" },
  { file: "guitar",             label: "Guitar" },
  { file: "headphones",         label: "Headphones" },
  { file: "microphone-studio",  label: "Studio Mic" },
  { file: "microphone-vintage", label: "Vintage Mic" },
  { file: "pen",                label: "Pen & Paper" },
  { file: "sheet-music",        label: "Sheet Music" },
  { file: "stone-tablet",       label: "Vintage TV" },
  { file: "typewriter",         label: "Typewriter" },
  { file: "vhs",                label: "VHS" },
  { file: "vinyl",              label: "Vinyl" },
];

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  });
}

const TMDB = "24f3b03466f2f7db2d54a0f53607da4f";

const CATEGORIES = [
  { key: "movies",   label: "Film"       },
  { key: "albums",   label: "Albums"     },
  { key: "artists",  label: "Artists"    },
  { key: "songs",    label: "Songs"      },
  { key: "books",    label: "Books"      },
  { key: "shows",    label: "Television" },
  { key: "podcasts", label: "Podcasts"   },
];

const EMPTY_BOARD = {
  movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [],
};

const BOARD_THEMES_BASE = [
  "Feelin' Lately", "All-Timers", "Nostalgic", "Deep Cuts",
  "New Releases", "Underrated", "No Boundaries",
  "Locals Only", "Old School", "Classics", "Guilty Pleasures", "Other",
];

const SEASONAL_ORANGE = "#E8762A";
const SEASONAL_ORANGE_SHADOW = "#B8520F";
const VOUCH_GOLD = "#C9A84C";
const VOUCH_GOLD_SHADOW = "#A07830";

/** Rotating season label (replaces old static "Seasonal"). */
function getCurrentSeasonTheme(date = new Date()) {
  const month = date.getMonth(); // 0 = Jan … 11 = Dec
  if (month === 11 || month <= 1) return "Winter";       // Dec–Feb
  if (month >= 2 && month <= 4) return "Spring";         // Mar–May
  if (month >= 5 && month <= 7) return "Summer";         // Jun–Aug
  return null;                                           // Sep–Nov: fall / year-end themes in picker
}

function getPromotedSeasonalThemes(date = new Date()) {
  const month = date.getMonth();
  const year = date.getFullYear();
  const set = new Set();
  if (month >= 10) set.add(`Best of ${year}`);
  if (month === 8 || month === 9) {
    set.add("Spooky SZN");
    set.add("Horror");
    set.add("Fall Favorites");
    set.add("Halloween");
  }
  const season = getCurrentSeasonTheme(date);
  if (season) set.add(season);
  return set;
}

/** Timed + seasonal themes for publish/edit pickers — updates automatically by calendar. */
function getTimedBoardThemes(date = new Date()) {
  const month = date.getMonth();
  const year = date.getFullYear();
  const timed = [];

  if (month >= 10) timed.push(`Best of ${year}`);
  if (month === 8 || month === 9) timed.push("Spooky SZN", "Horror", "Fall Favorites", "Halloween");

  const seasonTheme = getCurrentSeasonTheme(date);
  const core = BOARD_THEMES_BASE.filter(t => t !== "Other" && t !== seasonTheme && !timed.includes(t));
  const prefix = [...timed];
  if (seasonTheme && !prefix.includes(seasonTheme)) prefix.push(seasonTheme);
  return [...prefix, ...core, "Other"];
}

function boardThemeName(board) {
  return (board?.theme && board.theme !== "Other") ? board.theme : (board?.name || "Vouch");
}

function isOrangeBoardTheme(theme) {
  if (!theme || theme === "Other") return false;
  if (["Spooky SZN", "Spooky Season", "Horror", "Fall Favorites", "Halloween", "Winter", "Spring", "Summer"].includes(theme)) return true;
  return /^Best of \d{4}$/.test(theme);
}

function isHorrorBoardTheme(theme) {
  return theme === "Horror" || theme === "Halloween" || theme === "Spooky SZN" || theme === "Spooky Season";
}

function getVouchSectionClassName(theme) {
  const base = "vouch-section";
  return isOrangeBoardTheme(theme) ? `${base} vouch-section-seasonal` : base;
}

function getVouchThemeLabelStyle(theme) {
  if (isHorrorBoardTheme(theme)) {
    return {
      fontFamily: "'Creepster', cursive",
      fontWeight: 400,
      fontSize: 26,
      letterSpacing: "0.04em",
    };
  }
  return {};
}

function themePickerButtonStyle(t, selected, date = new Date()) {
  const isSeasonal = getPromotedSeasonalThemes(date).has(t);
  const sel = selected === t;
  if (isSeasonal) {
    return {
      fontFamily: "'Spectral SC',serif",
      fontSize: "9px",
      letterSpacing: "0.14em",
      padding: "4px 10px",
      border: `2px solid ${SEASONAL_ORANGE}`,
      background: sel ? T.ink : "transparent",
      color: sel ? T.bg : T.inkMid,
      cursor: "pointer",
    };
  }
  return {
    fontFamily: "'Spectral SC',serif",
    fontSize: "9px",
    letterSpacing: "0.14em",
    padding: "4px 10px",
    border: `1px solid ${sel ? T.ink : T.paperDark}`,
    background: sel ? T.ink : "transparent",
    color: sel ? T.bg : T.inkMid,
    cursor: "pointer",
  };
}

function vouchShareBorderStyle(theme) {
  const orange = isOrangeBoardTheme(theme);
  return {
    border: `3px double ${orange ? SEASONAL_ORANGE : VOUCH_GOLD}`,
    boxShadow: orange ? `0 0 0 1px ${SEASONAL_ORANGE_SHADOW}` : `0 0 0 1px ${VOUCH_GOLD_SHADOW}`,
  };
}

function publishVouchButtonStyle(theme, hasItems) {
  const horror = isHorrorBoardTheme(theme);
  const accent = horror ? SEASONAL_ORANGE : "#c9a820";
  return {
    flex: 1,
    padding: horror ? "10px 12px" : "12px",
    background: hasItems ? T.ink : "transparent",
    border: `2px solid ${hasItems ? accent : T.paperDark}`,
    color: hasItems ? accent : T.inkFaint,
    fontFamily: horror ? "'Creepster', cursive" : "'Spectral SC',serif",
    fontSize: horror ? "22px" : "9px",
    letterSpacing: horror ? "0.04em" : "0.18em",
    cursor: hasItems ? "pointer" : "not-allowed",
    transition: "all 0.2s",
  };
}

const T = {
  bg:        "#C8C2B4",
  ink:       "#111008",
  inkMid:    "#3a3830",
  inkLight:  "#7a7568",
  inkFaint:  "#a09890",
  paperDark: "#b3ada0",
};

const GRAY_PILL_BG = "linear-gradient(180deg, #E0E0E0 0%, #C0C0C0 60%, #909090 100%)";
const GRAY_PILL_ACTIVE = "linear-gradient(180deg, #C8C2B4 0%, #A8A29E 55%, #7a7568 100%)";
const AGREE_CHECK_GOLD = "#D4AF37";

function grayPillStyle(active = false, extra = {}) {
  return {
    background: active ? GRAY_PILL_ACTIVE : GRAY_PILL_BG,
    border: "none",
    color: T.ink,
    cursor: "pointer",
    fontWeight: 700,
    fontFamily: "'Spectral SC',serif",
    ...extra,
  };
}

function agreePillStyle(agreed = false, extra = {}) {
  if (!agreed) return grayPillStyle(false, extra);
  return {
    background: T.ink,
    border: "none",
    color: T.bg,
    cursor: "pointer",
    fontWeight: 700,
    fontFamily: "'Spectral SC',serif",
    ...extra,
  };
}

function activityBtnStyle(extra = {}) {
  return {
    background: T.ink,
    border: "none",
    color: T.bg,
    cursor: "pointer",
    fontWeight: 700,
    fontFamily: "'Spectral SC',serif",
    ...extra,
  };
}

function activityAgreePillStyle(agreed = false, extra = {}) {
  return activityBtnStyle({
    color: agreed ? AGREE_CHECK_GOLD : T.bg,
    ...extra,
  });
}

function activityQueuePillStyle(queued = false, extra = {}) {
  return {
    background: queued ? AGREE_CHECK_GOLD : T.ink,
    border: "none",
    color: queued ? T.ink : T.bg,
    cursor: "pointer",
    fontWeight: 700,
    fontFamily: "'Spectral SC',serif",
    ...extra,
  };
}

function usePullToRefresh(enabled, onRefresh) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pulling = useRef(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const threshold = 72;

  useEffect(() => { refreshingRef.current = refreshing; }, [refreshing]);

  useEffect(() => {
    if (!enabled) return undefined;
    const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 1;
    const resetTouch = () => { startY.current = null; pulling.current = false; };

    const onTouchStart = (e) => {
      if (!atTop() || refreshingRef.current) return;
      startY.current = e.touches[0].clientY;
      pulling.current = false;
    };

    const onTouchMove = (e) => {
      if (startY.current == null || refreshingRef.current) return;
      if (!atTop() && !pulling.current) {
        resetTouch();
        setPull(0);
        pullRef.current = 0;
        return;
      }
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        setPull(0);
        pullRef.current = 0;
        return;
      }
      pulling.current = true;
      if (e.cancelable) e.preventDefault();
      const next = Math.min(dy * 0.5, 100);
      pullRef.current = next;
      setPull(next);
    };

    const onTouchEnd = async () => {
      if (startY.current == null) return;
      const shouldRefresh = pulling.current && pullRef.current >= threshold && !refreshingRef.current;
      resetTouch();
      if (!shouldRefresh) {
        setPull(0);
        pullRef.current = 0;
        return;
      }
      setRefreshing(true);
      setPull(56);
      try {
        await onRefreshRef.current?.();
      } finally {
        setRefreshing(false);
        setPull(0);
        pullRef.current = 0;
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, threshold]);

  useEffect(() => {
    const appEl = document.querySelector(".app");
    if (!appEl) return undefined;
    if (pull > 0 || refreshing) {
      appEl.style.transform = `translateY(${refreshing ? 56 : pull}px)`;
      appEl.style.transition = refreshing || pull === 0 ? "transform 0.22s ease" : "none";
    } else {
      appEl.style.transform = "";
      appEl.style.transition = "";
    }
    return () => {
      appEl.style.transform = "";
      appEl.style.transition = "";
    };
  }, [pull, refreshing]);

  return { pull, refreshing, threshold };
}

function PullToRefreshIndicator({ pull, refreshing, threshold }) {
  if (pull <= 0 && !refreshing) return null;
  const progress = Math.min(pull / threshold, 1);
  return (
    <div className="pull-refresh-indicator" style={{ opacity: refreshing ? 1 : 0.35 + progress * 0.65 }}>
      {refreshing ? "Refreshing…" : progress >= 1 ? "Release to refresh" : "Pull to refresh"}
    </div>
  );
}

const FEATURES_ANNOUNCE_KEY = "vouch-features-announce-2026-03-v2";

function FeaturesAnnounceModal({ onDismiss }) {
  const items = [
    "You can now comment on buddies' Vouches",
    "You can now listen to samples of songs, artists, and albums that have been Vouched for",
    "You can now watch the trailer for any film or TV show",
  ];
  return (
    <div className="modal-overlay" onClick={onDismiss}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">What's New</div>
          <button className="modal-x" onClick={onDismiss}>×</button>
        </div>
        <div className="modal-body">
          <ul style={{ listStyle: "none", margin: "0 0 20px", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {items.map(text => (
              <li key={text} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontFamily: "'Spectral',serif", fontSize: 14, lineHeight: 1.5, color: T.ink }}>
                <span style={{ fontFamily: "'Spectral SC',serif", fontSize: 10, letterSpacing: "0.08em", color: T.inkMid, marginTop: 3, flexShrink: 0 }}>→</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkMid, marginBottom: 20, lineHeight: 1.6 }}>
            Hope you like it — Christian
          </div>
          <button className="btn btn-solid" style={{ width: "100%", padding: "14px" }} onClick={onDismiss}>Got it</button>
        </div>
      </div>
    </div>
  );
}

const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Creepster&family=Playfair+Display:wght@700;800;900&family=Spectral:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,600;1,700&family=Spectral+SC:wght@300;400;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body, #root { font-family: 'Spectral', serif; background: ${T.bg}; color: ${T.ink}; -webkit-font-smoothing: antialiased; }

    body::before {
      content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 9999; opacity: 0.04;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      background-size: 180px 180px;
    }

    .app { min-height: 100vh; background: ${T.bg}; }

    .masthead { background: ${T.bg}; border-bottom: 3px double ${T.ink}; user-select: none; }
    .masthead-meta {
      display: flex; justify-content: space-between; align-items: center;
      padding: 7px 14px; border-bottom: 1px solid ${T.ink};
      font-family: 'Spectral SC', serif; font-size: 8px; letter-spacing: 0.12em;
      color: ${T.inkMid}; text-transform: uppercase; white-space: nowrap; gap: 8px;
    }
    .masthead-meta .clickable { cursor: pointer; }
    .masthead-meta .clickable:hover { color: ${T.ink}; }
    .masthead-meta-stars { font-size: 7px; letter-spacing: 0.3em; opacity: 0.6; }
    .masthead-nameplate { text-align: center; padding: 18px 28px 6px; cursor: pointer; }
    .nameplate-word {
      font-family: 'Times New Roman', Times, serif; font-weight: 900;
      font-size: clamp(58px, 11vw, 104px); letter-spacing: 0.02em;
      line-height: 0.92; color: ${T.ink};
    }
    .nameplate-gold { color: #C9A84C !important; font-family: inherit; font-weight: inherit; font-size: inherit; }
    .masthead-rule-ornament { text-align: center; font-family: 'Spectral', serif; font-size: 11px; color: ${T.inkLight}; padding: 4px 0 2px; display: flex; align-items: center; justify-content: center; gap: 10px; letter-spacing: 0; }
    .masthead-tagline { text-align: center; font-family: 'Spectral', serif; font-style: italic; font-weight: 300; font-size: 12.5px; letter-spacing: 0.12em; color: ${T.inkLight}; padding-bottom: 20px; }

    .nav { display: flex; overflow-x: auto; scrollbar-width: none; border-top: 1px solid ${T.ink}; }
    .nav::-webkit-scrollbar { display: none; }
    .nav-btn {
      flex-shrink: 0; flex: 1; padding: 9px 10px; font-family: 'Spectral SC', serif; font-size: 10px;
      font-weight: 600; letter-spacing: 0.22em; color: ${T.inkMid}; background: transparent;
      border: none; border-right: 1px solid ${T.paperDark}; cursor: pointer;
      transition: background 0.14s, color 0.14s; white-space: nowrap;
    }
    .nav-btn:hover  { background: ${T.paperDark}; color: ${T.ink}; }
    .nav-btn.active { background: ${T.ink}; color: ${T.bg}; }

    .page { max-width: 1380px; margin: 0 auto; padding: 0 28px 80px; }
    .board-header { display: flex; justify-content: space-between; align-items: flex-end; padding: 30px 0 18px; border-bottom: 1px solid ${T.ink}; margin-bottom: 32px; }
    .board-name { font-family: 'Spectral', serif; font-weight: 700; font-size: 26px; }
    .board-sub  { font-family: 'Spectral SC', serif; font-size: 10px; letter-spacing: 0.16em; color: ${T.inkLight}; margin-top: 5px; }

    .btn { font-family: 'Spectral SC', serif; font-size: 10px; font-weight: 600; letter-spacing: 0.2em; padding: 8px 20px; border: 1px solid ${T.ink}; cursor: pointer; transition: background 0.14s, color 0.14s; }
    .btn-solid { background: ${T.ink}; color: ${T.bg}; }
    .btn-solid:hover { background: ${T.inkMid}; }
    .btn-ghost { background: transparent; color: ${T.ink}; }
    .btn-ghost:hover { background: ${T.ink}; color: ${T.bg}; }

    .ornament { text-align: center; font-family: 'Spectral', serif; font-size: 13px; color: ${T.inkFaint}; margin: 4px 0 28px; display: flex; align-items: center; justify-content: center; gap: 8px; }

    .vouch-section { margin-bottom: 52px; border: 3px double #C9A84C; box-shadow: 0 0 0 1px #A07830; background: ${T.ink}; padding: 22px 22px 22px; position: relative; }
    .vouch-section-inner { margin-bottom: 0; border: none; box-shadow: none; background: transparent; padding: 0; position: relative; }
    .vouch-section.vouch-section-seasonal { border: 3px double ${SEASONAL_ORANGE}; box-shadow: 0 0 0 1px ${SEASONAL_ORANGE_SHADOW}; }
    .vouch-section-header { display: flex; align-items: center; gap: 10px; flex-wrap: nowrap; border-bottom: 1px solid rgba(200,194,180,0.25); padding-bottom: 12px; margin-bottom: 24px; }
    .vouch-section-label { font-family: 'Times New Roman', Times, serif; font-weight: 900; font-size: 22px; letter-spacing: 0.04em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; color: ${T.bg}; }
    .vouch-section-sub   { font-family: 'Spectral', serif; font-style: italic; font-size: 11px; color: rgba(200,194,180,0.55); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .vouch-section-add   { margin-left: auto; font-family: 'Spectral SC', serif; font-size: 9.5px; font-weight: 600; letter-spacing: 0.2em; padding: 4px 14px; border: 1px solid rgba(200,194,180,0.4); background: transparent; color: ${T.bg}; cursor: pointer; transition: all 0.14s; }
    .vouch-section-add:hover { background: rgba(200,194,180,0.15); }

    .cards-row-large { display: flex; gap: 12px; flex-wrap: nowrap; }
    .card-large { flex: 1; min-width: 0; max-width: 320px; cursor: pointer; }
    .card-poster-large { width: 100%; aspect-ratio: 2/3; object-fit: cover; display: block; border: 1px solid ${T.paperDark}; transition: transform 0.2s, box-shadow 0.2s; max-height: 400px; }
    .card-poster-placeholder-large { width: 100%; aspect-ratio: 2/3; background: ${T.paperDark}; border: 1px solid ${T.paperDark}; display: flex; align-items: center; justify-content: center; font-family: 'Spectral', serif; font-style: italic; font-size: 12px; color: ${T.inkLight}; text-align: center; padding: 14px; }
    .slot-empty-large { flex: 1; min-width: 0; aspect-ratio: 2/3; border: 1px dashed rgba(200,194,180,0.3); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.14s, background 0.14s; }
    .slot-empty-large:hover { background: rgba(200,194,180,0.06); }
    .slot-empty-inner { text-align: center; font-family: 'Spectral SC', serif; font-size: 9.5px; letter-spacing: 0.18em; color: rgba(200,194,180,0.4); }
    .slot-empty-plus  { display: block; font-size: 22px; margin-bottom: 6px; color: ${T.paperDark}; }

    .cat-section { margin-bottom: 44px; }
    .cat-header { display: flex; align-items: baseline; gap: 14px; border-bottom: 2px solid ${T.ink}; padding-bottom: 10px; margin-bottom: 18px; }
    .cat-label { font-family: 'Spectral SC', serif; font-weight: 700; font-size: 17px; letter-spacing: 0.08em; }
    .cat-sublabel { font-family: 'Spectral', serif; font-style: italic; font-size: 11px; color: ${T.inkLight}; }
    .cat-count { font-family: 'Spectral SC', serif; font-size: 9.5px; letter-spacing: 0.18em; color: ${T.inkFaint}; }
    .cat-add { margin-left: auto; font-family: 'Spectral SC', serif; font-size: 9.5px; font-weight: 600; letter-spacing: 0.2em; padding: 4px 14px; border: 1px solid ${T.inkLight}; background: transparent; color: ${T.inkMid}; cursor: pointer; transition: all 0.14s; }
    .cat-add:hover { border-color: ${T.ink}; color: ${T.ink}; }

    .cards-row { display: flex; gap: 14px; flex-wrap: wrap; }
    .card { width: 180px; flex-shrink: 0; cursor: pointer; }
    .card:hover .card-poster { transform: translateY(-3px); box-shadow: 0 7px 20px rgba(17,16,8,0.16); }
    .card-poster { width: 180px; height: 248px; object-fit: cover; display: block; border: 1px solid ${T.paperDark}; transition: transform 0.2s, box-shadow 0.2s; }
    .card-poster-placeholder { width: 180px; height: 248px; background: ${T.paperDark}; border: 1px solid ${T.paperDark}; display: flex; align-items: center; justify-content: center; font-family: 'Spectral', serif; font-style: italic; font-size: 11px; color: ${T.inkLight}; text-align: center; padding: 10px; }
    .card-title   { font-family: 'Spectral', serif; font-weight: 600; font-size: 12.5px; line-height: 1.35; margin-top: 7px; }
    .card-sub     { font-family: 'Spectral SC', serif; font-size: 9.5px; letter-spacing: 0.06em; color: ${T.inkLight}; margin-top: 2px; }
    .tile-media { position: relative; display: block; }
    .tile-media-actions {
      position: absolute; z-index: 49;
      display: flex; flex-direction: column; align-items: stretch;
    }
    .tile-media-actions-row {
      flex-direction: row; flex-wrap: wrap; align-items: stretch;
    }
    .tile-media-actions-row .tile-inline-btn {
      width: auto; flex: 1 1 auto; min-width: 0; justify-content: center;
    }
    .tile-inline-btn {
      display: flex; align-items: center; justify-content: flex-start;
      width: 100%; box-sizing: border-box;
      background: rgba(17,16,8,0.88); border: 1px solid rgba(179,173,160,0.55);
      color: #C8C2B4; cursor: pointer;
      font-family: 'Spectral SC', serif; font-weight: 700;
      letter-spacing: 0.12em; text-transform: uppercase; line-height: 1;
      transition: background 0.14s, color 0.14s, border-color 0.14s;
    }
    .tile-inline-btn-spacer { flex-shrink: 0; display: block; }
    .tile-inline-btn-stacked { width: auto; white-space: nowrap; }
    .archive-tiles-row {
      display: flex; flex-direction: row; flex-wrap: nowrap;
      overflow-x: auto; gap: 14px; padding-bottom: 8px;
      -webkit-overflow-scrolling: touch; scrollbar-width: none;
    }
    .archive-tiles-row::-webkit-scrollbar { display: none; }
    .archive-tiles-row-single { justify-content: center; overflow-x: visible; }
    .archive-tile-single { display: flex; flex-direction: column; align-items: center; width: 240px; }
    .archive-tile-card { width: 220px; flex-shrink: 0; }
    .archive-tile-poster {
      width: 100%; height: 304px; object-fit: cover; display: block;
      border: 1px solid ${T.paperDark};
    }
    .archive-tile-poster-placeholder {
      width: 100%; height: 304px; background: ${T.paperDark};
      border: 1px solid ${T.paperDark}; display: flex; align-items: center;
      justify-content: center; font-family: 'Spectral', serif; font-style: italic;
      font-size: 11px; color: ${T.inkLight}; text-align: center; padding: 10px;
    }
    .tile-inline-btn:hover { background: rgba(17,16,8,0.96); color: #fff; border-color: rgba(179,173,160,0.75); }
    .tile-inline-btn.is-playing { background: rgba(17,16,8,0.96); color: #fff; border-color: rgba(179,173,160,0.75); }
    .trailer-overlay {
      position: fixed; inset: 0; z-index: 2000;
      background: rgba(17,16,8,0.92);
      display: flex; align-items: center; justify-content: center;
      padding: 24px 16px;
    }
    .trailer-modal {
      position: relative; width: min(920px, 100%);
      background: #111008; border: 1px solid rgba(255,255,255,0.2);
      box-shadow: 0 24px 80px rgba(0,0,0,0.55);
    }
    .trailer-close {
      position: absolute; top: 8px; right: 10px; z-index: 2;
      background: transparent; border: none; color: #fff;
      font-family: 'Spectral', serif; font-size: 28px; line-height: 1;
      cursor: pointer; opacity: 0.7; padding: 4px 8px;
    }
    .trailer-close:hover { opacity: 1; }
    .trailer-frame-wrap {
      position: relative; width: 100%; aspect-ratio: 16 / 9; background: #000;
    }
    .trailer-frame-wrap iframe {
      position: absolute; inset: 0; width: 100%; height: 100%; border: 0;
    }
    .trailer-caption {
      padding: 10px 14px 12px;
      font-family: 'Spectral SC', serif; font-size: 10px; letter-spacing: 0.12em;
      color: rgba(255,255,255,0.75); text-transform: uppercase; text-align: center;
    }
    .card-comment { font-family: 'Spectral', serif; font-style: italic; font-size: 10.5px; line-height: 1.5; color: ${T.inkMid}; margin-top: 4px; white-space: normal; word-break: break-word; }
    .slot-empty-sm { width: 180px; height: 248px; border: 2px dashed ${T.inkLight}; background: rgba(17,16,8,0.06); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.14s, background 0.14s; flex-shrink: 0; }
    .slot-empty-sm:hover { border-color: ${T.ink}; background: rgba(17,16,8,0.12); }

    .lb-overlay { position: fixed; inset: 0; background: rgba(17,16,8,0.96); z-index: 1000; display: flex; align-items: center; justify-content: center; }
    .lb-close { position: fixed; top: 22px; right: 26px; background: transparent; border: none; color: ${T.bg}; font-family: 'Spectral', serif; font-size: 30px; line-height: 1; cursor: pointer; opacity: 0.6; transition: opacity 0.14s; }
    .lb-close:hover { opacity: 1; }
    .lb-body { text-align: center; color: ${T.bg}; padding: 32px 24px; max-width: 480px; width: 100%; }
    .lb-category { font-family: 'Spectral SC', serif; font-size: 9.5px; letter-spacing: 0.3em; color: rgba(200,194,180,0.45); margin-bottom: 16px; }
    .lb-poster   { width: 200px; height: 280px; object-fit: cover; display: block; margin: 0 auto 22px; box-shadow: 0 24px 64px rgba(0,0,0,0.7); }
    .lb-title    { font-family: 'Spectral', serif; font-weight: 700; font-size: 24px; line-height: 1.2; margin-bottom: 5px; }
    .lb-sub      { font-family: 'Spectral SC', serif; font-size: 11px; letter-spacing: 0.1em; color: rgba(200,194,180,0.55); margin-bottom: 20px; }
    .lb-comment  { font-family: 'Spectral', serif; font-style: italic; font-weight: 300; font-size: 15px; line-height: 1.75; color: rgba(200,194,180,0.88); border-left: 2px solid rgba(200,194,180,0.2); text-align: left; padding: 0 0 0 18px; margin: 0 auto 28px; max-width: 360px; }
    .lb-nav      { display: flex; align-items: center; justify-content: center; gap: 20px; }
    .lb-nav-btn  { font-family: 'Spectral SC', serif; font-size: 9.5px; letter-spacing: 0.2em; padding: 7px 18px; border: 1px solid rgba(200,194,180,0.25); background: transparent; color: ${T.bg}; cursor: pointer; transition: background 0.14s; }
    .lb-nav-btn:hover:not(:disabled) { background: rgba(200,194,180,0.08); }
    .lb-nav-btn:disabled { opacity: 0.25; cursor: default; }
    .lb-dots { display: flex; gap: 8px; }
    .lb-dot    { width: 5px; height: 5px; border-radius: 50%; background: rgba(200,194,180,0.25); cursor: pointer; transition: background 0.14s; }
    .lb-dot.on { background: ${T.bg}; }

    .modal-overlay { position: fixed; inset: 0; background: rgba(17,16,8,0.82); z-index: 900; display: flex; align-items: flex-start; justify-content: center; padding-top: 72px; }
    .modal       { background: ${T.bg}; width: 100%; max-width: 540px; max-height: 82vh; overflow-y: auto; border: 1px solid ${T.ink}; }
    .modal-head  { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px; border-bottom: 2px solid ${T.ink}; }
    .modal-title { font-family: 'Spectral', serif; font-weight: 700; font-size: 17px; }
    .modal-x     { font-family: 'Spectral', serif; font-size: 22px; background: transparent; border: none; cursor: pointer; color: ${T.ink}; opacity: 0.6; }
    .modal-x:hover { opacity: 1; }
    .modal-body  { padding: 20px 22px; }

    .search-input { width: 100%; font-family: 'Spectral', serif; font-size: 15px; padding: 11px 14px; border: 1px solid ${T.ink}; background: transparent; color: ${T.ink}; outline: none; margin-bottom: 14px; }
    .search-input::placeholder { color: ${T.inkFaint}; }
    .search-input:focus { box-shadow: 0 0 0 2px rgba(17,16,8,0.1); }

    .result-item { display: flex; gap: 12px; align-items: center; padding: 9px; border: 1px solid transparent; cursor: pointer; transition: border-color 0.13s, background 0.13s; }
    .result-item:hover { border-color: ${T.ink}; background: rgba(17,16,8,0.03); }
    .result-img   { width: 40px; height: 56px; object-fit: cover; border: 1px solid ${T.paperDark}; flex-shrink: 0; background: ${T.paperDark}; }
    .result-title { font-family: 'Spectral', serif; font-weight: 600; font-size: 14px; }
    .result-sub   { font-family: 'Spectral SC', serif; font-size: 10px; letter-spacing: 0.06em; color: ${T.inkLight}; margin-top: 2px; }

    .selected-preview { display: flex; gap: 12px; align-items: center; padding: 11px; border: 1px solid ${T.ink}; background: rgba(17,16,8,0.03); margin-bottom: 14px; }
    .comment-label { display: block; font-family: 'Spectral SC', serif; font-size: 9.5px; letter-spacing: 0.18em; color: ${T.inkMid}; margin-bottom: 7px; }
    .comment-area  { width: 100%; font-family: 'Spectral', serif; font-style: italic; font-size: 13.5px; line-height: 1.6; padding: 11px 13px; border: 1px solid ${T.ink}; background: transparent; color: ${T.ink}; resize: none; height: 78px; outline: none; }
    .comment-area::placeholder { color: ${T.inkFaint}; }
    .tile-buddy-comments { margin-top: 8px; }
    .tile-buddy-comment-row { font-size: 11px; line-height: 1.45; margin-bottom: 5px; word-break: break-word; }
    .tile-buddy-comment-name { font-family: 'Spectral SC', serif; font-size: 8px; letter-spacing: 0.08em; margin-right: 6px; }
    .tile-buddy-comment-input {
      flex: 1; min-width: 0; font-family: 'Spectral', serif; font-size: 11px;
      padding: 6px 8px; border: 1px solid rgba(200,194,180,0.25); background: rgba(0,0,0,0.15); color: #C8C2B4; outline: none;
    }
    .tile-buddy-comment-input::placeholder { color: rgba(200,194,180,0.35); }
    .editor-tile-row {
      display: flex; gap: 12px; align-items: center; margin-bottom: 0;
      padding: 8px 0; border-bottom: 1px solid ${T.paperDark};
    }
    .char-count { font-family: 'Spectral SC', serif; font-size: 9.5px; color: ${T.inkFaint}; text-align: right; margin: 4px 0 12px; }

    .friend-row { display: flex; align-items: center; justify-content: space-between; padding: 13px 0; border-bottom: 1px solid ${T.paperDark}; cursor: pointer; }
    .friend-row:hover .friend-name { text-decoration: underline; }
    .friend-name   { font-family: 'Spectral', serif; font-weight: 600; font-size: 15px; }
    .friend-handle { font-family: 'Spectral SC', serif; font-size: 10px; letter-spacing: 0.1em; color: ${T.inkLight}; margin-top: 2px; }
    .friend-arrow  { font-size: 13px; color: ${T.inkFaint}; }

    .auth-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: ${T.bg}; padding: 24px; }
    .auth-box  { width: 100%; max-width: 400px; }
    .auth-plate { text-align: center; border-top: 3px double ${T.ink}; border-bottom: 3px double ${T.ink}; padding: 14px 0 10px; margin-bottom: 10px; }
    .auth-plate-name { font-family: 'Times New Roman', Times, serif; font-weight: 900; font-size: 72px; letter-spacing: 0.02em; line-height: 0.92; }
    .auth-tagline { font-family: 'Spectral', serif; font-style: italic; font-weight: 300; font-size: 12px; letter-spacing: 0.1em; color: ${T.inkLight}; text-align: center; margin-bottom: 32px; }
    .auth-google { font-family: 'Spectral SC', serif; font-size: 10.5px; font-weight: 600; letter-spacing: 0.25em; padding: 13px; width: 100%; background: transparent; color: ${T.ink}; border: 1px solid ${T.ink}; cursor: pointer; transition: all 0.14s; }
    .auth-google:hover { background: ${T.ink}; color: ${T.bg}; }

    .loading    { text-align: center; padding: 18px 0; font-family: 'Spectral SC', serif; font-size: 10px; letter-spacing: 0.2em; color: ${T.inkFaint}; }
    .no-results { text-align: center; padding: 18px 0; font-family: 'Spectral', serif; font-style: italic; font-size: 13px; color: ${T.inkLight}; }

    @media (max-width: 640px) {
      .cards-row-large { flex-direction: column; gap: 0; }
      .card-large { width: 100%; }
      .card-poster-large { width: 100%; height: auto; aspect-ratio: 2/3; }
      .card-poster-placeholder-large { width: 100%; aspect-ratio: 2/3; height: auto; }
      .slot-empty-large { width: 100%; aspect-ratio: 2/3; height: auto; margin-bottom: 0; }
      .cards-row { flex-direction: row; flex-wrap: nowrap; overflow-x: auto; gap: 10px; padding-bottom: 8px; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
      .cards-row::-webkit-scrollbar { display: none; }
      .card { width: 95px; flex-shrink: 0; }
      .card-poster { width: 95px; height: 130px; flex-shrink: 0; }
      .card-poster-placeholder { width: 95px; height: 130px; flex-shrink: 0; font-size: 9px; }
      .card:hover .card-poster { transform: none; box-shadow: none; }
      .slot-empty-sm { width: 95px; height: 130px; flex-shrink: 0; border: 2px dashed rgba(17,16,8,0.3); background: rgba(17,16,8,0.06); }
      .archive-tile-card { width: 120px; }
      .archive-tile-single { width: 200px; }
      .archive-tile-poster { height: 166px; }
      .archive-tile-poster-placeholder { height: 166px; font-size: 9px; padding: 4px; }
      .page { padding: 0 16px 60px; }
    .pull-refresh-indicator {
      position: fixed; top: 0; left: 0; right: 0; z-index: 9997;
      height: 56px; display: flex; align-items: center; justify-content: center;
      font-family: 'Spectral SC', serif; font-size: 9px; letter-spacing: 0.16em;
      color: ${T.inkMid}; pointer-events: none;
    }
      .masthead-meta { padding: 7px 16px; }
      .vouch-section { padding: 16px 14px 20px; }
    }
  `}</style>
);

function Auth({ inviteUserId }) {
  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin + `?invite=${inviteUserId}` }
    });
  };
  return (
    <div className="auth-wrap">
      <div className="auth-box">
        <div className="auth-plate" style={{display:"flex",justifyContent:"center",width:"100%"}}><img src="/vouch5-logo.png" alt="Vouch5" style={{height:"clamp(80px,16vw,160px)"}} /></div>
        <div className="auth-tagline">Love it? Vouch for it.</div>
        <div style={{ marginBottom: 32, borderBottom: `1px solid ${T.paperDark}`, paddingBottom: 32 }}>
          <HowItWorks />
        </div>
        <button className="auth-google" onClick={signInWithGoogle}>Continue with Google</button>
      </div>
    </div>
  );
}

function boardItemToTile(item) {
  return normalizeTileItem({
    id: item.item_id,
    item_id: item.item_id,
    category: item.category,
    title: item.title,
    subtitle: item.subtitle,
    poster: item.poster,
    source_url: item.source_url,
  }, item.category);
}

function vouchItemToDisplay(item) {
  return {
    id: item.item_id,
    boardItemId: item.id,
    title: item.title,
    sub: item.subtitle || "",
    poster: item.poster,
    vouched: true,
    sourceUrl: item.source_url,
    _cat: item.category,
    _catLabel: CATEGORIES.find(c => c.key === item.category)?.label || item.category,
  };
}

async function loadTileCommentsMap(boardItems) {
  const ids = (boardItems || []).map(i => i.id).filter(Boolean);
  if (!ids.length) return {};
  const { data, error } = await supabase
    .from("vouch_tile_buddy_comments")
    .select("id, board_item_id, user_id, body, created_at")
    .in("board_item_id", ids)
    .order("created_at", { ascending: true });
  if (error) {
    console.error("loadTileCommentsMap:", error);
    return {};
  }
  const userIds = [...new Set((data || []).map(r => r.user_id))];
  const { data: profiles } = userIds.length
    ? await supabase.from("profiles").select("id, display_name").in("id", userIds)
    : { data: [] };
  const nameMap = Object.fromEntries((profiles || []).map(p => [p.id, p.display_name]));
  const map = {};
  (data || []).forEach(row => {
    if (!map[row.board_item_id]) map[row.board_item_id] = [];
    map[row.board_item_id].push({ ...row, displayName: nameMap[row.user_id] || "Buddy" });
  });
  return map;
}

async function loadNewCommentNotifications(uid, lastVisit) {
  const { data: rows, error } = await supabase
    .from("vouch_tile_buddy_comments")
    .select("id, user_id, body, created_at, board_item_id")
    .gt("created_at", lastVisit)
    .neq("user_id", uid)
    .order("created_at", { ascending: false })
    .limit(30);
  if (error || !rows?.length) return [];

  const itemIds = [...new Set(rows.map(r => r.board_item_id))];
  const { data: items } = await supabase
    .from("vouch_board_items")
    .select("id, title, board_id")
    .in("id", itemIds);
  const itemMap = Object.fromEntries((items || []).map(i => [i.id, i]));
  const boardIds = [...new Set((items || []).map(i => i.board_id).filter(Boolean))];
  const { data: boards } = boardIds.length
    ? await supabase.from("vouch_boards").select("id, user_id, name, theme").in("id", boardIds)
    : { data: [] };
  const boardMap = Object.fromEntries((boards || []).map(b => [b.id, b]));

  const { data: myComments } = await supabase
    .from("vouch_tile_buddy_comments")
    .select("board_item_id")
    .eq("user_id", uid)
    .in("board_item_id", itemIds);
  const participated = new Set((myComments || []).map(r => r.board_item_id));

  const ownerIds = [...new Set((boards || []).map(b => b.user_id).filter(Boolean))];
  const commenterIds = [...new Set(rows.map(r => r.user_id))];
  const profileIds = [...new Set([...ownerIds, ...commenterIds])];
  const { data: profs } = profileIds.length
    ? await supabase.from("profiles").select("id, display_name, username").in("id", profileIds)
    : { data: [] };
  const nameMap = Object.fromEntries((profs || []).map(p => [p.id, p.display_name || p.username || "Someone"]));

  return rows
    .filter(r => {
      const item = itemMap[r.board_item_id];
      const board = item ? boardMap[item.board_id] : null;
      if (!item || !board) return false;
      if (board.user_id === uid) return true;
      return participated.has(r.board_item_id);
    })
    .slice(0, 10)
    .map(r => {
      const item = itemMap[r.board_item_id];
      const board = boardMap[item.board_id];
      return {
        id: r.id,
        user_id: r.user_id,
        display_name: nameMap[r.user_id] || "Someone",
        title: item.title,
        body: r.body,
        board_owner_id: board.user_id,
        board_owner_name: nameMap[board.user_id] || "Someone",
        board_item_id: r.board_item_id,
        on_own_vouch: board.user_id === uid,
        created_at: r.created_at,
      };
    });
}

async function enrichCommentsForFeed(commentRows, allPeople) {
  if (!commentRows?.length) return [];
  const itemIds = [...new Set(commentRows.map(c => c.board_item_id))];
  const { data: items } = await supabase
    .from("vouch_board_items")
    .select("id, title, poster, board_id, category, subtitle")
    .in("id", itemIds);
  const itemMap = Object.fromEntries((items || []).map(i => [i.id, i]));
  const boardIds = [...new Set((items || []).map(i => i.board_id).filter(Boolean))];
  const { data: boards } = boardIds.length
    ? await supabase.from("vouch_boards").select("id, user_id, name, theme").in("id", boardIds)
    : { data: [] };
  const boardMap = Object.fromEntries((boards || []).map(b => [b.id, b]));
  const profileIds = [...new Set([
    ...commentRows.map(c => c.user_id),
    ...(boards || []).map(b => b.user_id),
  ])];
  const { data: profs } = profileIds.length
    ? await supabase.from("profiles").select("id, display_name, username, avatar_url").in("id", profileIds)
    : { data: [] };
  const profMap = Object.fromEntries((profs || []).map(p => [p.id, p]));

  return commentRows.map(c => {
    const item = itemMap[c.board_item_id];
    const board = item ? boardMap[item.board_id] : null;
    if (!item || !board) return null;
    const ownerProf = profMap[board.user_id];
    const commenterProf = profMap[c.user_id];
    const buddy = allPeople.find(x => x.userId === c.user_id) || {
      userId: c.user_id,
      displayName: commenterProf?.display_name || "Someone",
      username: commenterProf?.username || "",
      avatarUrl: commenterProf?.avatar_url || null,
    };
    return {
      type: "comment",
      date: new Date(c.created_at),
      comment: c,
      buddy,
      boardOwner: {
        userId: board.user_id,
        displayName: ownerProf?.display_name || "Someone",
        username: ownerProf?.username || "",
        avatarUrl: ownerProf?.avatar_url || null,
      },
      tile: item,
      vouchLabel: (board.theme && board.theme !== "Other") ? board.theme : (board.name || "Vouch"),
    };
  }).filter(Boolean);
}

function TileBuddyComments({ comments = [], canComment, boardItemId, currentUserId, onPost, onDelete, dark = true, compact = false }) {
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [panelOpen, setPanelOpen] = useState(!compact);
  if (!comments.length && !canComment) return null;

  const visible = expanded ? comments : comments.slice(-3);
  const hiddenCount = comments.length - visible.length;
  const nameColor = dark ? "rgba(200,194,180,0.55)" : T.inkLight;
  const textColor = dark ? "rgba(200,194,180,0.88)" : T.inkMid;

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = body.trim();
    if (!text || posting || !boardItemId) return;
    setPosting(true);
    await onPost(boardItemId, text);
    setBody("");
    setPosting(false);
  };

  if (compact && !panelOpen) {
    return (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); setPanelOpen(true); }}
        style={grayPillStyle(false, { marginTop: 6, width: "100%", fontSize: "7px", letterSpacing: "0.1em", padding: "5px 8px" })}
      >
        Comments{comments.length > 0 ? ` · ${comments.length}` : ""}
      </button>
    );
  }

  return (
    <div className="tile-buddy-comments" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
      {compact && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setPanelOpen(false); }}
          style={{ background: "transparent", border: "none", padding: 0, marginBottom: 6, cursor: "pointer", fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.1em", color: nameColor }}
        >
          Hide comments ▲
        </button>
      )}
      {comments.length > 0 && (
        <div style={{ marginBottom: canComment ? 8 : 0, paddingTop: compact ? 0 : 6, borderTop: compact ? "none" : `1px solid ${dark ? "rgba(200,194,180,0.15)" : T.paperDark}` }}>
          {hiddenCount > 0 && !expanded && (
            <button type="button" onClick={e => { e.stopPropagation(); setExpanded(true); }} style={{ background: "transparent", border: "none", padding: 0, marginBottom: 4, cursor: "pointer", fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.1em", color: nameColor }}>
              +{hiddenCount} earlier
            </button>
          )}
          {visible.map(c => (
            <div key={c.id} className="tile-buddy-comment-row">
              <span className="tile-buddy-comment-name" style={{ color: nameColor }}>{(c.displayName || "Buddy").split(" ")[0]}</span>
              <span style={{ fontFamily: "'Spectral', serif", color: textColor }}>{c.body}</span>
              {currentUserId && c.user_id === currentUserId && onDelete && canComment && (
                <button type="button" onClick={e => { e.stopPropagation(); onDelete(c.id, boardItemId); }} style={{ background: "transparent", border: "none", color: nameColor, cursor: "pointer", fontSize: 10, marginLeft: 4, padding: "0 2px" }} aria-label="Delete comment">×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {canComment && (
        <form onSubmit={submit} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            className="tile-buddy-comment-input"
            type="text"
            value={body}
            onChange={e => setBody(e.target.value.slice(0, 200))}
            placeholder={canComment && comments.length > 0 ? "Reply…" : "Add a comment…"}
            maxLength={200}
          />
          <button type="submit" disabled={posting || !body.trim()} style={grayPillStyle(!!body.trim(), { flexShrink: 0, fontSize: "7px", letterSpacing: "0.1em", padding: "6px 10px", opacity: body.trim() ? 1 : 0.45, cursor: body.trim() ? "pointer" : "default" })}>Post</button>
        </form>
      )}
    </div>
  );
}

function ArchiveTile({ item, onMusicOpen, itemCount = 5, badgeSize = "sm", style, titleBelow = true, compact = false, tileComments = [], canComment = false, commentsCompact = false, currentUserId, onPostTileComment, onDeleteTileComment }) {
  const tile = boardItemToTile(item);
  const catKey = item.category;
  const open = () => openTileLink(tile, { catKey, onMusicOpen });
  const isSingle = itemCount === 1 && !compact;
  const fixedWidth = style?.width;
  const isMusic = isMusicCategory(catKey);
  const clickable = tileIsClickable(tile, catKey);
  const useOverlayActions = !compact && !fixedWidth;

  let containerClass = "archive-tile-card";
  let posterStyle;
  let placeholderClass = "archive-tile-poster-placeholder";

  if (compact || fixedWidth) {
    const w = fixedWidth || 70;
    posterStyle = {
      width: w,
      height: Math.round(w * 1.5),
      objectFit: "cover",
      border: `1px solid ${T.paperDark}`,
      display: "block",
    };
    containerClass = "";
  } else if (isSingle) {
    posterStyle = {
      width: "100%",
      aspectRatio: isMusic ? "1/1" : "2/3",
      objectFit: "contain",
      background: "#000",
      border: `1px solid ${T.paperDark}`,
      display: "block",
    };
    containerClass = "archive-tile-single";
  } else {
    posterStyle = null;
  }

  const containerStyle = {
    flexShrink: 0,
    ...(compact || fixedWidth ? style : {}),
  };

  const posterContent = tile.poster
    ? posterStyle
      ? <img src={tile.poster} alt={tile.title} style={{ ...posterStyle, cursor: clickable ? "pointer" : "default" }} className={posterStyle ? undefined : "archive-tile-poster"} onError={e => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} />
      : <img src={tile.poster} alt={tile.title} className="archive-tile-poster" style={{ cursor: clickable ? "pointer" : "default" }} onError={e => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} />
    : posterStyle
      ? <div style={{ ...posterStyle, background: T.paperDark, alignItems: "center", justifyContent: "center", fontSize: 9, fontFamily: "'Spectral',serif", color: T.inkLight, textAlign: "center", padding: 4, display: "flex" }}>{tile.title}</div>
      : <div className={placeholderClass}>{tile.title}</div>;

  return (
    <div
      className={containerClass || undefined}
      style={Object.keys(containerStyle).length ? containerStyle : undefined}
    >
      {useOverlayActions ? (
        <TileMedia
          item={tile}
          catKey={catKey}
          onOpen={open}
          badgeSize={isSingle ? "md" : "sm"}
          actionsHorizontal
        >
          {posterContent}
        </TileMedia>
      ) : (
        <div
          className="tile-media"
          onClick={open}
          style={{ cursor: clickable ? "pointer" : "default" }}
        >
          {posterContent}
        </div>
      )}
      {titleBelow && (
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", color: T.inkFaint, marginTop: 3, lineHeight: 1.3, maxWidth: isSingle ? 240 : fixedWidth || 220, textAlign: isSingle ? "center" : undefined }}>{item.title}</div>
      )}
      {item.id && (tileComments.length > 0 || canComment || commentsCompact) && (
        <TileBuddyComments
          comments={tileComments}
          canComment={canComment}
          compact={commentsCompact}
          boardItemId={item.id}
          currentUserId={currentUserId}
          onPost={onPostTileComment}
          onDelete={onDeleteTileComment}
          dark={false}
        />
      )}
    </div>
  );
}

function OwnArchive({ boards, canPublish, onRepublish, onDelete, onMusicOpen, defaultOpen = false, tileComments = {}, currentUserId, onPostTileComment, onDeleteTileComment }) {
  const [open, setOpen] = useState(defaultOpen);
  const inactive = boards.filter(b => !b.is_active && b.published_at);
  if (!inactive.length) return null;
  return (
    <div style={{ marginBottom: 32 }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: "10px 0", width: "100%" }}>
        <div style={{ flex: 1, height: 1, background: T.paperDark }} />
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, whiteSpace: "nowrap" }}>{open ? "Hide Previous Vouches ▲" : "Previous Vouches ▼"}</div>
        <div style={{ flex: 1, height: 1, background: T.paperDark }} />
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {inactive.reduce((acc, b) => {
            const d = new Date(b.published_at);
            const key = d.toLocaleString("en-US", { month: "long" }) + " " + d.getFullYear();
            const existing = acc.find(g => g.key === key);
            if (existing) existing.boards.push(b);
            else acc.push({ key, boards: [b] });
            return acc;
          }, []).map(({ key: monthYear, boards: mBoards }) => (
            <div key={monthYear} style={{ marginBottom: 32 }}>
              <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 10, letterSpacing: "0.18em", color: T.inkMid, borderBottom: "2px solid " + T.ink, paddingBottom: 8, marginBottom: 16 }}>{monthYear}</div>
              {mBoards.map(b => {
                const theme = boardThemeName(b);
                const items = (b.vouch_board_items || []).sort((a,x) => a.position - x.position).slice(0,5);
                return (
                  <div key={b.id} style={{ marginBottom: 28 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontFamily: "'Times New Roman',Times,serif", fontWeight: 900, fontSize: 20, color: T.ink, ...getVouchThemeLabelStyle(theme) }}>{theme}</div>
                        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.12em", color: T.inkLight, marginTop: 2 }}>{new Date(b.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        {b.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkMid, marginTop: 3 }}>{b.description}</div>}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button className="btn btn-solid" style={{ padding: "4px 12px", fontSize: 10, opacity: canPublish ? 1 : 0.4, cursor: canPublish ? "pointer" : "not-allowed" }} onClick={() => canPublish && onRepublish(b)}>Republish</button>
                        <button onClick={() => { if (window.confirm("Delete this Vouch permanently?")) onDelete(b); }} style={{ padding: "4px 12px", fontSize: 10, fontFamily: "'Spectral SC',serif", letterSpacing: "0.1em", background: "transparent", border: "1px solid " + T.paperDark, color: T.inkMid, cursor: "pointer" }}>Delete</button>
                      </div>
                    </div>
                    <div className={`archive-tiles-row${items.length === 1 ? " archive-tiles-row-single" : ""}`}>
                      {items.map((item, idx) => (
                        <ArchiveTile
                          key={idx}
                          item={item}
                          onMusicOpen={onMusicOpen}
                          itemCount={items.length}
                          tileComments={tileComments[item.id] || []}
                          canComment={false}
                          commentsCompact
                          currentUserId={currentUserId}
                          onPostTileComment={onPostTileComment}
                          onDeleteTileComment={onDeleteTileComment}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviousVouches({ userId, onDudeSame, myReactions, queue, onAddToQueue, onMusicOpen, defaultOpen = false, tileComments = {}, currentUserId, onPostTileComment, onDeleteTileComment }) {
  const [boards, setBoards] = useState([]);
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [localTileComments, setLocalTileComments] = useState({});
  useEffect(() => {
    setBoards([]);
    setOpen(defaultOpen);
    setLocalTileComments({});
  }, [userId, defaultOpen]);
  useEffect(() => {
    if ((!open && !defaultOpen) || boards.length > 0) return;
    setLoading(true);
    supabase.from("vouch_boards").select("*, vouch_board_items(*)").eq("user_id", userId).eq("is_active", false).order("published_at", { ascending: false })
      .then(async ({ data }) => {
        const list = (data || []).filter(b => b.published_at && b.vouch_board_items?.length > 0);
        setBoards(list);
        const allItems = list.flatMap(b => b.vouch_board_items || []);
        const map = await loadTileCommentsMap(allItems);
        setLocalTileComments(map);
        setLoading(false);
      });
  }, [open, defaultOpen, userId, boards.length]);
  const commentsMap = { ...localTileComments, ...tileComments };
  if (!userId) return null;
  return (
    <div style={{ marginBottom: 32 }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: "10px 0", width: "100%" }}>
        <div style={{ flex: 1, height: 1, background: T.paperDark }} />
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, whiteSpace: "nowrap" }}>{open ? "Hide Previous Vouches ▲" : "Previous Vouches ▼"}</div>
        <div style={{ flex: 1, height: 1, background: T.paperDark }} />
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {loading && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkFaint, padding: "12px 0" }}>Loading…</div>}
          {!loading && boards.length === 0 && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkFaint, padding: "12px 0" }}>No previous vouches.</div>}
          {boards.reduce((acc, b) => {
            const d = new Date(b.published_at);
            const key = d.toLocaleString("en-US", { month: "long" }) + " " + d.getFullYear();
            const existing = acc.find(g => g.key === key);
            if (existing) existing.boards.push(b);
            else acc.push({ key, boards: [b] });
            return acc;
          }, []).map(({ key: monthYear, boards: mBoards }) => (
            <div key={monthYear} style={{ marginBottom: 32 }}>
              <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 10, letterSpacing: "0.18em", color: T.inkMid, borderBottom: "2px solid " + T.ink, paddingBottom: 8, marginBottom: 16 }}>{monthYear}</div>
              {mBoards.map(b => {
                const theme = boardThemeName(b);
                const items = (b.vouch_board_items || []).sort((a,x) => a.position - x.position).slice(0,5);
                return (
                  <div key={b.id} style={{ marginBottom: 28 }}>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontFamily: "'Times New Roman',Times,serif", fontWeight: 900, fontSize: 20, color: T.ink, ...getVouchThemeLabelStyle(theme) }}>{theme}</div>
                      <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.12em", color: T.inkLight, marginTop: 2 }}>{new Date(b.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      {b.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkMid, marginTop: 3 }}>{b.description}</div>}
                    </div>
                    <div className={`archive-tiles-row${items.length === 1 ? " archive-tiles-row-single" : ""}`}>
                      {items.map((item, idx) => (
                        <ArchiveTile
                          key={idx}
                          item={item}
                          onMusicOpen={onMusicOpen}
                          itemCount={items.length}
                          tileComments={commentsMap[item.id] || []}
                          canComment={false}
                          commentsCompact
                          currentUserId={currentUserId}
                          onPostTileComment={onPostTileComment}
                          onDeleteTileComment={onDeleteTileComment}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function countPublishedVouches(boards) {
  return (boards || []).filter(b => b.published_at).length;
}

function PublicBoard({ inviteUserId, onSignUp }) {
  const [board, setBoard]           = useState(null);
  const [profile, setProfile]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showSignupNudge, setShowSignupNudge] = useState(false);
  const [publicBuddies, setPublicBuddies] = useState([]);
  const [showPublicBuddies, setShowPublicBuddies] = useState(false);
  const [publicItemBadges, setPublicItemBadges] = useState({});
  const [publicUserBadges, setPublicUserBadges] = useState([]);
  const [publicPublishCount, setPublicPublishCount] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data: prof } = await supabase
          .from("profiles").select("id, username, display_name, avatar_url, created_at").eq("id", inviteUserId).maybeSingle();
        if (prof) setProfile(prof);
        // Load buddies for public display
        // Load actual buddies first, then fill to 20 with other users
        const { data: buddyRows } = await supabase
          .from("buddies")
          .select("requester_id, receiver_id")
          .or(`requester_id.eq.${inviteUserId},receiver_id.eq.${inviteUserId}`)
          .eq("status", "accepted");
        const buddyIds = (buddyRows || []).map(b =>
          b.requester_id === inviteUserId ? b.receiver_id : b.requester_id
        ).filter(Boolean);
        const { data: buddyProfiles } = buddyIds.length > 0 ? await supabase
          .from("profiles").select("id, display_name, avatar_url, created_at").in("id", buddyIds) : { data: [] };
        // Fill remaining slots with other users (exclude the profile owner and already-shown buddies)
        const excludeIds = [inviteUserId, ...buddyIds];
        const needed = Math.max(0, 20 - (buddyProfiles || []).length);
        const { data: otherProfiles } = needed > 0 ? await supabase
          .from("profiles").select("id, display_name, avatar_url, created_at")
          .not("id", "in", `(${excludeIds.join(",")})`)
          .order("created_at", { ascending: false })
          .limit(needed) : { data: [] };
        const combined = [...(buddyProfiles || []), ...(otherProfiles || [])];
        if (combined.length > 0) setPublicBuddies(combined);
        const { count: publishCount } = await supabase
          .from("vouch_boards")
          .select("*", { count: "exact", head: true })
          .eq("user_id", inviteUserId)
          .not("published_at", "is", null);
        setPublicPublishCount(publishCount || 0);
        const { board: activeVouchBoard } = await fetchDisplayVouchBoard(supabase, inviteUserId);
        const { data: rows } = await supabase
          .from("endorsements").select("*").eq("user_id", inviteUserId).order("created_at", { ascending: true });
        const b = { movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [] };
        (rows || []).forEach(row => {
          if (b[row.category] && b[row.category].length < 5) {
            b[row.category].push({
              id: row.item_id, title: row.title, sub: row.subtitle || "",
              poster: row.poster || null, comment: row.comment || "",
              vouched: row.vouched || false, sourceUrl: row.source_url || null,
            });
          }
        });
        setBoard({ shelf: b, activeVouchBoard: activeVouchBoard || null });
        const itemBadges = await loadBadgesForUser(inviteUserId).catch(() => ({}));
        setPublicItemBadges(itemBadges);
        loadUserBadges(inviteUserId, itemBadges).then(setPublicUserBadges).catch(() => {});
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [inviteUserId]);

  if (loading) return <><Styles /><div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg }}><div className="loading">Loading…</div></div></>;
  if (!board) return <><Styles /><Auth inviteUserId={inviteUserId} /></>;

  const name = profile?.display_name || profile?.username || "Someone";

  return (
    <>
      <Styles />
      <div className="app">
        <div style={{ background: T.ink, color: T.bg, padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14 }}>
            You're viewing <strong style={{ fontStyle: "normal" }}>{name}'s</strong> Vouch board.
          </div>
          <button onClick={onSignUp} style={{ background: T.bg, color: T.ink, border: "none", fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.15em", padding: "10px 18px", cursor: "pointer", width: "100%" }}>
            Create Your Own →
          </button>
        </div>
        <header className="masthead">
          <div className="masthead-meta">
            <span style={{ flex: 1 }}>Est. 2026</span>
            <span className="masthead-meta-stars" style={{ flex: "0 0 auto" }}>✦ · ✦ · ✦</span>
            <span style={{ flex: 1, display: "flex", justifyContent: "flex-end", fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: T.inkMid }}>vouch5.com</span>
          </div>
          <div className="masthead-nameplate"><img src="/vouch5-logo.png" alt="Vouch5" style={{height:"clamp(80px,16vw,160px)",display:"block",margin:"0 auto"}} /></div>
          <div className="masthead-rule-ornament"><span>—</span><span>✦</span><span>—</span></div>
          <div className="masthead-tagline">Love it? Vouch for it.</div>
        </header>
        <main className="page">
          <div style={{ marginBottom: 24, borderTop: `1px solid ${T.paperDark}`, borderBottom: `1px solid ${T.paperDark}`, padding: "14px 0", display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 8 }}>
            {[{label: "Your Vouch", desc: "Up to 5 tiles, one board at a time, updated once a week"},{label: "Your Shelf", desc: "Up to 5 per category — change it whenever you like"},{label: "Buddies", desc: "See what your friends are vouching for. Agree with anything that resonates, or add it to your Queue."}].map(item => (
              <div key={item.label} style={{ textAlign: "center", flex: "1 1 100px" }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 10, letterSpacing: "0.15em", color: T.ink }}>{item.label}</div>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkMid, marginTop: 3 }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <button onClick={onSignUp} className="btn btn-solid" style={{ width: "100%", padding: "12px", fontSize: 13, marginBottom: 28 }}>Create Your Own Vouch Board →</button>
          <div className="ornament"><span>—</span><span>✦</span><span>—</span></div>
          {/* Profile header - looks like signed-in view */}
          <div style={{ marginBottom: 28, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <Avatar name={name} size={56} avatarUrl={profile?.avatar_url} />
                <div className="board-name" style={{ fontSize: 28, marginBottom: 0 }}>{name}</div>
              </div>
              <div className="board-sub" style={{ marginBottom: 8 }}>@{profile?.username || ""}</div>
              <ProfileBadgeBar badges={publicUserBadges} ownerName={name} />
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.12em", color: T.inkLight }}>
                  <span style={{ fontWeight: 700, color: T.ink, fontSize: "12px", fontFamily: "'Spectral',serif" }}>{publicPublishCount}</span>
                  {" "}{publicPublishCount === 1 ? "vouch" : "vouches"}
                </div>
                {publicBuddies.length > 0 && (
                  <div onClick={() => setShowPublicBuddies(true)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.12em", color: T.inkLight, cursor: "pointer" }}>
                    <span style={{ fontWeight: 700, color: T.ink, fontSize: "12px", fontFamily: "'Spectral',serif" }}>{publicBuddies.length}</span> {" buddies"}
                  </div>
                )}
              </div>
            </div>
          </div>
          {board?.activeVouchBoard && (() => {
            const avb = board.activeVouchBoard;
            const vbBoard = { movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [] };
            (avb.vouch_board_items || []).sort((a,b) => a.position - b.position).slice(0,5).forEach(item => {
              if (vbBoard[item.category]) vbBoard[item.category].push(vouchItemToDisplay(item));
            });
            const theme = boardThemeName(avb);
            return (
              <div className={getVouchSectionClassName(theme)} style={{ marginBottom: 52 }}>
                <div className="vouch-section-header">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="vouch-section-label" style={getVouchThemeLabelStyle(theme)}>{theme}</div>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.4)", marginTop: 3 }}>Vouch</div>
                    {avb.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: "rgba(200,194,180,0.45)", marginTop: 4 }}>{avb.description}</div>}
                    {avb.published_at && <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.1em", color: "rgba(200,194,180,0.3)", marginTop: 4 }}>Published {new Date(avb.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>}
                  </div>
                </div>
                <VouchSection board={vbBoard} isOwn={false} onCard={()=>{}} onAdd={()=>{}} onRemove={()=>{}} onDudeSame={() => setShowSignupNudge(true)} myReactions={[]} hideHeader={true} itemBadges={publicItemBadges} badgeOwnerName={profile?.display_name} />
              </div>
            );
          })()}
          {publicBuddies.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.18em", color: T.inkMid }}>Also on Vouch</div>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkFaint, cursor: "pointer" }} onClick={() => setShowSignupNudge(true)}>Sign up to see their boards →</div>
              </div>
              <div style={{ display: "flex", gap: 16, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 4 }}>
                {publicBuddies.map((b, i) => (
                  <div key={i} onClick={() => setShowSignupNudge(true)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0, width: 72 }}>
                    <Avatar name={b.display_name} size={64} avatarUrl={b.avatar_url} />
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.06em", color: T.inkMid, textAlign: "center", lineHeight: 1.3, width: 72, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.display_name.split(" ")[0]}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {[...CATEGORIES].sort((a, b) => ((board?.shelf?.[b.key] || []).length - (board?.shelf?.[a.key] || []).length)).map(cat => {
            const items = board?.shelf?.[cat.key] || [];
            if (items.length === 0) return null;
            return <CatSection key={cat.key} catKey={cat.key} label={cat.label} items={items} isOwn={false} onCard={() => {}} onAdd={() => {}} onRemove={() => {}} onDudeSame={() => setShowSignupNudge(true)} myReactions={[]} itemBadges={publicItemBadges} badgeOwnerName={profile?.display_name} />;
          })}

          <div style={{ marginTop: 48, padding: "32px 0", borderTop: `3px double ${T.ink}`, textAlign: "center" }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 900, marginBottom: 8 }}>Make your own board.</div>
            <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkMid, marginBottom: 24 }}>What would you put your name behind right now?</div>
            <button onClick={onSignUp} className="btn btn-solid" style={{ fontSize: 13, padding: "12px 32px" }}>Get Started — It's Free</button>
          </div>
        </main>
        <footer style={{ borderTop: `3px double ${T.ink}`, padding: "24px 28px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid }}>© {new Date().getFullYear()} Vouch5. All Rights Reserved.</div>
        </footer>

        {showPublicBuddies && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(17,16,8,0.82)", zIndex: 900, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 72 }} onClick={() => setShowPublicBuddies(false)}>
            <div style={{ background: T.bg, width: "100%", maxWidth: 540, maxHeight: "82vh", overflow: "hidden", border: `1px solid ${T.ink}`, display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: `2px solid ${T.ink}` }}>
                <div style={{ fontFamily: "'Spectral',serif", fontWeight: 700, fontSize: 17 }}>{name}'s Buddies</div>
                <button onClick={() => setShowPublicBuddies(false)} style={{ fontFamily: "'Spectral',serif", fontSize: 22, background: "transparent", border: "none", cursor: "pointer", color: T.ink, opacity: 0.6 }}>×</button>
              </div>
              <div style={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>
                {publicBuddies.map((b, i) => (
                  <div key={i} onClick={() => setShowSignupNudge(true)} style={{ display: "flex", lignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.paperDark}`, cursor: "pointer" }}>
                    <Avatar name={b.display_name} size={48} avatarUrl={b.avatar_url} />
                    <div>
                      <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 15 }}>{b.display_name}</div>
                      <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: T.inkLight }}>Sign in to view →</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {showSignupNudge && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(17,16,8,0.82)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setShowSignupNudge(false)}>
            <div style={{ background: T.bg, maxWidth: 420, width: "100%", border: `2px solid ${T.ink}`, padding: "32px 28px" }} onClick={e => e.stopPropagation()}>
              <div style={{ fontFamily: "'Times New Roman',Times,serif", fontWeight: 900, fontSize: 36, marginBottom: 4 }}><em style={{fontStyle:"italic"}}>V</em>ouch<sup style={{fontSize:"0.42em",verticalAlign:"super",lineHeight:0,fontWeight:900}}>5</sup></div>
              <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 24 }}>Love it? Vouch for it.</div>
              <div style={{ marginBottom: 28, borderBottom: `1px solid ${T.paperDark}`, paddingBottom: 28 }}>
                <HowItWorks />
              </div>
              <button onClick={onSignUp} style={{ width: "100%", fontFamily: "'Spectral SC',serif", fontSize: "10.5px", fontWeight: 600, letterSpacing: "0.25em", padding: 13, background: T.ink, color: T.bg, border: "none", cursor: "pointer" }}>
                Create Your Account →
              </button>
              <button onClick={() => setShowSignupNudge(false)} style={{ width: "100%", fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", padding: "10px", background: "transparent", color: T.inkFaint, border: "none", cursor: "pointer", marginTop: 8 }}>
                Maybe later
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function HowItWorks() {
  return (
    <div style={{ fontFamily: "'Spectral',serif", color: T.inkMid, maxWidth: 380, margin: "0 auto", textAlign: "center" }}>
      <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.2em", color: T.ink, marginBottom: 20 }}>How It Works</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 600, fontSize: 10, letterSpacing: "0.15em", color: T.ink, marginBottom: 5 }}>Your Vouch</div>
          <div style={{ fontSize: 13, lineHeight: 1.7, fontStyle: "italic", color: T.inkMid }}>Publish up to 5 tiles — a movie, album, book, show, artist, whatever you would put your name behind right now. You can only have one Vouch published at a time, and you can update it once a week.</div>
        </div>
        <div style={{ borderTop: `1px solid ${T.paperDark}` }} />
        <div>
          <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 600, fontSize: 10, letterSpacing: "0.15em", color: T.ink, marginBottom: 5 }}>Your Shelf</div>
          <div style={{ fontSize: 13, lineHeight: 1.7, fontStyle: "italic", color: T.inkMid }}>Up to 5 picks per category across Film, Albums, Artists, Songs, Books, and Television. Change it whenever you like — and choose which categories appear on your shelf.</div>
        </div>
        <div style={{ borderTop: `1px solid ${T.paperDark}` }} />
        <div>
          <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 600, fontSize: 10, letterSpacing: "0.15em", color: T.ink, marginBottom: 5 }}>Buddies</div>
          <div style={{ fontSize: 13, lineHeight: 1.7, fontStyle: "italic", color: T.inkMid }}>Connect with friends and see what they are vouching for. Agree with anything that resonates, or add it to your Queue to revisit later.</div>
        </div>
      </div>
    </div>
  );
}

function Avatar({ name, size = 36, avatarUrl }) {
  const parts = (name || "?").trim().split(" ");
  const initials = parts.length >= 2
    ? parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
  const [imgFailed, setImgFailed] = useState(false);
  if (avatarUrl && !imgFailed) {
    return (
      <img src={avatarUrl} alt={name} onError={() => setImgFailed(true)}
        style={{ width: size, height: size, objectFit: "cover", flexShrink: 0, display: "block", border: `1px solid ${T.paperDark}`, filter: "grayscale(100%)" }} />
    );
  }
  return (
    <div style={{
      width: size, height: size,
      background: T.ink, color: T.bg, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Times New Roman', Times, serif", fontWeight: 900,
      fontSize: size * 0.33, letterSpacing: "0.04em", userSelect: "none"
    }}>
      {initials}
    </div>
  );
}

function IOSInstallBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone;
    const dismissed = localStorage.getItem("vouch-install-dismissed");
    if (isIOS && !isStandalone && !dismissed) setShow(true);
  }, []);
  if (!show) return null;
  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999, background: T.ink, color: T.bg, padding: "14px 20px 28px", display: "flex", alignItems: "flex-start", gap: 14, boxShadow: "0 -4px 20px rgba(0,0,0,0.3)" }}>
      <div style={{ width: 44, height: 44, background: T.bg, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ fontFamily: "'Times New Roman',Times,serif", fontWeight: 900, fontSize: 22, color: T.ink, fontStyle: "italic" }}>V.</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", marginBottom: 4 }}>Add Vouch to your Home Screen</div>
        <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: "rgba(200,194,180,0.7)", lineHeight: 1.5 }}>
          Tap <strong style={{ fontStyle: "normal" }}>Share</strong> in your browser then <strong style={{ fontStyle: "normal" }}>"Add to Home Screen"</strong> for the full app experience.
        </div>
      </div>
      <button onClick={() => { setShow(false); localStorage.setItem("vouch-install-dismissed", "1"); }} style={{ background: "transparent", border: "none", color: "rgba(200,194,180,0.5)", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
    </div>
  );
}

function Lightbox({ items, start, catLabel, onClose }) {
  const [i, setI] = useState(start);
  const item = items[i];
  useEffect(() => {
    const h = e => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowRight") setI(x => Math.min(x + 1, items.length - 1));
      if (e.key === "ArrowLeft")  setI(x => Math.max(x - 1, 0));
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [items.length, onClose]);
  return (
    <div className="lb-overlay" onClick={onClose}>
      <button className="lb-close" onClick={onClose}>×</button>
      <div className="lb-body" onClick={e => e.stopPropagation()}>
        <div className="lb-category">{catLabel}</div>
        <img src={item.poster} alt={item.title} className="lb-poster" onError={e => e.target.style.display = "none"} />
        <div className="lb-title">{item.title}</div>
        <div className="lb-sub">{item.artist || item.author || item.year || ""}</div>
        {item.comment && <div className="lb-comment">"{item.comment}"</div>}
        <div className="lb-nav">
          <button className="lb-nav-btn" disabled={i === 0} onClick={() => setI(x => x - 1)}>← Prev</button>
          <div className="lb-dots">{items.map((_, d) => <div key={d} className={`lb-dot${d === i ? " on" : ""}`} onClick={() => setI(d)} />)}</div>
          <button className="lb-nav-btn" disabled={i === items.length - 1} onClick={() => setI(x => x + 1)}>Next →</button>
        </div>
      </div>
    </div>
  );
}

function AddModal({ catKey, catLabel, used, onClose, onAdd }) {
  const [q, setQ]             = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked]   = useState(null);
  const [note, setNote]       = useState("");
  const [busy, setBusy]       = useState(false);
  const timer                 = useRef(null);
  const remaining             = 5 - used;

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        if (catKey === "movies") {
          const res  = await fetch(`https://api.themoviedb.org/3/search/movie?api_key=${TMDB}&query=${encodeURIComponent(q)}&language=en-US`);
          const data = await res.json();
          setResults((data.results || []).slice(0, 8).map(r => ({
            id: r.id,
            title: r.title,
            sub: r.release_date ? r.release_date.slice(0, 4) : "",
            poster: r.poster_path ? `https://image.tmdb.org/t/p/w500${r.poster_path}` : null,
            sourceUrl: justWatchSearchUrl(r.title, r.release_date?.slice(0, 4)),
          })));
        } else if (catKey === "shows") {
          const res  = await fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB}&query=${encodeURIComponent(q)}&language=en-US`);
          const data = await res.json();
          setResults((data.results || []).slice(0, 8).map(r => ({
            id: r.id,
            title: r.name,
            sub: r.first_air_date ? r.first_air_date.slice(0, 4) : "",
            poster: r.poster_path ? `https://image.tmdb.org/t/p/w500${r.poster_path}` : null,
            sourceUrl: justWatchSearchUrl(r.name, r.first_air_date?.slice(0, 4)),
          })));
        } else if (catKey === "songs" || catKey === "albums" || catKey === "artists" || catKey === "podcasts") {
          const typeMap = { songs: "track", albums: "album", artists: "artist", podcasts: "show" };
          const res = await fetch(`/api/spotify?q=${encodeURIComponent(q)}&type=${typeMap[catKey]}`);
          const data = await res.json();
          if (catKey === "songs") {
            setResults((data.tracks?.items || []).slice(0, 8).map(r => ({ id: r.id, title: r.name, sub: r.artists?.[0]?.name || "", poster: r.album?.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/track/${r.id}` })));
          } else if (catKey === "albums") {
            setResults((data.albums?.items || []).slice(0, 8).map(r => ({ id: r.id, title: r.name, sub: r.artists?.[0]?.name || "", poster: r.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/album/${r.id}` })));
          } else if (catKey === "podcasts") {
            setResults((data.shows?.items || []).slice(0, 8).map(r => ({ id: r.id, title: r.name, sub: r.publisher || "", poster: r.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/show/${r.id}` })));
          } else {
            setResults((data.artists?.items || []).slice(0, 8).map(r => ({ id: r.id, title: r.name, sub: r.genres?.[0] || "", poster: r.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/artist/${r.id}` })));
          }
        } else if (catKey === "books") {
          const res  = await fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(q)}&limit=8&language=eng`);
          const data = await res.json();
          setResults((data.docs || []).slice(0, 8).map(r => {
            const coverId = r.cover_i;
            const isbn = pickBookIsbn(r.isbn);
            const poster = coverId ? `https://covers.openlibrary.org/b/id/${coverId}-L.jpg` : isbn ? `https://covers.openlibrary.org/b/isbn/${isbn}-L.jpg` : null;
            return { id: r.key || r.title, title: r.title, sub: (r.author_name || []).join(", "), poster, isbn, sourceUrl: null };
          }));
        } else {
          setResults([]);
        }
      } catch (e) { console.error(e); }
      setBusy(false);
    }, 350);
  }, [q, catKey]);

  const confirm = async () => {
    if (!picked) return;
    let sourceUrl = picked.sourceUrl;
    if (catKey === "movies" || catKey === "shows") {
      sourceUrl = await fetchJustWatchTitleUrl(picked.title, picked.sub, catKey);
    } else if (catKey === "books") {
      sourceUrl = await fetchBookStoreUrl(picked.title, picked.sub, picked.isbn, picked.sourceUrl);
    }
    onAdd(catKey, { ...picked, sourceUrl, comment: note });
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">Add to {catLabel}</div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {remaining <= 0
            ? <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkLight, padding: "12px 0" }}>You've added 5 {catLabel} picks — remove one to swap it out.</div>
            : picked
              ? <>
                  <div className="selected-preview">
                    <img src={picked.poster} alt={picked.title} className="result-img" onError={e => { e.target.style.background = T.paperDark; e.target.src = ""; }} />
                    <div style={{ flex: 1 }}>
                      <div className="result-title">{picked.title}</div>
                      <div className="result-sub">{picked.sub || ""}</div>
                    </div>
                    <button onClick={() => setPicked(null)} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: T.inkFaint }}>×</button>
                  </div>
                  <span className="comment-label">Why are you vouching for this? <span style={{ fontStyle: "italic", fontFamily: "'Spectral',serif", textTransform: "none", letterSpacing: 0, fontWeight: 300 }}>(optional)</span></span>
                  <textarea className="comment-area" placeholder="Say something about it…" value={note} onChange={e => setNote(e.target.value)} maxLength={200} />
                  <div className="char-count">{note.length} / 200</div>
                  <button className="btn btn-solid" style={{ width: "100%" }} onClick={confirm}>Add to Shelf</button>
                </>
              : <>
                  <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9.5px", letterSpacing: "0.16em", color: T.inkFaint, marginBottom: 12 }}>
                    {remaining > 0 ? `${remaining} more can be added` : ""}
                  </div>
                  <input className="search-input" placeholder={`Search ${catLabel.toLowerCase()}…`} value={q} onChange={e => setQ(e.target.value)} autoFocus />
                  {busy && <div className="loading">Searching…</div>}
                  {!busy && q.trim() && results.length === 0 && <div className="no-results">No results found.</div>}
                  {results.map(r => (
                    <div key={r.id} className="result-item" onClick={() => setPicked(r)}>
                      {r.poster ? <img src={r.poster} alt={r.title} className="result-img" onError={e => { e.target.style.background = T.paperDark; e.target.src = ""; }} /> : <div className="result-img" />}
                      <div>
                        <div className="result-title">{r.title}</div>
                        <div className="result-sub">{r.sub || ""}</div>
                      </div>
                    </div>
                  ))}
                </>
          }
        </div>
      </div>
    </div>
  );
}

function UniversalSearchModal({ used, onClose, onAdd }) {
  const [q, setQ]             = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked]   = useState(null);
  const [note, setNote]       = useState("");
  const [busy, setBusy]       = useState(false);
  const [filter, setFilter]   = useState("all");
  const timer                 = useRef(null);
  const remaining             = 5 - used;

  const FILTERS = [
    { key: "all",      label: "All"      },
    { key: "movies",   label: "Film"     },
    { key: "shows",    label: "TV"       },
    { key: "songs",    label: "Songs"    },
    { key: "albums",   label: "Albums"   },
    { key: "artists",  label: "Artists"  },
    { key: "books",    label: "Books"    },
    { key: "podcasts", label: "Podcasts" },
  ];

  const visibleResults = filter === "all" ? results : results.filter(r => r.catKey === filter);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const [movieRes, tvRes, trackRes, albumRes, artistRes, booksRes, podcastRes] = await Promise.all([
          fetch(`https://api.themoviedb.org/3/search/movie?api_key=${TMDB}&query=${encodeURIComponent(q)}&language=en-US`).then(r => r.json()),
          fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB}&query=${encodeURIComponent(q)}&language=en-US`).then(r => r.json()),
          fetch(`/api/spotify?q=${encodeURIComponent(q)}&type=track`).then(r => r.json()),
          fetch(`/api/spotify?q=${encodeURIComponent(q)}&type=album`).then(r => r.json()),
          fetch(`/api/spotify?q=${encodeURIComponent(q)}&type=artist`).then(r => r.json()),
          fetch(`/api/spotify?q=${encodeURIComponent(q)}&type=show`).then(r => r.json()),
          fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(q)}&limit=3&language=eng`).then(r => r.json()),
        ]);
        const mixed = [];
        const movieResults = (movieRes.results || []).slice(0, 3);
        const tvResults    = (tvRes.results || []).slice(0, 2);
        movieResults.forEach(r => mixed.push({ id: r.id, title: r.title, catKey: "movies", catLabel: "Film", sub: r.release_date ? r.release_date.slice(0, 4) : "", poster: r.poster_path ? `https://image.tmdb.org/t/p/w500${r.poster_path}` : null, sourceUrl: justWatchSearchUrl(r.title, r.release_date?.slice(0, 4)) }));
        tvResults.forEach(r => mixed.push({ id: r.id, title: r.name, catKey: "shows", catLabel: "Television", sub: r.first_air_date ? r.first_air_date.slice(0, 4) : "", poster: r.poster_path ? `https://image.tmdb.org/t/p/w500${r.poster_path}` : null, sourceUrl: justWatchSearchUrl(r.name, r.first_air_date?.slice(0, 4)) }));
        (trackRes.tracks?.items || []).slice(0, 3).forEach(r => mixed.push({ id: r.id, title: r.name, catKey: "songs", catLabel: "Songs", sub: r.artists?.[0]?.name || "", poster: r.album?.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/track/${r.id}` }));
        (albumRes.albums?.items || []).slice(0, 2).forEach(r => mixed.push({ id: r.id, title: r.name, catKey: "albums", catLabel: "Albums", sub: r.artists?.[0]?.name || "", poster: r.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/album/${r.id}` }));
        (artistRes.artists?.items || []).slice(0, 2).forEach(r => mixed.push({ id: r.id, title: r.name, catKey: "artists", catLabel: "Artists", sub: r.genres?.[0] || "", poster: r.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/artist/${r.id}` }));
        (podcastRes?.shows?.items || []).slice(0, 2).forEach(r => mixed.push({ id: r.id, title: r.name, catKey: "podcasts", catLabel: "Podcasts", sub: r.publisher || "", poster: r.images?.[0]?.url || null, sourceUrl: `https://open.spotify.com/show/${r.id}` }));
        (booksRes.docs || []).slice(0, 2).forEach(r => {
          const coverId = r.cover_i;
          const isbn = pickBookIsbn(r.isbn);
          const poster = coverId ? `https://covers.openlibrary.org/b/id/${coverId}-L.jpg` : isbn ? `https://covers.openlibrary.org/b/isbn/${isbn}-L.jpg` : null;
          mixed.push({ id: r.key || r.title, title: r.title, catKey: "books", catLabel: "Book", sub: (r.author_name || []).join(", "), poster, isbn, sourceUrl: null });
        });
        setResults(mixed);
      } catch(e) { console.error(e); }
      setBusy(false);
    }, 400);
  }, [q]);

  const confirm = async () => {
    if (!picked) return;
    let sourceUrl = picked.sourceUrl;
    if (picked.catKey === "movies" || picked.catKey === "shows") {
      sourceUrl = await fetchJustWatchTitleUrl(picked.title, picked.sub, picked.catKey);
    } else if (picked.catKey === "books") {
      sourceUrl = await fetchBookStoreUrl(picked.title, picked.sub, picked.isbn, picked.sourceUrl);
    }
    await onAdd(picked.catKey, { ...picked, sourceUrl, comment: note });
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">Add to Vouch 5</div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {remaining <= 0
            ? <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkLight, padding: "12px 0" }}>Your Vouch 5 is full.</div>
            : picked
              ? <>
                  <div className="selected-preview">
                    <img src={picked.poster} alt={picked.title} className="result-img" onError={e => { e.target.style.background = T.paperDark; e.target.src = ""; }} />
                    <div style={{ flex: 1 }}>
                      <div className="result-title">{picked.title}</div>
                      <div className="result-sub">{picked.sub} · {picked.catLabel}</div>
                    </div>
                    <button onClick={() => setPicked(null)} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: T.inkFaint }}>×</button>
                  </div>
                  <span className="comment-label">Why are you vouching for this? <span style={{ fontStyle: "italic", fontFamily: "'Spectral',serif", textTransform: "none", letterSpacing: 0, fontWeight: 300 }}>(optional)</span></span>
                  <textarea className="comment-area" placeholder="Say something about it…" value={note} onChange={e => setNote(e.target.value)} maxLength={200} />
                  <div className="char-count">{note.length} / 200</div>
                  <button className="btn btn-solid" style={{ width: "100%" }} onClick={confirm}>Add to Shelf</button>
                </>
              : <>
                  <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9.5px", letterSpacing: "0.16em", color: T.inkFaint, marginBottom: 12 }}>
                    {remaining > 0 ? `${remaining} more can be added` : ""}
                  </div>
                  <input className="search-input" placeholder="Search films, shows, songs, albums, artists, books, podcasts…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                    {FILTERS.map(f => (
                      <button key={f.key} onClick={() => setFilter(f.key)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", padding: "4px 10px", border: `1px solid ${filter === f.key ? T.ink : T.paperDark}`, background: filter === f.key ? T.ink : "transparent", color: filter === f.key ? T.bg : T.inkMid, cursor: "pointer", transition: "all 0.12s" }}>{f.label}</button>
                    ))}
                  </div>
                  {busy && <div className="loading">Searching…</div>}
                  {!busy && q.trim() && visibleResults.length === 0 && <div className="no-results">No results found.</div>}
                  {visibleResults.map((r, i) => (
                    <div key={r.id + r.catKey + i} className="result-item" onClick={() => setPicked(r)}>
                      {r.poster ? <img src={r.poster} alt={r.title} className="result-img" onError={e => { e.target.style.background = T.paperDark; e.target.src = ""; }} /> : <div className="result-img" />}
                      <div style={{ flex: 1 }}>
                        <div className="result-title">{r.title}</div>
                        <div className="result-sub">{r.sub}</div>
                      </div>
                      <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8.5px", letterSpacing: "0.15em", color: T.inkFaint, flexShrink: 0 }}>{r.catLabel}</div>
                    </div>
                  ))}
                </>
          }
        </div>
      </div>
    </div>
  );
}

function UserBadgeExplainModal({ badge, ownerName, onClose }) {
  const ribbon = userBadgeRibbon(badge?.type, badge?.value);
  if (!ribbon) return null;
  const body = userBadgeExplain(badge.type, ownerName, badge.value);
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 340 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 14px 0" }}>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ textAlign: "center", paddingTop: 4, paddingBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <ProfileRibbonBadge badge={badge} interactive={false} />
          </div>
          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, lineHeight: 1.7, color: T.inkMid }}>{body}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ProfileRibbonBadge({ badge, interactive = true, ownerName }) {
  const [showExplain, setShowExplain] = useState(false);
  const ribbon = userBadgeRibbon(badge?.type, badge?.value);
  if (!ribbon) return null;
  const h = 19;
  const open = e => { e.stopPropagation(); setShowExplain(true); };
  const tailColor = userBadgeTailColor(badge.type, badge.value);
  const visual = (
    <>
      <span style={{
        ...ribbon.style,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 30,
        height: h,
        padding: "0 7px",
        fontFamily: "'Spectral SC',serif",
        fontSize: badge.type === "streak" || badge.type === "vouches" ? "9px" : "7px",
        fontWeight: 700,
        letterSpacing: badge.type === "streak" || badge.type === "vouches" ? "0.06em" : "0.18em",
        lineHeight: 1,
      }}>{ribbon.label}</span>
      <span style={{
        width: 0,
        height: 0,
        borderLeft: "15px solid transparent",
        borderRight: "15px solid transparent",
        borderTop: `5px solid ${tailColor}`,
        marginTop: -1,
      }} />
    </>
  );
  if (!interactive) {
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", flexShrink: 0, filter: "drop-shadow(0 1px 1px rgba(17,16,8,0.35))" }}>
        {visual}
      </span>
    );
  }
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        aria-label={ribbon.title}
        onClick={open}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(e); } }}
        style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", flexShrink: 0, cursor: "pointer", filter: "drop-shadow(0 1px 1px rgba(17,16,8,0.35))" }}
      >
        {visual}
      </span>
      {showExplain && <UserBadgeExplainModal badge={badge} ownerName={ownerName} onClose={() => setShowExplain(false)} />}
    </>
  );
}

function ProfileBadgeBar({ badges, ownerName }) {
  if (!badges?.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10, marginTop: 4 }}>
      {badges.map(b => (
        <ProfileRibbonBadge key={`${b.type}-${b.value || ""}`} badge={b} ownerName={ownerName} />
      ))}
    </div>
  );
}

function badgeWhoWas(ownerName) {
  const who = ownerName || "They";
  const verb = who === "You" || who === "They" ? "were" : "was";
  return `${who} ${verb}`;
}

function BadgeExplainModal({ type, ownerName, circlePhrase = "your", onClose }) {
  const ribbon = BADGE_RIBBONS[type];
  if (!ribbon) return null;
  const whoWas = badgeWhoWas(ownerName);
  const body = type === "first_global"
    ? `${whoWas} the first person to Vouch for this globally.`
    : `${whoWas} the first person to Vouch for this in ${circlePhrase} circle.`;
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 340 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 14px 0" }}>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ textAlign: "center", paddingTop: 4, paddingBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <RibbonBadge type={type} interactive={false} />
          </div>
          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, lineHeight: 1.7, color: T.inkMid }}>{body}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RibbonBadge({ type, compact = false, interactive = true, ownerName, circlePhrase = "your" }) {
  const [showExplain, setShowExplain] = useState(false);
  const ribbon = BADGE_RIBBONS[type];
  if (!ribbon) return null;
  const h = compact ? 15 : 19;
  const open = e => { e.preventDefault(); e.stopPropagation(); setShowExplain(true); };
  const visual = (
    <>
      <span style={{
        ...ribbon.style,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: compact ? 24 : 30,
        height: h,
        padding: "0 7px",
        fontFamily: "'Spectral SC',serif",
        fontSize: compact ? "6px" : "7px",
        fontWeight: 700,
        letterSpacing: "0.18em",
        lineHeight: 1,
      }}>{ribbon.label}</span>
      <span style={{
        width: 0,
        height: 0,
        borderLeft: compact ? "12px solid transparent" : "15px solid transparent",
        borderRight: compact ? "12px solid transparent" : "15px solid transparent",
        borderTop: `${compact ? 4 : 5}px solid ${type === "first_global" ? "#9A7820" : "#909090"}`,
        marginTop: -1,
      }} />
    </>
  );
  if (!interactive) {
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", flexShrink: 0, filter: "drop-shadow(0 1px 1px rgba(17,16,8,0.35))" }}>
        {visual}
      </span>
    );
  }
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        aria-label={ribbon.title}
        onClick={open}
        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
        onTouchStart={e => { e.stopPropagation(); }}
        onTouchEnd={open}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(e); } }}
        style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", flexShrink: 0, cursor: "pointer", filter: "drop-shadow(0 1px 1px rgba(17,16,8,0.35))", position: "relative", zIndex: 51, padding: compact ? 4 : 6, margin: compact ? -4 : -6 }}
      >
        {visual}
      </span>
      {showExplain && <BadgeExplainModal type={type} ownerName={ownerName} circlePhrase={circlePhrase} onClose={() => setShowExplain(false)} />}
    </>
  );
}

function VouchRibbon({ badges, align = "right", compact = false, ownerName, circlePhrase = "your" }) {
  const primary = primaryBadge(badges);
  if (!primary.length) return null;
  const pos = align === "left" ? { top: compact ? 6 : 10, left: compact ? 6 : 10 } : { top: compact ? 6 : 10, right: compact ? 6 : 10 };
  return (
    <div style={{ position: "absolute", ...pos, zIndex: 50, display: "flex", flexDirection: "row", alignItems: "flex-start", pointerEvents: "auto" }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
      <RibbonBadge type={primary[0]} compact={compact} ownerName={ownerName} circlePhrase={circlePhrase} />
    </div>
  );
}

function VouchedByLine({ names, name, first = false, dark = false }) {
  const label = first ? "First vouched by" : "Vouched by";
  if (name) {
    return (
      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: dark ? 10 : 11, color: dark ? "rgba(200,194,180,0.45)" : T.inkLight, marginTop: 4, lineHeight: 1.4 }}>
        {label} {name}
      </div>
    );
  }
  if (!names?.length) return null;
  return (
    <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: dark ? 10 : 11, color: dark ? "rgba(200,194,180,0.45)" : T.inkLight, marginTop: 4, lineHeight: 1.4 }}>
      {label} {names.join(", ")}
    </div>
  );
}

function VouchSection({ board, isOwn, onCard, onAdd, onRemove, onDudeSame, myReactions, hideHeader, hideEmptySlots, onAddToQueue, queue, ownerId, onMusicOpen, singleTile, itemBadges, badgeOwnerName, badgeCirclePhrase, tileCommentsByItemId, canCommentTiles, currentUserId, onPostTileComment, onDeleteTileComment }) {
  const [idx, setIdx]      = useState(0);
  const touchStartX        = useRef(null);
  const touchStartY        = useRef(null);
  const currentOffsetX     = useRef(0);
  const isHoriz            = useRef(false);
  const containerRef       = useRef(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640; // eslint-disable-line

  const allItems = [];
  CATEGORIES.forEach(cat => {
    (board[cat.key] || []).forEach(item => {
      if (item.vouched) allItems.push({ ...item, _cat: cat.key, _catLabel: cat.label });
    });
  });
  const total = allItems.length;

  useEffect(() => { if (idx >= total && total > 0) setIdx(total - 1); }, [total, idx]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isMobile) return;
    const handleStart = e => { touchStartX.current = e.touches[0].clientX; touchStartY.current = e.touches[0].clientY; currentOffsetX.current = 0; isHoriz.current = false; };
    const handleMove = e => {
      if (touchStartX.current === null) return;
      const dx = e.touches[0].clientX - touchStartX.current;
      const dy = e.touches[0].clientY - touchStartY.current;
      if (!isHoriz.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      if (!isHoriz.current) isHoriz.current = Math.abs(dx) > Math.abs(dy);
      if (!isHoriz.current) return;
      e.preventDefault();
      const w = el.offsetWidth;
      const bounded = (idx === 0 && dx > 0) || (idx === total - 1 && dx < 0) ? dx * 0.15 : dx;
      currentOffsetX.current = bounded;
      const track = el.querySelector(".swipe-track");
      if (track) track.style.transform = `translateX(${-(idx * w) - bounded * -1}px)`;
    };
    const handleEnd = () => {
      if (!isHoriz.current) return;
      const w = el.offsetWidth;
      const dx = currentOffsetX.current;
      let newIdx = idx;
      if (dx < -(w * 0.22) && idx < total - 1) newIdx = idx + 1;
      else if (dx > (w * 0.22) && idx > 0) newIdx = idx - 1;
      const track = el.querySelector(".swipe-track");
      if (track) { track.style.transition = "transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)"; track.style.transform = `translateX(${-(newIdx * w)}px)`; setTimeout(() => { if (track) track.style.transition = ""; }, 350); }
      currentOffsetX.current = 0; setIdx(newIdx); touchStartX.current = null;
    };
    el.addEventListener("touchstart", handleStart, { passive: true });
    el.addEventListener("touchmove", handleMove, { passive: false });
    el.addEventListener("touchend", handleEnd, { passive: true });
    return () => { el.removeEventListener("touchstart", handleStart); el.removeEventListener("touchmove", handleMove); el.removeEventListener("touchend", handleEnd); };
  }, [idx, total, isMobile]);

  const prevIdxRef = useRef(idx);
  useEffect(() => {
    if (prevIdxRef.current === idx) return;
    prevIdxRef.current = idx;
    const el = containerRef.current;
    if (!el || !isMobile) return;
    const track = el.querySelector(".swipe-track");
    if (!track) return;
    const w = el.offsetWidth;
    track.style.transition = "transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
    track.style.transform = `translateX(${-(idx * w)}px)`;
    setTimeout(() => { if (track) track.style.transition = ""; }, 350);
  }, [idx, isMobile]);

  const CardFace = ({ it }) => {
    const badges = itemBadges?.[badgeKey(it._cat, it.id)] || [];
    const clickable = tileIsClickable(it, it._cat);
    const clickTile = () => {
      if (Math.abs(currentOffsetX.current) > 8) return;
      if (!clickable) { onCard(it._cat, (board[it._cat] || []).findIndex(x => x.id === it.id)); return; }
      openTileLink(it, { catKey: it._cat, onMusicOpen });
    };
    return (
    <div style={{ position: "relative", isolation: "isolate" }}>
      <VouchRibbon badges={badges} ownerName={badgeOwnerName} circlePhrase={badgeCirclePhrase || (isOwn ? "your" : "their")} />
      <TileMedia
        item={it}
        catKey={it._cat}
        onOpen={clickTile}
        poster={it.poster}
        title={it.title}
        badgeSize="lg"
        style={{ cursor: clickable ? "pointer" : "default" }}
      >
        {it.poster
          ? <img src={it.poster} alt={it.title} style={{ width: "100%", height: 340, objectFit: "contain", background: "#000", display: "block", border: `1px solid ${T.paperDark}`, cursor: clickable ? "pointer" : "default" }} onError={e => e.target.style.display = "none"} />
          : <div style={{ width: "100%", height: 340, background: T.paperDark, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Spectral',serif", fontSize: 18, color: T.inkLight, padding: 24, textAlign: "center", cursor: clickable ? "pointer" : "default" }}>{it.title}</div>}
      </TileMedia>
      <div style={{ padding: "14px 4px 4px" }}>
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.45)", marginBottom: 4 }}>{it._catLabel}</div>
        <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: 18, lineHeight: 1.2, marginBottom: 4, color: T.bg }}>{it.title}</div>
        <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: "rgba(200,194,180,0.7)" }}>{it.artist || it.author || it.sub || ""}</div>
        <TileBuddyComments
          comments={tileCommentsByItemId?.[it.boardItemId] || []}
          canComment={!!canCommentTiles}
          boardItemId={it.boardItemId}
          currentUserId={currentUserId}
          onPost={onPostTileComment}
          onDelete={onDeleteTileComment}
          dark
        />
        {!isOwn && (
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button onClick={e => { e.stopPropagation(); onDudeSame(it, ownerId); }} style={agreePillStyle(myReactions?.includes(String(it.id)), { flex: 1, fontSize: "8px", letterSpacing: "0.1em", padding: "5px 4px" })}>
              {myReactions?.includes(String(it.id))
                ? <><span style={{ color: AGREE_CHECK_GOLD }}>✓</span> Agreed</>
                : "Agree"}
            </button>
            {onAddToQueue && <button onClick={e => { e.stopPropagation(); onAddToQueue(it); }} style={grayPillStyle(!!queue?.find(q => String(q.id) === String(it.id)), { flex: 1, fontSize: "8px", letterSpacing: "0.1em", padding: "5px 4px" })}>{queue?.find(q => String(q.id) === String(it.id)) ? "✓ Queue" : "+ Queue"}</button>}
          </div>
        )}
      </div>

    </div>
    );
  };

  return (
    <div className={hideHeader ? "vouch-section-inner" : "vouch-section"}>
      {!hideHeader && (
        <div className="vouch-section-header">
          <div className="vouch-section-label">Vouch 5</div>
          <div className="vouch-section-sub">1 to 5 — your choice, your moment</div>
          {isOwn && <button className="vouch-section-add" onClick={onAdd}>+ Add</button>}
        </div>
      )}
      {isMobile ? (
        <div ref={containerRef} style={{ overflow: "hidden", userSelect: "none" }}>
          {allItems.length === 0 && isOwn ? (
            <div style={{ height: 280, border: `1px dashed ${T.paperDark}`, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, cursor: "pointer" }} onClick={onAdd}>
              <span style={{ fontSize: 28, color: T.inkFaint }}>+</span>
              <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.18em", color: T.inkFaint }}>Add to Vouch 5</span>
            </div>
          ) : (
            <div className="swipe-track" style={{ display: "flex", willChange: "transform" }}>
              {allItems.map((it, slideIdx) => (
                <div key={it.id + it._cat} style={{ flex: "0 0 100%", width: "100%", pointerEvents: slideIdx === idx ? "auto" : "none" }}>
                  <CardFace it={it} />
                </div>
              ))}
            </div>
          )}
          {total > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
              {allItems.map((_, i) => (
                <div key={i} onClick={() => setIdx(i)} style={{ width: 6, height: 6, borderRadius: "50%", background: i === idx ? T.ink : T.paperDark, cursor: "pointer", transition: "background 0.2s" }} />
              ))}
            </div>
          )}
        </div>
      ) : singleTile ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button onClick={() => setIdx(x => Math.max(x - 1, 0))} disabled={idx === 0} style={{ background: "transparent", border: "none", color: idx === 0 ? "rgba(200,194,180,0.2)" : "rgba(200,194,180,0.7)", fontSize: 32, cursor: idx === 0 ? "default" : "pointer", padding: "0 8px", flexShrink: 0, lineHeight: 1 }}>‹</button>
            <div style={{ flex: 1, maxWidth: 400, margin: "0 auto" }}>
              {allItems[idx] && <CardFace it={allItems[idx]} />}
            </div>
            <button onClick={() => setIdx(x => Math.min(x + 1, total - 1))} disabled={idx === total - 1} style={{ background: "transparent", border: "none", color: idx === total - 1 ? "rgba(200,194,180,0.2)" : "rgba(200,194,180,0.7)", fontSize: 32, cursor: idx === total - 1 ? "default" : "pointer", padding: "0 8px", flexShrink: 0, lineHeight: 1 }}>›</button>
          </div>
          {total > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
              {allItems.map((_, i) => (
                <div key={i} onClick={() => setIdx(i)} style={{ width: 6, height: 6, borderRadius: "50%", background: i === idx ? "rgba(200,194,180,0.9)" : "rgba(200,194,180,0.25)", cursor: "pointer", transition: "background 0.2s" }} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, justifyContent: "center", overflow: "visible" }}>
          {allItems.map((it, i) => (
            <div key={it.id + it._cat} className="card-large" style={{ position: "relative", flex: "1", maxWidth: 280, zIndex: total - i }}>
              <CardFace it={it} />
            </div>
          ))}
          {isOwn && Array(Math.max(0, 5 - allItems.length)).fill(null).map((_, i) => (
            <div key={`ve${i}`} className="slot-empty-large" style={{ flex: "1", maxWidth: 280 }} onClick={onAdd}>
              <div className="slot-empty-inner"><span className="slot-empty-plus">+</span>Vouch</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CatSection({ catKey, label, items, isOwn, onCard, onAdd, onRemove, onDudeSame, myReactions, onAddToQueue, queue, onMusicOpen, itemBadges, badgeOwnerName, badgeCirclePhrase }) {
  const [open, setOpen] = useState(true);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640; // eslint-disable-line
  const slots = Array(5).fill(null).map((_, i) => items[i] || null);
  const collapsed = isMobile && !open;
  return (
    <div className="cat-section">
      <div className="cat-header" style={{ cursor: isMobile ? "pointer" : "default" }} onClick={() => isMobile && setOpen(o => !o)}>
        <div className="cat-label">{label}</div>
        <div className="cat-sublabel">My Shelf</div>
        <div className="cat-count">{items.length > 0 ? items.length : ""}</div>
        {isMobile && <span style={{ marginLeft: "auto", fontFamily: "'Spectral SC',serif", fontSize: "11px", color: T.inkFaint, paddingLeft: 8 }}>{open ? "▴" : "▾"}</span>}

      </div>
      {!collapsed && (
        <div className="cards-row">
          {slots.map((item, idx) =>
            item
              ? <div key={item.id} className="card" style={{ position: "relative", isolation: "isolate", zIndex: 5 - idx }}>
                  <VouchRibbon badges={itemBadges?.[badgeKey(catKey, item.id)] || []} align={isOwn ? "left" : "right"} compact ownerName={badgeOwnerName} circlePhrase={badgeCirclePhrase || (isOwn ? "your" : "their")} />
                  {isOwn && <button onClick={e => { e.stopPropagation(); onRemove(catKey, idx, false); }} style={{ position: "absolute", top: 4, right: 4, zIndex: 52, background: "rgba(17,16,8,0.85)", border: "none", color: "#C8C2B4", width: 26, height: 26, cursor: "pointer", fontSize: 15, lineHeight: "26px", textAlign: "center", borderRadius: 2 }}>×</button>}
                  <div onClick={() => { if (!tileIsClickable(item, catKey)) { onCard(catKey, idx); return; } openTileLink(item, { catKey, onMusicOpen }); }} style={{ cursor: tileIsClickable(item, catKey) ? "pointer" : "default" }}>
                    <TileMedia
                      item={item}
                      catKey={catKey}
                      onOpen={() => openTileLink(item, { catKey, onMusicOpen })}
                      poster={item.poster}
                      title={item.title}
                      className="card-poster"
                      badgeSize="sm"
                    >
                      {item.poster ? <img src={item.poster} alt={item.title} className="card-poster" onError={e => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} /> : null}
                      <div className="card-poster-placeholder" style={{ display: item.poster ? "none" : "flex" }}>{item.title}</div>
                    </TileMedia>
                    <div style={{ flex: 1 }}>
                      <div className="card-title">{item.title}</div>
                      <div className="card-sub">{item.artist || item.author || item.year || item.sub || ""}</div>
                      {item.comment && <div className="card-comment" style={{ fontSize: item.comment.length > 80 ? "9px" : item.comment.length > 40 ? "10px" : "10.5px" }}>"{item.comment}"</div>}
                      {!isOwn && (
                        <div style={{ display: "flex", marginTop: 6, gap: 0 }}>
                          <button onClick={e => { e.stopPropagation(); onDudeSame(item); }} style={{ flex: 1, background: myReactions?.includes(String(item.id)) ? T.ink : "transparent", border: `1px solid ${T.paperDark}`, color: myReactions?.includes(String(item.id)) ? T.bg : T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>{myReactions?.includes(String(item.id)) ? "✓ Agreed" : "Agree"}</button>
                          {onAddToQueue && <button onClick={e => { e.stopPropagation(); onAddToQueue({ ...item, _cat: catKey }); }} style={{ flex: 1, background: queue?.find(q => String(q.id) === String(item.id)) ? T.ink : "transparent", border: `1px solid ${T.paperDark}`, borderLeft: "none", color: queue?.find(q => String(q.id) === String(item.id)) ? T.bg : T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>{queue?.find(q => String(q.id) === String(item.id)) ? "✓ Queue" : "+ Queue"}</button>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              : isOwn
                ? <div key={`e${idx}`} className="slot-empty-sm" onClick={() => items.length < 5 && onAdd(catKey)} style={{ cursor: items.length >= 5 ? 'not-allowed' : 'pointer', opacity: items.length >= 5 ? 0.4 : 1 }}>
                    <div className="slot-empty-inner"><span className="slot-empty-plus">+</span>Vouch</div>
                  </div>
                : null
          )}
        </div>
      )}
    </div>
  );
}

function MutualMentions({ reactions, myReactions, isOwn, boardOwnerName, buddies, onViewBuddy }) {
  const [showAll, setShowAll] = useState(false);
  if (!reactions.length && !myReactions.length) return null;
  const items = isOwn ? myReactions : reactions;
  if (!items.length) return null;
  const preview = showAll ? items : items.slice(0, 9);
  return (
    <div style={{ marginTop: 52, borderTop: `1px solid ${T.paperDark}`, paddingTop: 28 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 15, letterSpacing: "0.08em", color: T.inkMid }}>
            {isOwn ? "Agreed With" : `${(boardOwnerName || "").split(" ")[0]} Agrees With`}
          </div>
          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkFaint }}>
            {isOwn ? "Things you've agreed with on buddy boards" : `Things ${(boardOwnerName || "").split(" ")[0]} has agreed with`}
          </div>
        </div>
        {items.length > 9 && <button onClick={() => setShowAll(s => !s)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", background: "transparent", border: "none", color: T.inkLight, cursor: "pointer", padding: 0, flexShrink: 0 }}>{showAll ? "Show Less" : `See All (${items.length})`}</button>}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {preview.map((item, i) => {
          const clickable = tileIsClickable(item, item.category);
          const sourceBuddy = isOwn && buddies ? buddies.find(b => b.userId === item.item_owner_id) : null;
          return (
            <div key={(item.id || item.item_id) + i} style={{ width: 100, flexShrink: 0 }}>
              <div style={{ cursor: clickable ? "pointer" : "default" }} onClick={() => openTileLink(item, { catKey: item.category })}>
                <TileMedia
                  item={item}
                  catKey={item.category}
                  onOpen={() => openTileLink(item, { catKey: item.category })}
                  poster={item.poster}
                  title={item.title}
                  badgeSize="sm"
                  style={{ width: 100 }}
                >
                  {item.poster
                    ? <img src={item.poster} alt={item.title} style={{ width: 100, height: 138, objectFit: "contain", background: "#000", border: `1px solid ${T.paperDark}`, display: "block" }} onError={e => e.target.style.display = "none"} />
                    : <div style={{ width: 100, height: 138, background: T.paperDark, border: `1px solid ${T.paperDark}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontFamily: "'Spectral',serif", color: T.inkLight, textAlign: "center", padding: 6 }}>{item.title}</div>}
                </TileMedia>
                <div style={{ fontFamily: "'Spectral',serif", fontSize: 11, fontWeight: 600, lineHeight: 1.3, marginTop: 5 }}>{item.title}</div>
                <div style={{ fontFamily: "'Spectral SC',serif", fontSize: 8.5, color: T.inkFaint, marginTop: 1 }}>{item.subtitle || ""}</div>
              </div>
              {sourceBuddy && (
                <div onClick={() => onViewBuddy && onViewBuddy(sourceBuddy)} style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 9.5, color: T.inkLight, marginTop: 3, cursor: "pointer" }}>
                  from {sourceBuddy.displayName} →
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BuddyModal({ userId, onClose, onSendRequest, onGenerateLink, inviteLink, existingBuddyIds, onViewProfile }) {
  const [q, setQ]               = useState("");
  const [results, setResults]   = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [sent, setSent]         = useState([]);
  const timer                   = useRef(null);

  // Load all users as suggestions on mount
  useEffect(() => {
    // Load already-sent requests
    supabase.from("buddies").select("receiver_id").eq("requester_id", userId).eq("status", "pending")
      .then(({ data }) => { if (data) setSent(data.map(r => r.receiver_id)); });
    supabase.from("profiles").select("id, username, display_name, avatar_url")
      .neq("id", userId).not("id", "in", `(${existingBuddyIds.length > 0 ? existingBuddyIds.join(",") : userId})`).order("display_name").limit(50)
      .then(async ({ data }) => {
        if (!data) return setSuggested([]);
        // Fetch mutual buddy counts via RPC in one shot
        const candidateIds = data.map(u => u.id);
        const { data: mutualData } = await supabase.rpc("get_mutual_buddy_counts", {
          my_id: userId,
          candidate_ids: candidateIds,
        });
        const mutualMap = {};
        (mutualData || []).forEach(row => { mutualMap[row.user_id] = Number(row.mutual_count); });
        const withMutual = data.map(u => ({ ...u, mutual_count: mutualMap[u.id] || 0 }));
        // Sort: mutual buddies first, then alphabetical
        withMutual.sort((a, b) => (b.mutual_count - a.mutual_count) || (a.display_name || "").localeCompare(b.display_name || ""));
        setSuggested(withMutual);
      });
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      const { data } = await supabase.from("profiles").select("id, username, display_name, avatar_url").or(`username.ilike.%${q}%,display_name.ilike.%${q}%`).neq("id", userId).limit(8);
      setResults(data || []);
      setBusy(false);
    }, 300);
  }, [q, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const UserRow = ({ r }) => {
    const isAlready = existingBuddyIds.includes(r.id);
    const isSent    = sent.includes(r.id);
    const mutualCount = r.mutual_count || 0;
    return (
      <div key={r.id} className="result-item" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, cursor: "pointer" }} onClick={() => onViewProfile && onViewProfile(r)}>
          <Avatar name={r.display_name} size={52} avatarUrl={r.avatar_url} />
          <div>
            <div className="result-title">{r.display_name}</div>
            <div className="result-sub">@{r.username}</div>
            {mutualCount > 0 && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, marginTop: 2 }}>{mutualCount} mutual {mutualCount === 1 ? "buddy" : "buddies"}</div>}
          </div>
        </div>
        {isAlready
          ? <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: T.inkFaint }}>Buddies</span>
          : isSent
          ? <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: T.inkFaint }}>Sent</span>
          : <button className="btn btn-solid" style={{ padding: "4px 12px" }} onClick={e => { e.stopPropagation(); onSendRequest(r.id); setSent(s => [...s, r.id]); }}>Add</button>
        }
      </div>
    );
  };

  const displayList = q.trim() ? results : suggested;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">Add Buddy</div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9.5px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 8 }}>Invite via Link</div>
            <button className="btn btn-solid" style={{ width: "100%" }} onClick={onGenerateLink}>
              {inviteLink ? "Link Copied!" : "Copy Invite Link"}
            </button>
            {inviteLink && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, marginTop: 6, wordBreak: "break-all" }}>{inviteLink}</div>}
          </div>
          <div style={{ borderBottom: `1px solid ${T.paperDark}`, marginBottom: 16 }} />
          <input className="search-input" placeholder="Search name or username…" value={q} onChange={e => setQ(e.target.value)} />
          {!q.trim() && suggested.length > 0 && (
            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkFaint, margin: "12px 0 8px" }}>On Vouch</div>
          )}
          {busy && <div className="loading">Searching…</div>}
          {!busy && q.trim() && results.length === 0 && <div className="no-results">No users found.</div>}
          {!busy && displayList.map(r => <UserRow key={r.id} r={r} />)}
        </div>
      </div>
    </div>
  );
}

function LegalModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">How It Works</div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body"><HowItWorks /></div>
      </div>
    </div>
  );
}

function BoardEditorModal({ onClose, onPublish, existing, categories, themes, userId, canPublish, nextPublishDate }) {
  const DRAFT_KEY = "vouch-board-draft-v2";
  const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch(e) { return null; } };
  const savedDraft = !existing ? loadDraft() : null;
  const [name, setName]               = useState(savedDraft?.name ?? existing?.name ?? "");
  const [theme, setTheme]             = useState(savedDraft?.theme ?? existing?.theme ?? "");
  const [description, setDescription] = useState(savedDraft?.description ?? existing?.description ?? "");
  const [singleCat, setSingleCat]     = useState(savedDraft?.singleCat ?? existing?.single_category ?? "");
  const [items, setItems]             = useState(savedDraft?.items ?? existing?.vouch_board_items?.sort((a,b)=>a.position-b.position).map(i => ({ ...i, id: i.item_id, sub: i.subtitle, catKey: i.category })) ?? []);
  const [addingItem, setAddingItem]   = useState(false);
  const [q, setQ]                     = useState("");
  const [results, setResults]         = useState([]);
  const [busy, setBusy]               = useState(false);
  const timer                         = useRef(null);
  const TMDB_KEY                      = "24f3b03466f2f7db2d54a0f53607da4f";

  // Auto-save draft to localStorage whenever state changes
  useEffect(() => {
    if (existing) return;
    const draft = { name, theme, description, singleCat, items };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [name, theme, description, singleCat, items, existing]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const fetches = [];
        if (!singleCat || singleCat === "movies") fetches.push(fetch(`https://api.themoviedb.org/3/search/movie?api_key=${TMDB_KEY}&query=${encodeURIComponent(q)}`).then(r=>r.json()).then(d=>(d.results||[]).slice(0,3).map(r=>({ id:r.id, title:r.title, sub:r.release_date?.slice(0,4)||"", poster:r.poster_path?`https://image.tmdb.org/t/p/w500${r.poster_path}`:null, catKey:"movies", sourceUrl:justWatchSearchUrl(r.title, r.release_date?.slice(0,4)) }))));
        if (!singleCat || singleCat === "shows") fetches.push(fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB_KEY}&query=${encodeURIComponent(q)}`).then(r=>r.json()).then(d=>(d.results||[]).slice(0,2).map(r=>({ id:r.id, title:r.name, sub:r.first_air_date?.slice(0,4)||"", poster:r.poster_path?`https://image.tmdb.org/t/p/w500${r.poster_path}`:null, catKey:"shows", sourceUrl:justWatchSearchUrl(r.name, r.first_air_date?.slice(0,4)) }))));
        if (!singleCat || ["albums","songs","artists","podcasts"].includes(singleCat)) {
          const type = singleCat === "albums" ? "album" : singleCat === "songs" ? "track" : singleCat === "artists" ? "artist" : singleCat === "podcasts" ? "show" : "track,album,artist,show";
          fetches.push(fetch(`/api/spotify?q=${encodeURIComponent(q)}&type=${type}`).then(r=>r.json()).then(d=>{
            const res = [];
            if (!singleCat || singleCat==="songs") (d.tracks?.items||[]).slice(0,2).forEach(r=>res.push({ id:r.id, title:r.name, sub:r.artists?.[0]?.name||"", poster:r.album?.images?.[0]?.url||null, catKey:"songs", sourceUrl:`https://open.spotify.com/track/${r.id}` }));
            if (!singleCat || singleCat==="albums") (d.albums?.items||[]).slice(0,2).forEach(r=>res.push({ id:r.id, title:r.name, sub:r.artists?.[0]?.name||"", poster:r.images?.[0]?.url||null, catKey:"albums", sourceUrl:`https://open.spotify.com/album/${r.id}` }));
            if (!singleCat || singleCat==="artists") (d.artists?.items||[]).slice(0,2).forEach(r=>res.push({ id:r.id, title:r.name, sub:r.genres?.[0]||"", poster:r.images?.[0]?.url||null, catKey:"artists", sourceUrl:`https://open.spotify.com/artist/${r.id}` }));
            if (!singleCat || singleCat==="podcasts") (d.shows?.items||[]).slice(0,2).forEach(r=>res.push({ id:r.id, title:r.name, sub:r.publisher||"", poster:r.images?.[0]?.url||null, catKey:"podcasts", sourceUrl:`https://open.spotify.com/show/${r.id}` }));
            return res;
          }));
        }
        if (!singleCat || singleCat === "books") fetches.push(fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(q)}&limit=3`).then(r=>r.json()).then(d=>(d.docs||[]).slice(0,2).map(r=>{ const isbn = pickBookIsbn(r.isbn); return { id:r.key||r.title, title:r.title, sub:(r.author_name||[]).join(", "), poster:r.cover_i?`https://covers.openlibrary.org/b/id/${r.cover_i}-L.jpg`:null, catKey:"books", isbn, sourceUrl: null }; })));
        const all = (await Promise.all(fetches)).flat();
        setResults(all);
      } catch(e) { console.error(e); }
      setBusy(false);
    }, 400);
  }, [q, singleCat]);

  const addItem = async (item) => {
    if (items.length >= 5) return;
    if (items.find(i => String(i.id) === String(item.id))) return;
    let sourceUrl = item.sourceUrl;
    const cat = item.catKey || item.category;
    if (cat === "movies" || cat === "shows") {
      sourceUrl = await fetchJustWatchTitleUrl(item.title, item.sub, cat);
    } else if (cat === "books") {
      sourceUrl = await fetchBookStoreUrl(item.title, item.sub, item.isbn, item.sourceUrl);
    }
    setItems(prev => [...prev, { ...item, sourceUrl }]);
    setQ(""); setResults([]); setAddingItem(false);
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => setItems(prev => {
    const next = [...prev];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return prev;
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });

  const [publishing, setPublishing] = useState(false);
  const handlePublish = async () => {
    if (publishing) return;
    if (!theme) { alert("Pick a title for your Vouch."); return; }
    if (theme === "Other" && !name.trim()) { alert("Give your custom Vouch a name — like 'Summer of 2009' or 'Scorsese’s Best'"); return; }
    if (items.length === 0) { alert("Add at least one title to your Vouch."); return; }
    setPublishing(true);
    const finalName = theme === "Other" ? name : theme;
    localStorage.removeItem(DRAFT_KEY);
    const ok = await onPublish({ name: finalName, theme, description, singleCategory: singleCat, items, existingBoardId: existing?.id || null, existingPublishedAt: existing?.published_at || null });
    if (!ok) setPublishing(false);
  };

  const catLabel = (key) => categories.find(c => c.key === key)?.label || key;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: "88vh" }}>
        <div className="modal-head">
          <div className="modal-title">{existing?.id ? "Edit Your Vouch" : "Create Your Vouch"}</div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">

          {/* Category */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 6 }}>Theme</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {themes.map(t => (
                <button key={t} type="button" onClick={() => setTheme(t)} style={themePickerButtonStyle(t, theme)}>{t === "Other" ? "Other — Create Your Own" : t}</button>
              ))}
            </div>
            {theme === "Other" && (
              <div>
                <input className="search-input" style={{ marginTop: 10, marginBottom: 0 }} placeholder="e.g. Summer of 2009, Scorsese's Best…" value={name} onChange={e => setName(e.target.value.slice(0, 25))} maxLength={25} />
                <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: name.length >= 21 ? "#c0392b" : T.inkFaint, textAlign: "right", marginTop: 3 }}>{name.length}/25</div>
              </div>
            )}
          </div>

          {/* Current items + Add */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 8 }}>Tiles ({items.length}/5) — tap arrows to reorder</div>
            {items.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {items.map((item, i) => (
                  <div key={i} className="editor-tile-row">
                    <div style={{ width: 72, flexShrink: 0 }}>
                      {item.poster
                        ? <img src={item.poster} alt={item.title} style={{ width: 72, height: 96, objectFit: "cover", border: `1px solid ${T.paperDark}`, display: "block" }} onError={e => e.target.style.display="none"} />
                        : <div style={{ width: 72, height: 96, background: T.paperDark, border: `1px solid ${T.paperDark}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontFamily: "'Spectral',serif", color: T.inkLight, textAlign: "center", padding: 4 }}>{item.title}</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{item.title}</div>
                      <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.12em", color: T.inkLight, marginTop: 2 }}>{catLabel(item.catKey || item.category)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <button type="button" onClick={() => moveItem(i, -1)} disabled={i === 0} style={{ background: "transparent", border: `1px solid ${T.paperDark}`, color: T.ink, cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.3 : 1, width: 28, height: 28, fontFamily: "monospace", fontSize: 14 }}>↑</button>
                      <button type="button" onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} style={{ background: "transparent", border: `1px solid ${T.paperDark}`, color: T.ink, cursor: i === items.length - 1 ? "default" : "pointer", opacity: i === items.length - 1 ? 0.3 : 1, width: 28, height: 28, fontFamily: "monospace", fontSize: 14 }}>↓</button>
                      <button type="button" onClick={() => removeItem(i)} style={{ background: T.ink, border: "none", color: T.bg, cursor: "pointer", width: 28, height: 28, fontSize: 16 }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {items.length < 5 && (
              addingItem ? (
                <div>
                  {/* Category filter inside search */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
                    <button onClick={() => setSingleCat("")} style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.12em", padding: "3px 8px", border: `1px solid ${!singleCat ? "#c9a820" : T.paperDark}`, background: !singleCat ? T.ink : "transparent", color: !singleCat ? "#c9a820" : T.inkMid, cursor: "pointer" }}>All</button>
                    {categories.map(c => (
                      <button key={c.key} onClick={() => setSingleCat(c.key)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.12em", padding: "3px 8px", border: `1px solid ${singleCat === c.key ? "#c9a820" : T.paperDark}`, background: singleCat === c.key ? T.ink : "transparent", color: singleCat === c.key ? "#c9a820" : T.inkMid, cursor: "pointer" }}>{c.label}</button>
                    ))}
                  </div>
                  <input className="search-input" placeholder={singleCat ? `Search ${catLabel(singleCat)}…` : "Search to add a title…"} value={q} onChange={e => setQ(e.target.value)} autoFocus />
                  {busy && <div className="loading">Searching…</div>}
                  {results.map((r, i) => (
                    <div key={i} className="result-item" onClick={() => addItem(r)}>
                      {r.poster ? <img src={r.poster} alt={r.title} className="result-img" /> : <div className="result-img" />}
                      <div style={{ flex: 1 }}>
                        <div className="result-title">{r.title}</div>
                        <div className="result-sub">{r.sub}</div>
                      </div>
                      <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", color: T.inkFaint }}>{catLabel(r.catKey)}</div>
                    </div>
                  ))}
                  <button className="btn btn-ghost" style={{ marginTop: 8, width: "100%" }} onClick={() => { setAddingItem(false); setQ(""); setResults([]); }}>Cancel</button>
                </div>
              ) : (
                <div>
                  <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, marginBottom: 8 }}>First tile is your cover — it shows when sharing.</div>
                  <button onClick={() => setAddingItem(true)} style={{ width: "100%", padding: "12px", background: T.ink, border: `1px solid ${T.paperDark}`, color: T.bg, fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpa: "0.18em", cursor: "pointer" }}>+ Add Tile</button>
                </div>
              )
            )}
          </div>

          {/* Description — below titles */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 6 }}>One Line About This Vouch (optional)</div>
            <input className="search-input" style={{ marginBottom: 0 }} placeholder="e.g. These artists remind me of the summer…" value={description} onChange={e => setDescription(e.target.value)} maxLength={120} />
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            {existing
              ? <button onClick={handlePublish} disabled={items.length === 0 || publishing} style={publishVouchButtonStyle(theme, items.length > 0)}>{publishing ? "Saving…" : "Save Changes"}</button>
              : canPublish
                ? <button onClick={handlePublish} disabled={items.length === 0 || publishing} style={publishVouchButtonStyle(theme, items.length > 0)}>{publishing ? "Publishing…" : "Publish Vouch"}</button>
                : <div style={{ flex: 1, padding: "12px", background: "transparent", border: `2px solid ${T.paperDark}`, color: T.inkFaint, fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><span>🔒</span><span style={{ whiteSpace: "nowrap" }}>Next Vouch unlocks {nextPublishDate}</span></div>
            }
            {!existing && <button onClick={() => { onClose(); }} style={{ padding: "12px 16px", background: "transparent", border: `1px solid ${T.paperDark}`, color: T.inkMid, fontFamily: "'Spectral SC',serif",ontSize: "9px", letterSpacing: "0.18em", cursor: "pointer" }}>Save Draft</button>}
          </div>
          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, textAlign: "center" }}>{existing ? "Edits will update your current Vouch." : canPublish ? "You can publish a Vouch once a week." : `Your next Vouch unlocks ${nextPublishDate}.`}</div>
        </div>
      </div>
    </div>
  );
}

function CategoryPicker({ selected, all, onSave, isOnboarding }) {
  const [cats, setCats] = useState(selected || all.map(c => c.key));
  const toggle = (key) => setCats(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const moveUp = (i) => { if (i === 0) return; const next = [...cats]; [next[i-1], next[i]] = [next[i], next[i-1]]; setCats(next); };
  const moveDown = (i) => { if (i === cats.length - 1) return; const next = [...cats]; [next[i], next[i+1]] = [next[i+1], next[i]]; setCats(next); };
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>Your categories — tap arrows to reorder</div>
        {cats.map((key, i) => {
          const cat = all.find(c => c.key === key);
          if (!cat) return null;
          return (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${T.paperDark}` }}>
              <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 15, flex: 1 }}>{cat.label}</div>
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" onClick={() => moveUp(i)} disabled={i === 0} style={{ background: "transparent", border: `1px solid ${T.paperDark}`, color: T.ink, cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.3 : 1, width: 28, height: 28, fontFamily: "monospace", fontSize: 14 }}>↑</button>
                <button type="button" onClick={() => moveDown(i)} disabled={i === cats.length - 1} style={{ background: "transparent", border: `1px solid ${T.paperDark}`, color: T.ink, cursor: i === cats.length - 1 ? "default" : "pointer", opacity: i === cats.length - 1 ? 0.3 : 1, width: 28, height: 28, fontFamily: "monospace", fontSize: 14 }}>↓</button>
                <button type="button" onClick={() => toggle(key)} style={{ background: T.ink, border: "none", color: T.bg, cursor: "pointer", width: 28, height: 28, fontSize: 16 }}>×</button>
              </div>
            </div>
          );
        })}
      </div>
      {all.filter(c => !cats.includes(c.key)).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkFaint, marginBottom: 10 }}>Add more</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {all.filter(c => !cats.includes(c.key)).map(cat => (
              <button key={cat.key} onClick={() => toggle(cat.key)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.14em", padding: "6px 14px", border: `1px solid ${T.paperDark}`, background: "transparent", color: T.inkMid, cursor: "pointer" }}>+ {cat.label}</button>
            ))}
          </div>
        </div>
      )}
      <button className="btn btn-solid" style={{ width: "100%", padding: "12px" }} onClick={() => onSave(cats)}>
        {isOnboarding ? "Set Up My Shelf →" : "Save Changes"}
      </button>
      {isOnboarding && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, marginTop: 8, textAlign: "center" }}>You can always change this later in Settings.</div>}
    </div>
  );
}

function EditMetaForm({ board, themes, onSave, onClose }) {
  const [name, setName]               = useState(board.name || "");
  const [theme, setTheme]             = useState(board.theme || "");
  const [description, setDescription] = useState(board.description || "");
  const handleSave = () => {
    const finalName = theme === "Other" ? name : (theme || name);
    if (!finalName.trim()) { alert("Give your Vouch a name."); return; }
    onSave({ name: finalName, theme: theme || null, description: description || null });
  };
  return (
    <div className="modal-body">
      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkLight, marginBottom: 20 }}>Change the name, theme, or description. Titles cannot be changed after publishing.</div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 6 }}>Theme</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {themes.map(t => (
            <button key={t} type="button" onClick={() => setTheme(t)} style={themePickerButtonStyle(t, theme)}>{t === "Other" ? "Other — Create Your Own" : t}</button>
          ))}
        </div>
        {theme === "Other" && <div>
          <input className="search-input" style={{ marginTop: 10, marginBottom: 0 }} placeholder="e.g. Summer of 2009…" value={name} onChange={e => setName(e.target.value.slice(0, 25))} maxLength={25} />
          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: name.length >= 21 ? "#c0392b" : T.inkFaint, textAlign: "right", marginTop: 3 }}>{name.length}/25</div>
        </div>}
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 6 }}>One Line Description (optional)</div>
        <input className="search-input" style={{ marginBottom: 0 }} placeholder="e.g. These artists remind me of the summer…" value={description} onChange={e => setDescription(e.target.value)} maxLength={120} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-solid" style={{ flex: 1, padding: "12px" }} onClick={handleSave}>Save Changes</button>
        <button className="btn btn-ghost" style={{ flex: 1, padding: "12px" }} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function GroupVouchSlideshow({ items, isMobile, onAddToQueue, queue, onDudeSame, onMusicOpen }) {
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);
  const currentOffsetX = useRef(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isMobile) return;
    const handleStart = e => { touchStartX.current = e.touches[0].clientX; touchStartY.current = e.touches[0].clientY; currentOffsetX.current = 0; };
    const touchStartY = { current: null };
    const handleMove = e => {
      if (touchStartX.current === null) return;
      const dx = e.touches[0].clientX - touchStartX.current;
      const dy = e.touches[0].clientY - (touchStartY.current || 0);
      if (Math.abs(dx) > Math.abs(dy)) e.preventDefault();
      currentOffsetX.current = dx;
      const track = el.querySelector(".gv-track");
      if (track) track.style.transform = `translateX(${-(idx * el.offsetWidth) + dx}px)`;
    };
    const handleEnd = () => {
      const w = el.offsetWidth;
      const dx = currentOffsetX.current;
      let newIdx = idx;
      if (dx < -(w * 0.22) && idx < items.length - 1) newIdx = idx + 1;
      else if (dx > (w * 0.22) && idx > 0) newIdx = idx - 1;
      const track = el.querySelector(".gv-track");
      if (track) { track.style.transition = "transform 0.32s cubic-bezier(0.25,0.46,0.45,0.94)"; track.style.transform = `translateX(${-(newIdx * w)}px)`; setTimeout(() => { if (track) track.style.transition = ""; }, 350); }
      setIdx(newIdx); touchStartX.current = null; touchStartY.current = null;
    };
    el.addEventListener("touchstart", handleStart, { passive: true });
    el.addEventListener("touchmove", handleMove, { passive: false });
    el.addEventListener("touchend", handleEnd, { passive: true });
    return () => { el.removeEventListener("touchstart", handleStart); el.removeEventListener("touchmove", handleMove); el.removeEventListener("touchend", handleEnd); };
  }, [idx, items.length, isMobile]);

  const CardFaceNoButtons = ({ item }) => {
    const clickable = tileIsClickable(item, item.category);
    const clickItem = () => openTileLink({ ...item, sub: item.subtitle }, { catKey: item.category, onMusicOpen });
    return (
    <div style={{ position: "relative", isolation: "isolate" }}>
      <VouchRibbon badges={item.groupBadge || []} ownerName={item.firstVouchedBy} circlePhrase="your" />
      <TileMedia item={item} catKey={item.category} onOpen={clickItem} poster={item.poster} title={item.title} badgeSize="lg">
        {item.poster
          ? <img src={item.poster} alt={item.title} style={{ width: "100%", height: 340, objectFit: "contain", background: "#000", display: "block", border: "1px solid rgba(200,194,180,0.2)", cursor: clickable ? "pointer" : "default" }} onError={e => e.target.style.display = "none"} />
          : <div style={{ width: "100%", height: 340, background: "rgba(200,194,180,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Spectral',serif", fontSize: 14, color: "rgba(200,194,180,0.5)", padding: 12, textAlign: "center", cursor: clickable ? "pointer" : "default" }}>{item.title}</div>}
      </TileMedia>
      <div style={{ padding: "10px 4px 4px" }}>
        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.45)", marginBottom: 4 }}>{item.category}</div>
        <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: 18, lineHeight: 1.2, marginBottom: 4, color: "#C8C2B4" }}>{item.title}</div>
        <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: "rgba(200,194,180,0.7)" }}>{item.subtitle || ""}</div>
        <VouchedByLine name={item.firstVouchedBy} first dark />
      </div>
    </div>
    );
  };

  const CardFace = ({ item }) => {
    const isQueued = queue?.find(q => String(q.id) === String(item.item_id || item.id));
    const clickable = tileIsClickable(item, item.category);
    const clickItem = () => openTileLink({ ...item, sub: item.subtitle }, { catKey: item.category, onMusicOpen });
    return (
      <div style={{ position: "relative", isolation: "isolate" }}>
        <VouchRibbon badges={item.groupBadge || []} ownerName={item.firstVouchedBy} circlePhrase="your" />
        <TileMedia item={item} catKey={item.category} onOpen={clickItem} poster={item.poster} title={item.title} badgeSize="lg">
          {item.poster
            ? <img src={item.poster} alt={item.title} style={{ width: "100%", height: 340, objectFit: "contain", background: "#000", display: "block", border: "1px solid rgba(200,194,180,0.2)", cursor: clickable ? "pointer" : "default" }} onError={e => e.target.style.display = "none"} />
            : <div style={{ width: "100%", height: 340, background: "rgba(200,194,180,0.1)", border: "1px solid rgba(200,194,180,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Spectral',serif", fontSize: 14, color: "rgba(200,194,180,0.5)", padding: 12, textAlign: "center", cursor: clickable ? "pointer" : "default" }}>{item.title}</div>}
        </TileMedia>
        <div style={{ padding: "10px 4px 4px" }}>
          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.45)", marginBottom: 4 }}>{item.category}</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: 18, lineHeight: 1.2, marginBottom: 4, color: "#C8C2B4" }}>{item.title}</div>
          <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: "rgba(200,194,180,0.7)" }}>{item.subtitle || ""}</div>
          <VouchedByLine name={item.firstVouchedBy} first dark />
          <div style={{ display: "flex", marginTop: 10 }}>
            {onAddToQueue && <button onClick={e => { e.stopPropagation(); onAddToQueue(item); }} style={{ flex: 1, background: isQueued ? "rgba(200,194,180,0.25)" : "rgba(200,194,180,0.1)", border: "1px solid rgba(200,194,180,0.2)", color: isQueued ? "rgba(200,194,180,0.95)" : "rgba(200,194,180,0.6)", cursor: "pointer", fontSize: "8px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.1em", padding: "6px 4px", fontWeight: 700, transition: "all 0.15s" }}>{isQueued ? "✓ Queued" : "+ Queue"}</button>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="vouch-section" style={{ marginBottom: 40, border: "3px double #999", boxShadow: "0 0 0 1px #666" }}>
      <div className="vouch-section-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="vouch-section-label">Group Vouch</div>
          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.4)", marginTop: 3 }}>Buzzing in your circle this week</div>
        </div>
      </div>
      {isMobile ? (
        <div>
          <div ref={containerRef} style={{ overflow: "hidden", userSelect: "none" }}>
            <div className="gv-track" style={{ display: "flex", willChange: "transform" }}>
              {items.map((item, i) => (
                <div key={i} style={{ flex: "0 0 100%", width: "100%" }}>
                  <CardFaceNoButtons item={item} />
                </div>
              ))}
            </div>
          </div>
          {items.length > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14, marginBottom: 10 }}>
              {items.map((_, i) => (
                <div key={i} onClick={() => setIdx(i)} style={{ width: 6, height: 6, borderRadius: "50%", background: i === idx ? "#C8C2B4" : "rgba(200,194,180,0.25)", cursor: "pointer", transition: "background 0.2s" }} />
              ))}
            </div>
          )}
          {items[idx] && (() => {
            const item = items[idx];
            const isQueued = queue?.find(q => String(q.id) === String(item.item_id || item.id));
            return (
              <div style={{ display: "flex", marginTop: 8 }}>
                {onAddToQueue && <button onClick={() => onAddToQueue(item)} style={{ flex: 1, background: isQueued ? "rgba(200,194,180,0.25)" : "rgba(200,194,180,0.1)", border: "1px solid rgba(200,194,180,0.2)", color: isQueued ? "rgba(200,194,180,0.95)" : "rgba(200,194,180,0.6)", cursor: "pointer", fontSize: "8px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.1em", padding: "8px 4px", fontWeight: 700, transition: "all 0.15s" }}>{isQueued ? "✓ Queued" : "+ Queue"}</button>}
              </div>
            );
          })()}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, flexWrap: "nowrap" }}>
          {items.map((item, i) => (
            <div key={i} style={{ flex: 1, minWidth: 0, position: "relative", zIndex: items.length - i }}>
              <CardFace item={item} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BuddiesBin({ allBuddyBoards, buddies, onViewBuddy, onAddToQueue, queue, onDudeSame, myReactions, userId, onMusicOpen }) {
  const [modalCat, setModalCat] = useState(null);
  const [vouchMeta, setVouchMeta] = useState({});
  const isMobile = window.innerWidth <= 640;

  // Build per-category tile lists - stable, shuffled once
  const catItemsRef = useRef(null);
  const prevBoardsLen = useRef(0);
  if (!catItemsRef.current || prevBoardsLen.current !== allBuddyBoards.length) {
    prevBoardsLen.current = allBuddyBoards.length;
    const built = {};
    CATEGORIES.forEach(cat => { built[cat.key] = []; });
    const seen = new Set();
    allBuddyBoards.forEach(row => {
      if (!row.user_id || buddies.every(b => b.userId !== row.user_id)) return;
      const key = row.category + ":" + row.item_id;
      if (seen.has(key)) {
        const existing = built[row.category]?.find(i => i.item_id === row.item_id);
        if (existing) {
          const buddy = buddies.find(b => b.userId === row.user_id);
          if (buddy && !existing.owners.find(o => o.userId === buddy.userId)) existing.owners.push(buddy);
        }
        return;
      }
      seen.add(key);
      if (!built[row.category]) return;
      const buddy = buddies.find(b => b.userId === row.user_id);
      built[row.category].push({ item_id: row.item_id, title: row.title, poster: row.poster, subtitle: row.subtitle || "", source_url: row.source_url, user_id: row.user_id, owners: buddy ? [buddy] : [] });
    });
    Object.keys(built).forEach(key => {
      const arr = built[key];
      for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
    });
    catItemsRef.current = built;
  }
  const catItems = catItemsRef.current || {};

  useEffect(() => {
    const items = [];
    CATEGORIES.forEach(cat => {
      (catItems[cat.key] || []).forEach(item => {
        items.push({ category: cat.key, item_id: item.item_id, title: item.title });
      });
    });
    if (!items.length) return;
    const circleIds = [userId, ...buddies.map(b => b.userId)].filter(Boolean);
    enrichGroupVouchItems(circleIds, items).then(enriched => {
      const map = {};
      enriched.forEach(i => {
        map[badgeKey(i.category, i.item_id)] = {
          firstVouchedBy: i.firstVouchedBy,
          groupBadge: i.groupBadge || [],
        };
      });
      setVouchMeta(map);
    }).catch(() => {});
  }, [allBuddyBoards.length, userId, buddies.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleCats = CATEGORIES.filter(cat => catItems[cat.key]?.length > 0);
  if (visibleCats.length === 0) return null;

  const TileCard = ({ item, catKey }) => {
    const meta = vouchMeta[badgeKey(catKey, item.item_id)] || {};
    return (
    <div style={{ flexShrink: 0, width: isMobile ? 95 : 150, position: "relative", isolation: "isolate" }}>
      <VouchRibbon badges={meta.groupBadge || []} compact ownerName={meta.firstVouchedBy} circlePhrase="your" />
      <div style={{ cursor: tileIsClickable(item, catKey) ? "pointer" : "default" }} onClick={() => openTileLink(item, { catKey, onMusicOpen })}>
      <TileMedia
        item={item}
        catKey={catKey}
        onOpen={() => openTileLink(item, { catKey, onMusicOpen })}
        poster={item.poster}
        title={item.title}
        badgeSize="sm"
      >
        {item.poster
          ? <img src={item.poster} alt={item.title} style={{ width: "100%", display: "block" }} onError={e => e.target.style.display = "none"} />
          : <div style={{ width: "100%", aspectRatio: "2/3", background: T.paperDark, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontFamily: "'Spectral',serif", color: T.inkLight, textAlign: "center", padding: 6 }}>{item.title}</div>}
      </TileMedia>
      <div style={{ fontFamily: "'Spectral',serif", fontSize: 11, fontWeight: 600, color: T.ink, marginTop: 5, lineHeight: 1.3 }}>{item.title}</div>
      {meta.firstVouchedBy && <VouchedByLine name={meta.firstVouchedBy} first />}
      </div>
      {item.owners.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: onAddToQueue ? 6 : 0 }}>
            {item.owners.slice(0,3).map((o, i) => {
              const parts = (o.displayName || "").trim().split(" ");
              const shortName = parts[0] + (parts[1] ? " " + parts[1][0] + "." : "");
              return (
                <div key={i} onClick={e => { e.stopPropagation(); onViewBuddy(o); }} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <Avatar name={o.displayName} size={18} avatarUrl={o.avatarUrl} />
                  <span style={{ fontFamily: "'Spectral',serif", fontSize: 10, color: T.inkMid }}>{shortName}</span>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", marginTop: 4, gap: 0 }}>
            {(() => {
              const isAgreed = myReactions?.find(r => String(r.item_id) === String(item.item_id) && r.item_owner_id === item.user_id);
              return onDudeSame && item.user_id && item.user_id !== userId && <button onClick={e => { e.stopPropagation(); onDudeSame({ ...item, id: item.item_id, _cat: item.category }, item.user_id); }} style={{ flex: 1, background: isAgreed ? T.ink : "transparent", border: `1px solid ${T.paperDark}`, color: isAgreed ? T.bg : T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>{isAgreed ? "✓ Agreed" : "Agree"}</button>;
            })()}
            {onAddToQueue && <button onClick={e => { e.stopPropagation(); onAddToQueue(item); }} style={{ flex: 1, background: queue?.find(q => String(q.id) === String(item.item_id)) ? T.ink : "transparent", border: `1px solid ${T.paperDark}`, borderLeft: "none", color: queue?.find(q => String(q.id) === String(item.item_id)) ? T.bg : T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>{queue?.find(q => String(q.id) === String(item.item_id)) ? "✓ Queue" : "+ Queue"}</button>}
          </div>
        </div>
      )}
    </div>
    );
  };

  return (
    <div style={{ marginBottom: 48 }}>
      <div style={{ fontFamily: "'Times New Roman',Times,serif", fontWeight: 900, fontSize: 22, color: T.ink, letterSpacing: "0.04em", marginBottom: 4 }}>Group Shelf</div>
      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkLight, marginBottom: 28 }}>Everything your circle is vouching for</div>
      {visibleCats.map(cat => (
        <div key={cat.key} style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", borderBottom: "2px solid " + T.ink, paddingBottom: 8, marginBottom: 14 }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 15, letterSpacing: "0.08em" }}>{cat.label}</div>
            {catItems[cat.key].length > 5 && (
              <button onClick={() => setModalCat(cat.key)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", background: "transparent", border: "none", color: T.inkLight, cursor: "pointer", padding: 0 }}>See All →</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
            {catItems[cat.key].slice(0, 5).map((item, i) => <TileCard key={i} item={item} catKey={cat.key} />)}
          </div>
        </div>
      ))}

      {modalCat && (
        <div className="modal-overlay" onClick={() => setModalCat(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
            <div className="modal-head">
              <div className="modal-title">{CATEGORIES.find(c => c.key === modalCat)?.label} — Your Circle</div>
              <button className="modal-x" onClick={() => setModalCat(null)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: "auto", flex: 1 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                {catItems[modalCat].map((item, i) => <TileCard key={i} item={item} catKey={modalCat} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const BuddyFeed = memo(function BuddyFeed({ buddies, selfId, selfName, selfAvatar, onViewBuddy, onViewOwnBoard, onDudeSame, onAddToQueue, queue, myReactions, onShelfExtras, onMusicOpen, tileCommentProps, isBuddyWithUser, refreshKey = 0 }) {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedTab, setFeedTab] = useState('vouches');
  const [discoveryBoards, setDiscoveryBoards] = useState([]);
  const [discoveryFeed, setDiscoveryFeed] = useState([]);
  const [visibleCount, setVisibleCount] = useState(15);
  const [feedBadgeMap, setFeedBadgeMap] = useState({});
  const [feedTileComments, setFeedTileComments] = useState({});

  useEffect(() => {
    const vouchItems = feed.filter(x => x.type === "vouch").flatMap(x => x.board?.vouch_board_items || []);
    if (!vouchItems.length) {
      setFeedTileComments({});
      return;
    }
    loadTileCommentsMap(vouchItems).then(setFeedTileComments);
  }, [feed]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const buddyIds = [...buddies.map(b => b.userId), selfId].filter(Boolean);
        const excludeIds = new Set(buddyIds);
        const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
        const allPeople = [...buddies, { userId: selfId, displayName: selfName || "You", avatarUrl: selfAvatar || null }];
        const items = [];

        if (buddyIds.length > 0) {
          const { data: boards } = await supabase
            .from("vouch_boards")
            .select("*, vouch_board_items(*)")
            .in("user_id", buddyIds)
            .eq("is_active", true)
            .order("published_at", { ascending: false })
            .limit(100);
          const { data: reactions } = await supabase
            .from("reactions")
            .select("*")
            .in("user_id", buddyIds)
            .gte("created_at", ninetyDaysAgo)
            .order("created_at", { ascending: false })
            .limit(200);
          const { data: shelfAdds } = await supabase
            .from("endorsements")
            .select("*")
            .in("user_id", buddyIds)
            .gte("created_at", ninetyDaysAgo)
            .order("created_at", { ascending: false })
            .limit(200);
          const ownerIds = [...new Set((reactions || []).map(r => r.item_owner_id).filter(Boolean))];
          const ownerProfiles = {};
          if (ownerIds.length > 0) {
            const { data: owners } = await supabase.from("profiles").select("id, display_name, username, avatar_url").in("id", ownerIds);
            (owners || []).forEach(p => { ownerProfiles[p.id] = p; });
          }
          (reactions || []).forEach(r => { r.owner = ownerProfiles[r.item_owner_id] || null; });
          (boards || []).forEach(b => {
            if (!b.published_at) return;
            const buddy = allPeople.find(x => x.userId === b.user_id);
            items.push({ type: "vouch", date: new Date(b.published_at), board: b, buddy });
          });
          (reactions || []).forEach(r => {
            const buddy = allPeople.find(x => x.userId === r.user_id);
            items.push({ type: "agree", date: new Date(r.created_at), reaction: r, buddy });
          });
          const shelfGroups = {};
          (shelfAdds || []).forEach(s => {
            if (!s.created_at) return;
            const buddy = allPeople.find(x => x.userId === s.user_id);
            const date = new Date(s.created_at);
            const key = s.user_id + ":" + Math.floor(date.getTime() / (20 * 60 * 1000));
            if (!shelfGroups[key]) {
              shelfGroups[key] = { type: "shelf", date, shelves: [s], buddy };
              items.push(shelfGroups[key]);
            } else {
              shelfGroups[key].shelves.push(s);
              if (date > shelfGroups[key].date) shelfGroups[key].date = date;
            }
          });
          const { data: commentRows } = await supabase
            .from("vouch_tile_buddy_comments")
            .select("id, user_id, body, created_at, board_item_id")
            .gte("created_at", ninetyDaysAgo)
            .order("created_at", { ascending: false })
            .limit(200);
          const buddyComments = (commentRows || []).filter(c => buddyIds.includes(c.user_id));
          const commentFeedItems = await enrichCommentsForFeed(buddyComments, allPeople);
          commentFeedItems.forEach(c => items.push(c));
        }

        items.sort((a, b) => b.date - a.date);
        const grouped = [];
        const agreeGroups = {};
        items.forEach(item => {
          if (item.type === "agree") {
            const key = item.reaction.item_id + ":" + item.reaction.item_owner_id;
            if (!agreeGroups[key]) {
              agreeGroups[key] = { ...item, buddies: [item.buddy] };
              grouped.push(agreeGroups[key]);
            } else {
              agreeGroups[key].buddies.push(item.buddy);
              if (item.date > agreeGroups[key].date) agreeGroups[key].date = item.date;
            }
          } else {
            grouped.push(item);
          }
        });
        grouped.sort((a, b) => b.date - a.date);
        setFeed(grouped);

        // Discovery — activity from users outside your circle
        const { data: discoverBoardsRaw } = await supabase
          .from("vouch_boards")
          .select("*, vouch_board_items(*)")
          .eq("is_active", true)
          .order("published_at", { ascending: false })
          .limit(50);
        const discoverBoardsFiltered = (discoverBoardsRaw || [])
          .filter(b => b.published_at && !excludeIds.has(b.user_id))
          .slice(0, 12);

        const { data: discoverReactions } = await supabase
          .from("reactions")
          .select("*")
          .gte("created_at", ninetyDaysAgo)
          .order("created_at", { ascending: false })
          .limit(200);
        const { data: discoverShelf } = await supabase
          .from("endorsements")
          .select("*")
          .gte("created_at", ninetyDaysAgo)
          .order("created_at", { ascending: false })
          .limit(200);

        const reactionsFiltered = (discoverReactions || []).filter(r => !excludeIds.has(r.user_id));
        const shelfFiltered = (discoverShelf || []).filter(s => !excludeIds.has(s.user_id));
        const discoverProfileIds = [...new Set([
          ...discoverBoardsFiltered.map(b => b.user_id),
          ...reactionsFiltered.map(r => r.user_id),
          ...shelfFiltered.map(s => s.user_id),
          ...reactionsFiltered.map(r => r.item_owner_id).filter(Boolean),
        ])];
        const discoverProfileMap = {};
        if (discoverProfileIds.length > 0) {
          const { data: discoverProfiles } = await supabase.from("profiles").select("id, display_name, username, avatar_url").in("id", discoverProfileIds);
          (discoverProfiles || []).forEach(p => { discoverProfileMap[p.id] = p; });
        }
        discoverBoardsFiltered.forEach(b => { b.profiles = discoverProfileMap[b.user_id] || null; });

        const personFromProfile = (uid) => {
          const p = discoverProfileMap[uid];
          return p
            ? { userId: p.id, displayName: p.display_name, username: p.username, avatarUrl: p.avatar_url }
            : { userId: uid, displayName: "Someone", username: "", avatarUrl: null };
        };

        const discoverItems = [];
        reactionsFiltered.forEach(r => {
          r.owner = discoverProfileMap[r.item_owner_id] || null;
          discoverItems.push({ type: "agree", date: new Date(r.created_at), reaction: r, buddy: personFromProfile(r.user_id), isDiscovery: true });
        });
        const discoverShelfGroups = {};
        shelfFiltered.forEach(s => {
          if (!s.created_at) return;
          const buddy = personFromProfile(s.user_id);
          const date = new Date(s.created_at);
          const key = s.user_id + ":" + Math.floor(date.getTime() / (20 * 60 * 1000));
          if (!discoverShelfGroups[key]) {
            discoverShelfGroups[key] = { type: "shelf", date, shelves: [s], buddy, isDiscovery: true };
            discoverItems.push(discoverShelfGroups[key]);
          } else {
            discoverShelfGroups[key].shelves.push(s);
            if (date > discoverShelfGroups[key].date) discoverShelfGroups[key].date = date;
          }
        });
        discoverItems.sort((a, b) => b.date - a.date);
        const discoverGrouped = [];
        const discoverAgreeGroups = {};
        discoverItems.forEach(item => {
          if (item.type === "agree") {
            const key = item.reaction.item_id + ":" + item.reaction.item_owner_id;
            if (!discoverAgreeGroups[key]) {
              discoverAgreeGroups[key] = { ...item, buddies: [item.buddy], isDiscovery: true };
              discoverGrouped.push(discoverAgreeGroups[key]);
            } else {
              discoverAgreeGroups[key].buddies.push(item.buddy);
              if (item.date > discoverAgreeGroups[key].date) discoverAgreeGroups[key].date = item.date;
            }
          } else {
            discoverGrouped.push(item);
          }
        });
        discoverGrouped.sort((a, b) => b.date - a.date);

        setDiscoveryBoards(discoverBoardsFiltered);
        setDiscoveryFeed(discoverGrouped);

        const badgeUserIds = new Set();
        grouped.forEach(item => {
          if (item.type === "vouch") badgeUserIds.add(item.board.user_id);
          else if (item.type === "shelf" && item.buddy?.userId) badgeUserIds.add(item.buddy.userId);
          else if (item.type === "agree") {
            (item.buddies || [item.buddy]).forEach(b => { if (b?.userId) badgeUserIds.add(b.userId); });
            if (item.reaction?.item_owner_id) badgeUserIds.add(item.reaction.item_owner_id);
          }
        });
        discoverBoardsFiltered.forEach(b => badgeUserIds.add(b.user_id));
        reactionsFiltered.forEach(r => { badgeUserIds.add(r.user_id); if (r.item_owner_id) badgeUserIds.add(r.item_owner_id); });
        shelfFiltered.forEach(s => badgeUserIds.add(s.user_id));
        loadBadgesForUsers([...badgeUserIds]).then(setFeedBadgeMap).catch(() => {});
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [buddies, selfId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const profileBuddy = (profile, userId) => profile
    ? { userId: profile.id || userId, displayName: profile.display_name, username: profile.username, avatarUrl: profile.avatar_url }
    : { userId, displayName: "Someone", username: "", avatarUrl: null };

  const DiscoveryBadge = () => (
    <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.14em", color: T.ink, border: `1px solid ${T.ink}`, padding: "2px 6px", marginLeft: 8, whiteSpace: "nowrap" }}>From Vouch</span>
  );

  const renderFeedItem = (item, i, isDiscovery = false) => {
    if (item.type === "vouch") {
      const b = item.board;
      const buddy = item.buddy || profileBuddy(b.profiles, b.user_id);
      const theme = boardThemeName(b);
      const vouchItems = (b.vouch_board_items || []).sort((a,x) => a.position - x.position).slice(0,5);
      return (
        <div key={`${isDiscovery ? "d" : "b"}-vouch-${i}`} style={{ marginBottom: 32, opacity: isDiscovery ? 0.92 : 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div onClick={() => buddy && onViewBuddy(buddy)} style={{ cursor: "pointer", flexShrink: 0 }}>
              <Avatar name={buddy?.displayName || "?"} size={28} avatarUrl={buddy?.avatarUrl} />
            </div>
            <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: isDiscovery ? T.inkMid : "#3a3830", flex: 1 }}>
              <span onClick={() => buddy && onViewBuddy(buddy)} style={{ fontWeight: 600, cursor: "pointer" }}>{buddy?.displayName}</span>
              <span style={{ fontStyle: "italic", color: isDiscovery ? T.inkLight : "#7a7568" }}> published a Vouch</span>
              {isDiscovery && <DiscoveryBadge />}
              <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.1em", color: "#a09890", marginLeft: 8 }}>{item.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            </div>
          </div>
          <div className={getVouchSectionClassName(theme)} style={{ marginBottom: 32 }}>
            <div className="vouch-section-header">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="vouch-section-label" style={getVouchThemeLabelStyle(theme)}>{theme}</div>
                <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.4)", marginTop: 3 }}>Vouch</div>
                {b.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: "rgba(200,194,180,0.45)", marginTop: 4 }}>{b.description}</div>}
              </div>
            </div>
            {vouchItems.length > 0 && (() => {
              const vbBoard = { movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [] };
              vouchItems.forEach(it => {
                if (vbBoard[it.category]) vbBoard[it.category].push(vouchItemToDisplay(it));
              });
              const isSelfBoard = b.user_id === selfId;
              const mergedComments = { ...feedTileComments, ...(tileCommentProps?.tileCommentsByItemId || {}) };
              return <VouchSection board={vbBoard} isOwn={isSelfBoard} onCard={()=>{}} onAdd={()=>{}} onRemove={()=>{}} onDudeSame={onDudeSame || (()=>{})} myReactions={(myReactions || []).filter(r => r.item_owner_id === b.user_id).map(r => r.item_id)} hideHeader={true} hideEmptySlots={true} onAddToQueue={isSelfBoard ? null : (onAddToQueue || null)} queue={queue} ownerId={b.user_id} onMusicOpen={onMusicOpen} singleTile={true} itemBadges={itemBadgesForOwner(feedBadgeMap, b.user_id)} badgeOwnerName={buddy?.displayName} canCommentTiles={!isDiscovery && (isSelfBoard || !!isBuddyWithUser?.(b.user_id))} {...(tileCommentProps || {})} tileCommentsByItemId={mergedComments} />;
            })()}
          </div>
        </div>
      );
    }
    if (item.type === "shelf") {
      const shelves = item.shelves || [item.shelf];
      const primary = shelves[0];
      const extras = shelves.slice(1);
      const buddy = item.buddy;
      if (!primary) return null;
      return (
        <div key={`${isDiscovery ? "d" : "b"}-shelf-${i}`} style={{ borderBottom: "1px solid #b3ada0", paddingBottom: 16, marginBottom: 16, opacity: isDiscovery ? 0.92 : 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div onClick={() => buddy && onViewBuddy(buddy)} style={{ cursor: "pointer", flexShrink: 0 }}>
              <Avatar name={buddy?.displayName || "?"} size={28} avatarUrl={buddy?.avatarUrl} />
            </div>
            <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: isDiscovery ? T.inkMid : "#3a3830", flex: 1 }}>
              <span onClick={() => buddy && onViewBuddy(buddy)} style={{ fontWeight: 600, cursor: "pointer" }}>{buddy?.displayName}</span>
              <span style={{ fontStyle: "italic", color: isDiscovery ? T.inkLight : "#7a7568" }}> added </span>
              {extras.length > 0
                ? <span><strong style={{ fontStyle: "normal" }}>{primary.title}</strong><span style={{ fontStyle: "italic", color: "#7a7568" }}> and </span><span style={{ fontWeight: 600, color: "#7a7568", cursor: "pointer", borderBottom: "1px dashed #7a7568" }} onClick={e => { e.stopPropagation(); onShelfExtras && onShelfExtras(extras); }}>{extras.length} other tile{extras.length > 1 ? "s" : ""}</span><span style={{ fontStyle: "italic", color: "#7a7568" }}> to their shelf</span></span>
                : <span><strong style={{ fontStyle: "normal" }}>{primary.title}</strong><span style={{ fontStyle: "italic", color: "#7a7568" }}> to their shelf</span></span>
              }
              {isDiscovery && <DiscoveryBadge />}
              <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.1em", color: "#a09890", marginLeft: 8 }}>{item.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            </div>
          </div>
          <div style={{ width: "100%", maxWidth: 300, margin: "0 auto", position: "relative" }}>
            <VouchRibbon badges={badgesForOwner(feedBadgeMap, buddy?.userId, primary.category, primary.item_id)} ownerName={buddy?.displayName} circlePhrase="their" />
            <div style={{ cursor: tileIsClickable(primary, primary.category) ? "pointer" : "default" }} onClick={() => openTileLink(primary, { catKey: primary.category, onMusicOpen })}>
            <TileMedia
              item={primary}
              catKey={primary.category}
              onOpen={() => openTileLink(primary, { catKey: primary.category, onMusicOpen })}
              poster={primary.poster}
              title={primary.title}
              badgeSize="md"
            >
              {primary.poster && <img src={primary.poster} alt={primary.title} style={{ width: "100%", display: "block" }} onError={e => e.target.style.display = "none"} />}
            </TileMedia>
            <div style={{ fontFamily: "'Spectral',serif", fontSize: "14px", fontWeight: 600, color: "#111008", marginTop: 8, lineHeight: 1.3 }}>{primary.title}</div>
            {primary.subtitle && <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: "#a09890", marginTop: 2 }}>{primary.subtitle}</div>}
            </div>
            {onDudeSame && buddy && buddy.userId !== selfId && (
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <button onClick={e => { e.stopPropagation(); onDudeSame({ id: primary.item_id, title: primary.title, poster: primary.poster, _cat: primary.category }, buddy.userId); }} style={activityAgreePillStyle(!!(myReactions||[]).find(r => r.item_id === String(primary.item_id) && r.item_owner_id === buddy.userId), { flex: 1, fontSize: "8px", letterSpacing: "0.1em", padding: "6px 4px" })}>{(myReactions||[]).find(r => r.item_id === String(primary.item_id) && r.item_owner_id === buddy.userId) ? "✓ Agreed" : "Agree"}</button>
                {onAddToQueue && <button onClick={e => { e.stopPropagation(); onAddToQueue({ id: primary.item_id, title: primary.title, poster: primary.poster, source_url: primary.source_url, category: primary.category, user_id: buddy.userId }); }} style={activityQueuePillStyle(!!(queue||[]).find(q => String(q.id) === String(primary.item_id)), { flex: 1, fontSize: "8px", letterSpacing: "0.1em", padding: "6px 4px" })}>{(queue||[]).find(q => String(q.id) === String(primary.item_id)) ? "✓ Queued" : "+ Queue"}</button>}
              </div>
            )}
          </div>
        </div>
      );
    }
    if (item.type === "comment") {
      const { buddy, boardOwner, tile, vouchLabel, comment } = item;
      const ownerView = {
        userId: boardOwner.userId,
        displayName: boardOwner.displayName,
        username: boardOwner.username,
        avatarUrl: boardOwner.avatarUrl,
      };
      const isOwnVouch = boardOwner.userId === selfId;
      const goToVouch = () => (isOwnVouch ? onViewOwnBoard?.() : onViewBuddy(ownerView));
      return (
        <div key={`${isDiscovery ? "d" : "b"}-comment-${i}`} style={{ borderBottom: "1px solid #b3ada0", paddingBottom: 24, marginBottom: 24, opacity: isDiscovery ? 0.92 : 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div onClick={() => buddy && onViewBuddy(buddy)} style={{ cursor: "pointer", flexShrink: 0 }}>
              <Avatar name={buddy?.displayName || "?"} size={28} avatarUrl={buddy?.avatarUrl} />
            </div>
            <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: isDiscovery ? T.inkMid : "#3a3830", flex: 1 }}>
              <span onClick={() => buddy && onViewBuddy(buddy)} style={{ fontWeight: 600, cursor: "pointer" }}>{buddy?.displayName}</span>
              <span style={{ fontStyle: "italic", color: "#7a7568" }}> commented on </span>
              {isOwnVouch ? (
                <span style={{ fontWeight: 600, cursor: "pointer" }} onClick={goToVouch}>your Vouch</span>
              ) : (
                <span onClick={goToVouch} style={{ fontWeight: 600, cursor: "pointer" }}>{boardOwner.displayName}'s Vouch</span>
              )}
              {isDiscovery && <DiscoveryBadge />}
              <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.1em", color: "#a09890", marginLeft: 8 }}>{item.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            </div>
          </div>
          <div style={{ width: "100%", maxWidth: 300, margin: "0 auto" }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.14em", color: "#7a7568", marginBottom: 6 }}>{vouchLabel}</div>
            {tile.poster && (
              <div style={{ cursor: tileIsClickable(tile, tile.category) ? "pointer" : "default" }} onClick={() => openTileLink(tile, { catKey: tile.category, onMusicOpen })}>
                <img src={tile.poster} alt={tile.title} style={{ width: "100%", maxHeight: 220, objectFit: "contain", background: "#000", display: "block", border: `1px solid ${T.paperDark}` }} onError={e => e.target.style.display = "none"} />
              </div>
            )}
            <div style={{ fontFamily: "'Spectral',serif", fontSize: "14px", fontWeight: 600, color: "#111008", marginTop: 8, lineHeight: 1.3 }}>{tile.title}</div>
            {comment?.body && (
              <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: "#3a3830", marginTop: 8, lineHeight: 1.5 }}>"{comment.body}"</div>
            )}
            <button type="button" onClick={goToVouch} style={activityBtnStyle({ marginTop: 10, width: "100%", fontSize: "8px", letterSpacing: "0.12em", padding: "7px 10px" })}>View Vouch</button>
          </div>
        </div>
      );
    }
    if (item.type === "agree") {
      const r = item.reaction;
      const agreeBuddies = item.buddies || [item.buddy];
      const shown = agreeBuddies.slice(0, 2).filter(Boolean);
      const rest = agreeBuddies.slice(2).filter(Boolean);
      return (
        <div key={`${isDiscovery ? "d" : "b"}-agree-${i}`} style={{ borderBottom: "1px solid #b3ada0", paddingBottom: 24, marginBottom: 24, opacity: isDiscovery ? 0.92 : 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ display: "flex", gap: -6, flexShrink: 0 }}>
              {shown.map((b, j) => (
                <div key={j} onClick={() => b && onViewBuddy(b)} style={{ cursor: "pointer", marginLeft: j > 0 ? -8 : 0 }}>
                  <Avatar name={b?.displayName || "?"} size={28} avatarUrl={b?.avatarUrl} />
                </div>
              ))}
            </div>
            <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: isDiscovery ? T.inkMid : "#3a3830", flex: 1 }}>
              {shown.map((b, j) => (
                <span key={j}>
                  {j > 0 && <span style={{ fontStyle: "italic", color: "#7a7568" }}> and </span>}
                  <span onClick={() => b && onViewBuddy(b)} style={{ fontWeight: 600, cursor: "pointer" }}>{b?.displayName}</span>
                </span>
              ))}
              {rest.length > 0 && (
                <span>
                  <span style={{ fontStyle: "italic", color: "#7a7568" }}> and </span>
                  <span style={{ fontWeight: 600, color: "#7a7568" }}>{rest.map(b => b?.displayName).filter(Boolean).join(", ")}</span>
                </span>
              )}
              <span style={{ fontStyle: "italic", color: "#7a7568" }}> agreed with </span>
              {r.owner
                ? <span onClick={() => r.owner && onViewBuddy({ userId: r.owner.id, displayName: r.owner.display_name, username: r.owner.username, avatarUrl: r.owner.avatar_url })} style={{ fontWeight: 600, cursor: "pointer" }}>{r.owner.display_name}</span>
                : null}
              {isDiscovery && <DiscoveryBadge />}
              <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.1em", color: "#a09890", marginLeft: 8 }}>{item.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            </div>
          </div>
          {r.poster && (
            <div style={{ width: "100%", maxWidth: 300, margin: "0 auto", position: "relative" }}>
              <VouchRibbon badges={badgesForOwner(feedBadgeMap, r.item_owner_id, r.category, r.item_id)} ownerName={r.owner?.display_name} circlePhrase="their" />
              <div style={{ cursor: tileIsClickable(r, r.category) ? "pointer" : "default" }} onClick={() => openTileLink(r, { catKey: r.category, onMusicOpen })}>
                <TileMedia
                  item={r}
                  catKey={r.category}
                  onOpen={() => openTileLink(r, { catKey: r.category, onMusicOpen })}
                  poster={r.poster}
                  title={r.title}
                  badgeSize="md"
                >
                  <img src={r.poster} alt={r.title} style={{ width: "100%", display: "block" }} onError={e => e.target.style.display = "none"} />
                </TileMedia>
                <div style={{ fontFamily: "'Spectral',serif", fontSize: "14px", fontWeight: 600, color: "#111008", marginTop: 8, lineHeight: 1.3 }}>{r.title}</div>
                {r.subtitle && <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: "#a09890", marginTop: 2 }}>{r.subtitle}</div>}
              </div>
              {onDudeSame && r.item_owner_id && r.item_owner_id !== selfId && (
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button onClick={() => onDudeSame({ id: r.item_id, title: r.title, poster: r.poster, _cat: r.category }, r.item_owner_id)} style={activityAgreePillStyle(!!(myReactions||[]).find(x => x.item_id === r.item_id && x.item_owner_id === r.item_owner_id), { flex: 1, fontSize: "8px", letterSpacing: "0.1em", padding: "6px 4px" })}>
                    {(myReactions||[]).find(x => x.item_id === r.item_id && x.item_owner_id === r.item_owner_id) ? "✓ Agreed" : "Agree"}
                  </button>
                  {onAddToQueue && <button onClick={() => onAddToQueue({ id: r.item_id, title: r.title, poster: r.poster, source_url: r.source_url, category: r.category })} style={activityQueuePillStyle(!!(queue||[]).find(q => q.id === r.item_id), { flex: 1, fontSize: "8px", letterSpacing: "0.1em", padding: "6px 4px" })}>
                    {(queue||[]).find(q => q.id === r.item_id) ? "✓ Queued" : "+ Queue"}
                  </button>}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) return <div className="loading">Loading…</div>;

  const allFiltered = feed.filter(item => feedTab === 'vouches' ? item.type === 'vouch' : item.type !== 'vouch');
  const filteredFeed = allFiltered.slice(0, visibleCount);
  const totalFiltered = allFiltered.length;
  const showDiscovery = visibleCount >= totalFiltered;
  const discoveryActivity = discoveryFeed;
  const hasDiscoveryContent = feedTab === 'vouches' ? discoveryBoards.length > 0 : discoveryActivity.length > 0;

  return (
    <div>
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `2px solid ${T.ink}` }}>
        {[['vouches', 'Vouches'], ['activity', 'Activity']].map(([key, label]) => (
          <button key={key} onClick={() => { setFeedTab(key); setVisibleCount(15); }} style={{ fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.18em", padding: "10px 20px", background: feedTab === key ? T.ink : "transparent", color: feedTab === key ? T.bg : T.inkMid, border: "none", cursor: "pointer", fontWeight: feedTab === key ? 700 : 400 }}>{label}</button>
        ))}
      </div>
      {filteredFeed.length === 0 && !showDiscovery && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: "#7a7568", padding: "24px 0" }}>{feedTab === 'vouches' ? 'No vouches from your circle yet.' : 'No activity from your circle yet.'}</div>}
      {filteredFeed.map((item, i) => renderFeedItem(item, i, false))}
      {totalFiltered > visibleCount && (
        <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
          <button onClick={() => setVisibleCount(v => v + 15)} style={{ fontFamily: "'Spectral SC',serif", fontSize: '9px', letterSpacing: '0.18em', padding: '10px 28px', background: 'transparent', border: `1px solid ${T.paperDark}`, color: T.inkMid, cursor: 'pointer' }}>Load More</button>
        </div>
      )}
      {showDiscovery && hasDiscoveryContent && (
        <div style={{ marginTop: totalFiltered > 0 ? 40 : 8 }}>
          <div style={{ borderTop: totalFiltered > 0 ? `3px double ${T.ink}` : "none", paddingTop: totalFiltered > 0 ? 20 : 0, marginBottom: 20 }}>
            <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, lineHeight: 1.5 }}>
              See what others on Vouch are into
            </div>
          </div>
          {feedTab === 'vouches'
            ? discoveryBoards.map((b, i) => renderFeedItem({ type: "vouch", date: new Date(b.published_at), board: b, buddy: profileBuddy(b.profiles, b.user_id) }, i, true))
            : discoveryActivity.slice(0, 15).map((item, i) => renderFeedItem(item, i, true))
          }
        </div>
      )}
      {showDiscovery && !hasDiscoveryContent && totalFiltered === 0 && (
        <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: "#7a7568", padding: "24px 0" }}>
          {feedTab === 'vouches' ? 'No vouches to discover yet — check back soon.' : 'No activity to discover yet — check back soon.'}
        </div>
      )}
    </div>
  );
});


function ContactForm({ userId, userEmail, onSent }) {
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await fetch("https://formsubmit.co/ajax/ctanner.wallis@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          message: msg,
          _subject: "Vouch Feedback",
          userId: userId || "unknown",
          username: userEmail || "unknown",
        })
      });
      setSent(true);
      setMsg("");
      if (onSent) setTimeout(onSent, 1500);
    } catch(e) { console.error(e); }
    setBusy(false);
  };

  if (sent) return <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight }}>Thanks — we got it.</div>;

  return (
    <div>
      <textarea
        className="comment-area"
        style={{ height: 100, marginBottom: 10 }}
        placeholder="Your message…"
        value={msg}
        onChange={e => setMsg(e.target.value)}
        maxLength={1000}
      />
      <button className="btn btn-solid" onClick={send} disabled={busy || !msg.trim()} style={{ width: "100%" }}>
        {busy ? "Sending…" : "Send Message"}
      </button>
    </div>
  );
}

function StartPage({ onSignUp }) {
  const [featuredMovies, setFeaturedMovies] = useState([]);
  const [featuredAlbums, setFeaturedAlbums] = useState([]);
  const [showSignupNudge, setShowSignupNudge] = useState(false);

  const DUMMY_USERS = [
    { name: "Jordan Mills",   handle: "jordanmills",   avatar: "/avatars/vinyl.jpg",              vouches: 12 },
    { name: "Sam Rivera",     handle: "samrivera",     avatar: "/avatars/headphones.jpg",         vouches: 8  },
    { name: "Casey Park",     handle: "caseypark",     avatar: "/avatars/film-camera.jpg",        vouches: 21 },
    { name: "Morgan Lee",     handle: "morganlee",     avatar: "/avatars/book.jpg",               vouches: 6  },
    { name: "Taylor Knox",    handle: "taylorknox",    avatar: "/avatars/cassette.jpg",           vouches: 15 },
    { name: "Drew Holloway",  handle: "drewholloway",  avatar: "/avatars/guitar.jpg",             vouches: 9  },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const [movieRes, albumRes] = await Promise.all([
          fetch(`https://api.themoviedb.org/3/movie/popular?api_key=24f3b03466f2f7db2d54a0f53607da4f&language=en-US&page=1`).then(r => r.json()),
          fetch(`/api/spotify?q=best+albums+2024&type=album`).then(r => r.json()),
        ]);
        setFeaturedMovies((movieRes.results || []).slice(0, 5).map(r => ({
          id: r.id, title: r.title, sub: r.release_date?.slice(0,4) || "",
          poster: r.poster_path ? `https://image.tmdb.org/t/p/w500${r.poster_path}` : null,
        })));
        setFeaturedAlbums((albumRes.albums?.items || []).slice(0, 5).map(r => ({
          id: r.id, title: r.name, sub: r.artists?.[0]?.name || "",
          poster: r.images?.[0]?.url || null,
        })));
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const dummyVouchBoard = {
    movies: featuredMovies.slice(0, 3).map(m => ({ ...m, vouched: true, _cat: "movies", _catLabel: "Film" })),
    albums: featuredAlbums.slice(0, 2).map(a => ({ ...a, vouched: true, _cat: "albums", _catLabel: "Albums" })),
    artists: [], songs: [], books: [], shows: [], podcasts: [],
  };

  const nudge = () => setShowSignupNudge(true);

  return (
    <>
      <Styles />
      <div className="app">

        {/* Masthead */}
        <header className="masthead">
          <div className="masthead-meta">
            <span style={{ flex: 1 }}>Est. 2026</span>
            <span className="masthead-meta-stars">✦ · ✦ · ✦</span>
            <span style={{ flex: 1, display: "flex", justifyContent: "flex-end", fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: T.inkMid }}>vouch5.com</span>
          </div>
          <div className="masthead-nameplate"><img src="/vouch5-logo.png" alt="Vouch5" style={{height:"clamp(80px,16vw,160px)",display:"block",margin:"0 auto"}} /></div>
          <div className="masthead-rule-ornament"><span>—</span><span>✦</span><span>—</span></div>
          <div className="masthead-tagline">Love it? Vouch for it.</div>
        </header>

        <main className="page">

          {/* ── HERO ── */}
          <div style={{ textAlign: "center", padding: "40px 0 32px", borderBottom: `1px solid ${T.paperDark}`, marginBottom: 36 }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: "clamp(22px, 5vw, 36px)", lineHeight: 1.25, marginBottom: 12, maxWidth: 520, margin: "0 auto 12px" }}>
              Put your name behind what you love.
            </div>
            <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 15, color: T.inkMid, maxWidth: 420, margin: "0 auto 28px", lineHeight: 1.75 }}>
              Film, music, books, TV, podcasts — one board, updated weekly. See what your circle is into.
            </div>
            <button onClick={onSignUp} style={{ fontFamily: "'Spectral SC',serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.25em", padding: "14px 36px", background: T.ink, color: T.bg, border: "none", cursor: "pointer", display: "inline-block" }}>
              Continue with Google — It's Free
            </button>
            <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkFaint, marginTop: 10 }}>No algorithm. No ads. Just taste.</div>
          </div>

          {/* ── HOW IT WORKS ── */}
          <div style={{ marginBottom: 48 }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.22em", color: T.inkMid, textAlign: "center", marginBottom: 28 }}>How It Works</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 480, margin: "0 auto" }}>
              {[
                { label: "Your Vouch", desc: "Publish up to 5 tiles — a movie, album, book, show, artist, whatever you'd put your name behind right now. One Vouch at a time, updated once a week." },
                { label: "Your Shelf", desc: "Up to 5 picks per category across Film, Albums, Artists, Songs, Books, Television, and Podcasts. Change it whenever you like." },
                { label: "Buddies", desc: "Connect with friends and see what they're vouching for. Agree with anything that resonates, or add it to your Queue to revisit later." },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
                  <div style={{ fontFamily: "'Times New Roman',serif", fontWeight: 900, fontSize: 32, color: T.inkFaint, lineHeight: 1, flexShrink: 0, width: 24 }}>{i + 1}</div>
                  <div>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.ink, marginBottom: 5 }}>{item.label}</div>
                    <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkMid, lineHeight: 1.7 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="ornament"><span>—</span><span>✦</span><span>—</span></div>

          {/* ── HOW IT LOOKS ── */}
          <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.22em", color: T.inkMid, textAlign: "center", marginBottom: 28 }}>How It Looks</div>

          {/* Dummy profile */}
          <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/avatars/vinyl.jpg" alt="Jordan" style={{ width: 56, height: 56, objectFit: "cover", filter: "grayscale(100%)", border: `1px solid ${T.paperDark}` }} />
            <div>
              <div className="board-name" style={{ fontSize: 24, marginBottom: 2 }}>Jordan Mills</div>
              <div className="board-sub">@jordanmills</div>
            </div>
          </div>

          {/* Dummy vouch board */}
          {featuredMovies.length > 0 && (
            <div onClick={nudge} style={{ cursor: "pointer" }}>
              <div className="vouch-section" style={{ marginBottom: 32 }}>
                <div className="vouch-section-header">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="vouch-section-label">Feelin' Lately</div>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.4)", marginTop: 3 }}>Vouch</div>
                    <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: "rgba(200,194,180,0.45)", marginTop: 4 }}>What I'd put my name behind right now</div>
                  </div>
                  <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.14em", color: "rgba(200,194,180,0.35)", alignSelf: "flex-start", paddingTop: 4 }}>Sign in to agree →</div>
                </div>
                <VouchSection
                  board={dummyVouchBoard}
                  isOwn={false}
                  onCard={nudge}
                  onAdd={nudge}
                  onRemove={() => {}}
                  onDudeSame={nudge}
                  myReactions={[]}
                  hideHeader={true}
                />
              </div>
            </div>
          )}

          {/* Dummy Film shelf */}
          {featuredMovies.length > 0 && (
            <div className="cat-section">
              <div className="cat-header">
                <div className="cat-label">Film</div>
                <div className="cat-sublabel">My Shelf</div>
                <div className="cat-count">{Math.min(featuredMovies.length, 4)}</div>
              </div>
              <div className="cards-row">
                {featuredMovies.slice(0,4).map(item => (
                  <div key={item.id} className="card" onClick={nudge} style={{ cursor: "pointer" }}>
                    {item.poster
                      ? <img src={item.poster} alt={item.title} className="card-poster" onError={e => e.target.style.display="none"} />
                      : <div className="card-poster-placeholder">{item.title}</div>}
                    <div className="card-title">{item.title}</div>
                    <div className="card-sub">{item.sub}</div>
                    <div style={{ display: "flex", marginTop: 6 }}>
                      <button onClick={e => { e.stopPropagation(); nudge(); }} style={{ flex: 1, background: "transparent", border: `1px solid ${T.paperDark}`, color: T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>Agree</button>
                      <button onClick={e => { e.stopPropagation(); nudge(); }} style={{ flex: 1, background: "transparent", border: `1px solid ${T.paperDark}`, borderLeft: "none", color: T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>+ Queue</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dummy Albums shelf */}
          {featuredAlbums.length > 0 && (
            <div className="cat-section">
              <div className="cat-header">
                <div className="cat-label">Albums</div>
                <div className="cat-sublabel">My Shelf</div>
                <div className="cat-count">{Math.min(featuredAlbums.length, 4)}</div>
              </div>
              <div className="cards-row">
                {featuredAlbums.slice(0,4).map(item => (
                  <div key={item.id} className="card" onClick={nudge} style={{ cursor: "pointer" }}>
                    {item.poster
                      ? <img src={item.poster} alt={item.title} className="card-poster" onError={e => e.target.style.display="none"} />
                      : <div className="card-poster-placeholder">{item.title}</div>}
                    <div className="card-title">{item.title}</div>
                    <div className="card-sub">{item.sub}</div>
                    <div style={{ display: "flex", marginTop: 6 }}>
                      <button onClick={e => { e.stopPropagation(); nudge(); }} style={{ flex: 1, background: "transparent", border: `1px solid ${T.paperDark}`, color: T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>Agree</button>
                      <button onClick={e => { e.stopPropagation(); nudge(); }} style={{ flex: 1, background: "transparent", border: `1px solid ${T.paperDark}`, borderLeft: "none", color: T.inkMid, cursor: "pointer", fontSize: "7px", fontFamily: "'Spectral SC',serif", letterSpacing: "0.08em", padding: "3px 2px", fontWeight: 700 }}>+ Queue</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dummy buddy list */}
          <div style={{ marginTop: 40, marginBottom: 48 }}>
            <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.08em", borderBottom: `2px solid ${T.ink}`, paddingBottom: 10, marginBottom: 18 }}>Also on Vouch</div>
            {DUMMY_USERS.map((u, i) => (
              <div key={i} onClick={nudge} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: `1px solid ${T.paperDark}`, cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <img src={u.avatar} alt={u.name} style={{ width: 44, height: 44, objectFit: "cover", filter: "grayscale(100%)", border: `1px solid ${T.paperDark}`, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 15 }}>{u.name}</div>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: T.inkLight, letterSpacing: "0.1em" }}>@{u.handle} · {u.vouches} vouches</div>
                  </div>
                </div>
                <button onClick={e => { e.stopPropagation(); nudge(); }} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", padding: "5px 14px", background: "transparent", color: T.ink, border: `1px solid ${T.ink}`, cursor: "pointer" }}>+ Add</button>
              </div>
            ))}
            <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkFaint, marginTop: 12, textAlign: "center" }}>Sign in to see their boards →</div>
          </div>

          {/* Bottom CTA */}
          <div style={{ padding: "40px 0", borderTop: `3px double ${T.ink}`, textAlign: "center" }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(22px, 5vw, 32px)", fontWeight: 900, marginBottom: 10 }}>Make your own board.</div>
            <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkMid, marginBottom: 28, lineHeight: 1.7 }}>
              Free. No algorithm. No ads. Just taste.
            </div>
            <button onClick={onSignUp} style={{ fontFamily: "'Spectral SC',serif", fontSize: "11px", fontWeight: 600, letterSpacing: "0.25em", padding: "14px 36px", background: T.ink, color: T.bg, border: "none", cursor: "pointer" }}>
              Continue with Google — It's Free
            </button>
          </div>
        </main>

        <footer style={{ borderTop: `3px double ${T.ink}`, padding: "24px 28px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid }}>© {new Date().getFullYear()} Vouch. All Rights Reserved.</div>
        </footer>

        {/* Signup nudge modal */}
        {showSignupNudge && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(17,16,8,0.82)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setShowSignupNudge(false)}>
            <div style={{ background: T.bg, maxWidth: 420, width: "100%", border: `2px solid ${T.ink}`, padding: "32px 28px" }} onClick={e => e.stopPropagation()}>
              <div style={{ fontFamily: "'Times New Roman',Times,serif", fontWeight: 900, fontSize: 36, marginBottom: 4 }}><em style={{fontStyle:"italic"}}>V</em>ouch<sup style={{fontSize:"0.42em",verticalAlign:"super",lineHeight:0,fontWeight:900}}>5</sup></div>
              <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 24 }}>Love it? Vouch for it.</div>
              <div style={{ marginBottom: 28, borderBottom: `1px solid ${T.paperDark}`, paddingBottom: 28 }}>
                <HowItWorks />
              </div>
              <button onClick={onSignUp} style={{ width: "100%", fontFamily: "'Spectral SC',serif", fontSize: "10.5px", fontWeight: 600, letterSpacing: "0.25em", padding: 13, background: T.ink, color: T.bg, border: "none", cursor: "pointer" }}>
                Continue with Google →
              </button>
              <button onClick={() => setShowSignupNudge(false)} style={{ width: "100%", fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", padding: "10px", background: "transparent", color: T.inkFaint, border: "none", cursor: "pointer", marginTop: 8 }}>
                Maybe later
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}


export default function Vouch() {
  const [user,           setUser]           = useState(null);
  const [userId,         setUserId]         = useState(null);
  const [tab,            setTab]            = useState("board");
  const [viewing,        setViewing]        = useState(null);
  const [board,          setBoard]          = useState({ ...EMPTY_BOARD });
  const [viewBoard,      setViewBoard]      = useState({ ...EMPTY_BOARD });
  const [loading,        setLoading]        = useState(false);
  const [lightbox,       setLightbox]       = useState(null);
  const [addModal,       setAddModal]       = useState(null);
  const [vouchModal,     setVouchModal]     = useState(false);
  const [saving, setSaving] = useState(false); // eslint-disable-line no-unused-vars
  const [buddies,        setBuddies]        = useState([]);
  const [pendingIn,      setPendingIn]      = useState([]);
  const [buddyModal,     setBuddyModal]     = useState(false);
  const [inviteLink,     setInviteLink]     = useState(null);
  const [myReactions,    setMyReactions]    = useState([]);
  const [boardReactions, setBoardReactions] = useState([]);
  const [legalPage,      setLegalPage]      = useState(null);
  const [allBuddyBoards, setAllBuddyBoards] = useState([]);
  const [viewBuddies,    setViewBuddies]    = useState([]);
  const [showBuddyList,  setShowBuddyList]  = useState(false);
  const [buddySearch,    setBuddySearch]    = useState("");
  const [shareModal,     setShareModal]     = useState(false);
  const [shareCardUrl,   setShareCardUrl]   = useState(null);
  const [shareCardGenerating, setShareCardGenerating] = useState(false);
  const shareCardBlobRef = useRef(null);
  const [isAdmin,        setIsAdmin]        = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [avatarPicker,   setAvatarPicker]   = useState(false);
  const [avatarLightbox, setAvatarLightbox] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [musicPreference, setMusicPreference] = useState(null); // "spotify" | "apple_music" | null
  const musicPrefRef = useRef(null);
  const [musicPickerModal, setMusicPickerModal] = useState(null); // { url, title, sub, catKey }
  const [removeVouchModal, setRemoveVouchModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileUsername, setProfileUsername] = useState("");
  const [profileDisplayName, setProfileDisplayName] = useState("");
  const [profileSaveMsg, setProfileSaveMsg] = useState("");

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "instant" });
  const isMobileGlobal = typeof window !== "undefined" && window.innerWidth <= 640;
  const isIOSDevice = typeof navigator !== "undefined" && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
  const prefersPhotoSave = isIOSDevice || isMobileGlobal;


  // Removed stale profile cache — always fetch fresh vouch board on profile view

  useEffect(() => {
    const handlePop = (e) => {
      const path = window.location.pathname;
      if (path === "/" || path === "") {
        setViewing(null);
        const state = e.state;
        if (state?.tab) setTab(state.tab);
        else setTab("board");
        scrollToTop();
      } else if (path.startsWith("/@")) {
        const username = path.slice(2);
        supabase.from("profiles").select("id, display_name, avatar_url, username").eq("username", username).maybeSingle()
          .then(({ data }) => {
            if (data) {
              setViewing({ userId: data.id, username: data.username, displayName: data.display_name, avatarUrl: data.avatar_url });
              setTab("board");
              loadViewBoard(data.id);
              loadBoardReactions(data.id, true);
            }
          });
      }
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [sentRequests,   setSentRequests]   = useState([]);
  const [acceptedBuddies, setAcceptedBuddies] = useState([]);
  const [userCategories, setUserCategories] = useState(null);
  const [onboarding,     setOnboarding]     = useState(false);
  const [activeBoard,    setActiveBoard]    = useState(null);
  const [boardArchive,   setBoardArchive]   = useState([]);
  const [boardEditor,    setBoardEditor]    = useState(false);
  const [archivePage,    setArchivePage]    = useState(false);
  const [editingBoard,   setEditingBoard]   = useState(null);
  const [editingMeta,    setEditingMeta]    = useState(false);
  const [newAgreements,  setNewAgreements]  = useState([]);
  const [newCommentNotifs, setNewCommentNotifs] = useState([]);
  const [showFeaturesAnnounce, setShowFeaturesAnnounce] = useState(false);
  const [expandPreviousVouches, setExpandPreviousVouches] = useState(true);
  const [viewExpandPreviousVouches, setViewExpandPreviousVouches] = useState(true);
  const [showExpandVouchesPrompt, setShowExpandVouchesPrompt] = useState(false);
  const [newBuddies,     setNewBuddies]     = useState([]);
  const [showAgreements, setShowAgreements] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showShareNudge,   setShowShareNudge]   = useState(false);
  const [shelfExtras,     setShelfExtras]     = useState(null);
  const [pastNotifications, setPastNotifications] = useState([]);
  const [viewerReactions,setViewerReactions]= useState([]);
  const [viewActiveBoard,setViewActiveBoard]= useState(null);
  const [vouchTileComments, setVouchTileComments] = useState({});
  const [viewPublishCount, setViewPublishCount] = useState(0);
  const [ownItemBadges,    setOwnItemBadges]    = useState({});
  const [viewItemBadges,   setViewItemBadges]   = useState({});
  const [ownUserBadges,    setOwnUserBadges]    = useState([]);
  const [viewUserBadges,   setViewUserBadges]   = useState([]);
  const [suggested, setSuggested] = useState([]); // eslint-disable-line no-unused-vars
  const [queue,          setQueue]          = useState([]);
  const queueRef = useRef([]);
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);
  const [shelfView,      setShelfView]      = useState("shelf"); // "shelf" | "queue"
  useEffect(() => { queueRef.current = queue; }, [queue]);

  useEffect(() => {
    if (!userId) return;
    backfillVouchBadges()
      .then(() => loadBadgesForUser(userId))
      .then(map => {
        setOwnItemBadges(map);
        return loadUserBadges(userId, map);
      })
      .then(setOwnUserBadges)
      .catch(() => {});
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewing?.userId) {
      setViewUserBadges([]);
      return;
    }
    loadBadgesForUser(viewing.userId)
      .then(map => {
        setViewItemBadges(map);
        return loadUserBadges(viewing.userId, map);
      })
      .then(setViewUserBadges)
      .catch(() => {});
  }, [viewing?.userId, viewActiveBoard?.id]);

  useEffect(() => {
    if (!userId) return;
    loadUserBadges(userId, ownItemBadges).then(setOwnUserBadges).catch(() => {});
  }, [userId, ownItemBadges, boardArchive.length]);

  const loadMyReactions = async (uid) => {
    const { data } = await supabase.from("reactions").select("*").eq("user_id", uid).order("created_at", { ascending: false });
    setMyReactions(data || []);
  };

  const loadVouchBoards = async (uid) => {
    const { data } = await supabase
      .from("vouch_boards")
      .select("*, vouch_board_items(*)")
      .eq("user_id", uid)
      .order("created_at", { ascending: false });
    if (data) {
      const active = pickDisplayVouchBoard(data);
      if (active?.id) {
        const dupes = data.filter((b) => b.is_active && b.published_at && b.id !== active.id);
        if (dupes.length > 0) healDuplicateActiveBoards(supabase, uid, active.id).catch(() => {});
      }
      setActiveBoard(active);
      setBoardArchive(data);
    }
    loadBadgesForUser(uid).then(map => {
      setOwnItemBadges(map);
      loadUserBadges(uid, map).then(setOwnUserBadges).catch(() => {});
    }).catch(() => {});
  };

  const publishBoard = async (boardData) => {
    const { name, theme, description, singleCategory, items, existingBoardId } = boardData;
    let publishedBoardId = existingBoardId || null;

    const insertItems = async (boardId) => {
      if (!items.length) return true;
      const { error } = await supabase.from("vouch_board_items").insert(
        items.map((item, i) => ({
          board_id: boardId,
          item_id: String(item.id || item.item_id),
          title: item.title,
          subtitle: item.sub || item.subtitle || "",
          poster: item.poster || null,
          source_url: item.sourceUrl || item.source_url || null,
          category: item.catKey || item.category || "",
          position: i,
        }))
      );
      if (error) {
        console.error("vouch_board_items insert failed:", error);
        alert(`Could not save your vouch tiles${error.message ? `: ${error.message}` : "."}`);
        return false;
      }
      return true;
    };

    if (existingBoardId) {
      await supabase.from("vouch_boards").update({
        name, theme, description,
        single_category: singleCategory || null,
      }).eq("id", existingBoardId);
      await revokeBoardItemClaims(existingBoardId, userId, items).catch(() => {});
      await supabase.from("vouch_board_items").delete().eq("board_id", existingBoardId);
      if (!(await insertItems(existingBoardId))) {
        await loadVouchBoards(userId);
        return false;
      }
    } else {
      await supabase.from("vouch_boards").update({ is_active: false }).eq("user_id", userId).eq("is_active", true);
      const { data: newBoard, error: insertError } = await supabase.from("vouch_boards").insert({
        user_id: userId,
        name, theme, description,
        single_category: singleCategory || null,
        published_at: new Date().toISOString(),
        is_active: true,
      }).select().single();
      if (insertError || !newBoard) {
        console.error("publishBoard insert failed:", insertError);
        return false;
      }
      publishedBoardId = newBoard.id;
      if (!(await insertItems(newBoard.id))) {
        await loadVouchBoards(userId);
        return false;
      }
    }
    if (publishedBoardId) {
      await syncClaimsAfterPublish(userId, publishedBoardId, items, new Date().toISOString()).catch(() => {});
    }
    await loadVouchBoards(userId);
    setBoardEditor(false);
    setEditingBoard(null);
    setTimeout(() => setShareModal(true), 300);
    return true;
  };

  const removeActiveVouch = async (mode) => {
    if (!activeBoard) return;
    if (mode === "delete") {
      await revokeClaimsForBoard(activeBoard.id).catch(() => {});
      await supabase.from("vouch_board_items").delete().eq("board_id", activeBoard.id);
      await supabase.from("vouch_boards").delete().eq("id", activeBoard.id);
    } else {
      // archive — keep it, just deactivate and clear published_at so timer resets
      await supabase.from("vouch_boards").update({ is_active: false, published_at: null }).eq("id", activeBoard.id);
    }
    setActiveBoard(null);
    await loadVouchBoards(userId);
    setRemoveVouchModal(false);
  };

  const unpublishBoard = async () => { // eslint-disable-line no-unused-vars
    if (!activeBoard) return;
    if (!window.confirm("Unpublish your current Vouch? It will be removed from public view. You can republish or create a new one immediately.")) return;
    await supabase.from("vouch_boards").update({ is_active: false, published_at: null }).eq("id", activeBoard.id);
    await loadVouchBoards(userId);
  };

  const republishBoard = async (archivedBoard) => {
    // Deactivate current
    await supabase.from("vouch_boards").update({ is_active: false }).eq("user_id", userId).eq("is_active", true);
    // Reactivate archived board with new published_at
    await supabase.from("vouch_boards").update({ is_active: true, published_at: new Date().toISOString() }).eq("id", archivedBoard.id);
    await loadVouchBoards(userId);
  };

  const loadBoardReactions = async (ownerId, isViewing = false) => {
    const { data } = await supabase.from("reactions").select("*").eq("item_owner_id", ownerId).order("created_at", { ascending: false });
    setBoardReactions(data || []);
    if (isViewing) {
      const { data: theirAgreements } = await supabase.from("reactions").select("*").eq("user_id", ownerId).order("created_at", { ascending: false });
      setViewerReactions(theirAgreements || []);
    }
  };


  const loadBoard = async (uid, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    const { data, error } = await supabase
      .from("endorsements").select("*").eq("user_id", uid).order("created_at", { ascending: true });
    if (!error && data) {
      const b = { movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [] };
      data.forEach(row => {
        const cat = row.category;
        if (b[cat] && b[cat].length < 5) {
          b[cat].push({ id: row.item_id, title: row.title, sub: row.subtitle || "", poster: row.poster || null, comment: row.comment || "", vouched: row.vouched || false, sourceUrl: row.source_url || null, dbId: row.id });
        }
      });
      setBoard(b);
    }
    if (!silent) setLoading(false);
  };

  const loadViewBoard = async (uid) => {
    const { data, error } = await supabase
      .from("endorsements").select("*").eq("user_id", uid).order("created_at", { ascending: true });
    if (error) { console.error("loadViewBoard error:", error); return; }
    const b = { movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [] };
    (data || []).forEach(row => {
      const cat = row.category;
      if (b[cat] && b[cat].length < 5) {
        b[cat].push({ id: row.item_id, title: row.title, sub: row.subtitle || "", poster: row.poster || null, comment: row.comment || "", vouched: row.vouched || false, sourceUrl: row.source_url || null, dbId: row.id });
      }
    });
    setViewBoard(b);
    const { board: activeBoardData, hasDuplicateActive } = await fetchDisplayVouchBoard(supabase, uid);
    if (activeBoardData?.id && hasDuplicateActive) {
      healDuplicateActiveBoards(supabase, uid, activeBoardData.id).catch(() => {});
    }
    setViewActiveBoard(activeBoardData || null);
    const { count: publishCount } = await supabase
      .from("vouch_boards")
      .select("*", { count: "exact", head: true })
      .eq("user_id", uid)
      .not("published_at", "is", null);
    setViewPublishCount(publishCount || 0);
    // Always fetch fresh profile data including avatar
    const { data: profile } = await supabase.from("profiles").select("id, display_name, avatar_url, username, expand_previous_vouches").eq("id", uid).maybeSingle();
    if (profile) {
      setViewExpandPreviousVouches(profile.expand_previous_vouches !== false);
      setViewing(prev => ({ ...(prev || {}), userId: uid, avatarUrl: profile.avatar_url, displayName: profile.display_name, username: profile.username }));
      if (profile.username !== user?.username) {
        window.history.replaceState({}, "", `/@${profile.username}`);
      }
      scrollToTop();
    }
    // Also load this person's buddies
    const { data: buddyRows } = await supabase.from("buddies")
      .select("requester_id, receiver_id")
      .or(`requester_id.eq.${uid},receiver_id.eq.${uid}`)
      .eq("status", "accepted");
    if (buddyRows && buddyRows.length > 0) {
      const ids = buddyRows.map(b => b.requester_id === uid ? b.receiver_id : b.requester_id);
      const { data: profiles } = await supabase.from("profiles").select("id, display_name, avatar_url, username").in("id", ids);
      setViewBuddies(profiles || []);
    } else {
      setViewBuddies([]);
    }
    loadBadgesForUser(uid).then(setViewItemBadges).catch(() => {});
  };

  const [groupVouchItems, setGroupVouchItems] = useState([]);

  const loadGroupVouch = async (buddyIds, excludeId) => {
    if (!buddyIds.length) return;
    try {
      const buildCounts = (vbItems, shelfItems, agreeItems, profileMap) => {
        const counts = {};
        const addRow = (row) => {
          if (!row.item_id || !row.title) return;
          const key = (row.category || "") + ":" + row.item_id;
          if (!counts[key]) counts[key] = { item_id: row.item_id, title: row.title, category: row.category || "", poster: row.poster || null, source_url: row.source_url || null, count: 0, voucher_ids: new Set(), voucher_names: [], primary_user: null };
          const uid = row.user_id || row.item_owner_id;
          if (uid && !counts[key].voucher_ids.has(uid)) {
            counts[key].voucher_ids.add(uid);
            if (profileMap[uid]) counts[key].voucher_names.push(profileMap[uid]);
            if (!counts[key].primary_user) counts[key].primary_user = uid;
          }
          counts[key].count++;
        };
        (vbItems || []).forEach(r => addRow(r));
        (shelfItems || []).forEach(r => addRow(r));
        (agreeItems || []).forEach(r => addRow({ ...r, user_id: r.user_id }));
        return Object.values(counts).filter(i => i.category);
      };

      const pick5WithCap = (items, minCount = 1) => {
        // Filter by minimum unique buddy count, sort descending
        const sorted = items.filter(i => i.count >= minCount).sort((a, b) => b.count - a.count);
        // Cap each buddy at 2 tiles
        const userCounts = {};
        const selected = [];
        for (const item of sorted) {
          if (selected.length >= 8) break;
          const uid = item.primary_user;
          if (uid) {
            userCounts[uid] = (userCounts[uid] || 0);
            if (userCounts[uid] >= 2) continue;
            userCounts[uid]++;
          }
          selected.push(item);
        }
        return selected.sort(() => Math.random() - 0.5).slice(0, 5);
      };

      // Load buddy profiles
      const { data: buddyProfiles } = await supabase
        .from("profiles").select("id, display_name").in("id", buddyIds);
      const profileMap = {};
      (buddyProfiles || []).forEach(p => { profileMap[p.id] = p.display_name; });

      // Active vouch board items
      const { data: buddyBoards } = await supabase
        .from("vouch_boards").select("id, user_id").in("user_id", buddyIds).eq("is_active", true);
      const boardMap = {};
      (buddyBoards || []).forEach(b => { boardMap[b.id] = b.user_id; });
      const boardIds = Object.keys(boardMap);
      const { data: vbItemsRaw } = boardIds.length > 0 ? await supabase
        .from("vouch_board_items").select("item_id, title, category, poster, source_url, board_id").in("board_id", boardIds) : { data: [] };
      const vbItems = (vbItemsRaw || []).map(r => ({ ...r, user_id: boardMap[r.board_id] || null }));

      // Try past 7 days first
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: shelfWeek } = await supabase.from("endorsements").select("item_id, title, category, poster, source_url, user_id").in("user_id", buddyIds).gte("created_at", weekAgo);
      const { data: agreeWeek } = await supabase.from("reactions").select("item_id, title, category, poster, source_url, item_owner_id, user_id").in("user_id", buddyIds).gte("created_at", weekAgo);

      let items = buildCounts(vbItems, shelfWeek, agreeWeek, profileMap);
      // Try min 2 unique buddies first (avoids one-off picks)
      let result = pick5WithCap(items, 2);
      // Fall back to min 1 within weekly window
      if (result.length < 5) result = pick5WithCap(items, 1);
      // Fall back to all time with min 2
      if (result.length < 5) {
        const { data: shelfAll } = await supabase.from("endorsements").select("item_id, title, category, poster, source_url, user_id").in("user_id", buddyIds);
        const { data: agreeAll } = await supabase.from("reactions").select("item_id, title, category, poster, source_url, item_owner_id, user_id").in("user_id", buddyIds);
        items = buildCounts(vbItems, shelfAll, agreeAll, profileMap);
        result = pick5WithCap(items, 2);
        if (result.length < 5) result = pick5WithCap(items, 1);
      }

      const circleIds = [...buddyIds, excludeId].filter(Boolean);
      const enriched = await enrichGroupVouchItems(circleIds, result);
      setGroupVouchItems(enriched);
    } catch(e) { console.error("loadGroupVouch error:", e); }
  };

    const loadAllBuddyBoards = async (buddyList, uid) => {
    const allBoards = await Promise.all(buddyList.map(async b => {
      const { data } = await supabase.from("endorsements").select("*").eq("user_id", b.userId);
      return data || [];
    }));
    // Also include own board
    const ownId = uid || userId;
    const { data: ownData } = await supabase.from("endorsements").select("*").eq("user_id", ownId);
    const allRows = [...(ownData || []), ...allBoards.flat()];
    setAllBuddyBoards(allRows);
  };

  const loadBuddies = async (uid) => {
    const { data } = await supabase
      .from("buddies")
      .select("*, requester:requester_id(id, username, display_name, avatar_url), receiver:receiver_id(id, username, display_name, avatar_url)")
      .or(`requester_id.eq.${uid},receiver_id.eq.${uid}`);
    if (data) {
      const accepted = data.filter(b => b.status === "accepted").map(b => {
        const other = b.requester_id === uid ? b.receiver : b.requester;
        return { buddyRowId: b.id, userId: other.id, username: other.username, displayName: other.display_name, avatarUrl: other.avatar_url || null };
      });
      const incoming = data.filter(b => b.status === "pending" && b.receiver_id === uid).map(b => ({
        buddyRowId: b.id, userId: b.requester.id, username: b.requester.username, displayName: b.requester.display_name, avatarUrl: b.requester.avatar_url || null
      }));
      const sentOut = data.filter(b => b.status === "pending" && b.requester_id === uid).map(b => b.receiver_id);
      setSentRequests(sentOut);
      setBuddies(accepted);
      setPendingIn(incoming);
      if (accepted.length > 0) {
        loadAllBuddyBoards(accepted, uid);
        loadGroupVouch(accepted.map(b => b.userId), uid);
      }
      // Load suggested users (not already buddies or pending)
      const allIds = [...accepted.map(b => b.userId), ...incoming.map(b => b.userId), uid];
      const { data: suggestedData } = await supabase.from("profiles")
        .select("id, username, display_name, avatar_url")
        .not("id", "in", `(${allIds.join(",")})`)
        .order("display_name").limit(20);
      setSuggested(suggestedData || []);
    }
  };

  useEffect(() => {
    const setUserFromSession = async (session) => {
      if (session?.user) {
        const uid = session.user.id;
        const googleAvatar = session.user.user_metadata?.avatar_url || null;
        // Check if profile already exists with a custom avatar
        const { data: existingProfile } = await supabase.from("profiles").select("avatar_url, username, display_name").eq("id", uid).maybeSingle();
        // Prefer stored avatar unless it is a Google URL (googleusercontent = Google photo)
        const storedAvatar = existingProfile?.avatar_url;
        // Always use stored avatar if it exists - never override with Google
        const avatarUrl = storedAvatar || googleAvatar;
        if (!existingProfile) {
          await supabase.from("profiles").upsert({
            id: uid,
            username: session.user.email.split("@")[0],
            display_name: session.user.user_metadata?.full_name || session.user.email.split("@")[0],
            avatar_url: googleAvatar,
          }, { onConflict: "id" });
          // Auto-buddy with Christian (founder) on signup
          if (uid !== "bd7a4b83-c56c-438a-8ad0-d188f810fe70") {
            const { data: existing } = await supabase.from("buddies")
              .select("id")
              .or(`and(requester_id.eq.${uid},receiver_id.eq.bd7a4b83-c56c-438a-8ad0-d188f810fe70),and(requester_id.eq.bd7a4b83-c56c-438a-8ad0-d188f810fe70,receiver_id.eq.${uid})`)
              .maybeSingle();
            if (!existing) {
              await supabase.from("buddies").insert({ requester_id: "bd7a4b83-c56c-438a-8ad0-d188f810fe70", receiver_id: uid, status: "accepted" });
              await supabase.from("buddies").insert({ requester_id: uid, receiver_id: "bd7a4b83-c56c-438a-8ad0-d188f810fe70", status: "accepted" }).catch(() => {});
              // Auto-buddy with Vouch account
              if (uid !== "eeaec7b6-6fd7-4c8b-be16-376f0ebdc155") {
                await supabase.from("buddies").insert({ requester_id: "eeaec7b6-6fd7-4c8b-be16-376f0ebdc155", receiver_id: uid, status: "accepted" }).catch(() => {});
                await supabase.from("buddies").insert({ requester_id: uid, receiver_id: "eeaec7b6-6fd7-4c8b-be16-376f0ebdc155", status: "accepted" }).catch(() => {});
              }
              // Store flag so we can show the welcome notification on first login
              localStorage.setItem("vouch-new-buddy-christian-" + uid, "1");
            }
          }
        } else if (existingProfile && !storedAvatar && googleAvatar) {
          // Only set Google avatar if user has absolutely no avatar stored
          // Double-check DB hasn't been updated since we fetched existingProfile
          const { data: freshProfile } = await supabase.from("profiles").select("avatar_url").eq("id", uid).maybeSingle();
          if (!freshProfile?.avatar_url) {
            await supabase.from("profiles").update({ avatar_url: googleAvatar }).eq("id", uid);
          }
        }
        // Always ensure auto-buddy with Christian and Vouch account
        if (uid !== "bd7a4b83-c56c-438a-8ad0-d188f810fe70") {
          let isNewBuddy = false;
          try {
            const res = await supabase.from("buddies").insert({ requester_id: "bd7a4b83-c56c-438a-8ad0-d188f810fe70", receiver_id: uid, status: "accepted" });
            if (!res.error) isNewBuddy = true;
          } catch(e) {}
          try {
            await supabase.from("buddies").insert({ requester_id: uid, receiver_id: "bd7a4b83-c56c-438a-8ad0-d188f810fe70", status: "accepted" });
          } catch(e) {}
          // Ensure Vouch account buddy
          if (uid !== "eeaec7b6-6fd7-4c8b-be16-376f0ebdc155") {
            try { await supabase.from("buddies").insert({ requester_id: "eeaec7b6-6fd7-4c8b-be16-376f0ebdc155", receiver_id: uid, status: "accepted" }); } catch(e) {}
            try { await supabase.from("buddies").insert({ requester_id: uid, receiver_id: "eeaec7b6-6fd7-4c8b-be16-376f0ebdc155", status: "accepted" }); } catch(e) {}
          }
          // Show welcome notification on buddies tab for new user
          if (isNewBuddy) setNewBuddies(["Christian Wallis"]);
        }
        // Never overwrite a custom (non-Google) avatar on login
        // Re-fetch fresh profile to avoid stale data setting wrong username/avatar
        const { data: freshForState } = await supabase.from("profiles").select("username, display_name, avatar_url, is_admin").eq("id", uid).maybeSingle();
        if (freshForState?.is_admin) setIsAdmin(true);
        const finalUsername = freshForState?.username || existingProfile?.username || session.user.email.split("@")[0];
        const finalDisplayName = freshForState?.display_name || existingProfile?.display_name || session.user.user_metadata?.full_name || session.user.email.split("@")[0];
        const finalAvatar = freshForState?.avatar_url || avatarUrl;
        setUser({ username: finalUsername, displayName: finalDisplayName, avatarUrl: finalAvatar });
        setUserId(uid);
        setTab("home");
        loadBoard(uid);
        loadBuddies(uid);
        loadMyReactions(uid);
        loadVouchBoards(uid);
        backfillVouchBadges().then(() => loadBadgesForUser(uid).then(setOwnItemBadges)).catch(() => {});
        // Check for new agreements since last visit
        const { data: visitProf } = await supabase.from("profiles").select("last_visit").eq("id", uid).maybeSingle();
        const lastVisit = visitProf?.last_visit || localStorage.getItem("vouch-last-visit") || new Date(0).toISOString();
        const { data: newAgrees } = await supabase.from("reactions")
          .select("user_id, title")
          .eq("item_owner_id", uid)
          .gt("created_at", lastVisit)
          .order("created_at", { ascending: false })
          .limit(5);
        // Check for newly accepted buddy requests since last visit
        const { data: newAccepted } = await supabase.from("buddies")
          .select("requester_id, receiver_id, profiles!buddies_receiver_id_fkey(display_name), requester:profiles!buddies_requester_id_fkey(display_name)")
          .or(`requester_id.eq.${uid},receiver_id.eq.${uid}`)
          .eq("status", "accepted")
          .gt("updated_at", lastVisit);
        if (newAccepted && newAccepted.length > 0) {
          const buddyNames = newAccepted.map(b => {
            const otherName = b.requester_id === uid ? b.profiles?.display_name : b.requester?.display_name;
            return otherName;
          }).filter(Boolean);
          setNewBuddies(buddyNames);
        }
        if (newAgrees && newAgrees.length > 0) {
          const uids = [...new Set(newAgrees.map(r => r.user_id))];
          const { data: profs } = await supabase.from("profiles").select("id, display_name").in("id", uids);
          newAgrees.forEach(r => { r.display_name = profs?.find(p => p.id === r.user_id)?.display_name || "Someone"; });
          setNewAgreements(newAgrees);
        }
        const commentNotifs = await loadNewCommentNotifications(uid, lastVisit);
        if (commentNotifs.length > 0) setNewCommentNotifs(commentNotifs);
        // Check for new buddy requests since last visit
        const { data: newBuddyReqs } = await supabase.from("buddies")
          .select("requester_id, profiles!buddies_requester_id_fkey(display_name)")
          .eq("receiver_id", uid)
          .eq("status", "pending")
          .gt("created_at", lastVisit);
        if (newBuddyReqs && newBuddyReqs.length > 0) {
          // pendingIn state will handle display - just ensure badge updates
          console.log("New buddy requests:", newBuddyReqs.length);
        }
        const nowVisit = new Date().toISOString();
        localStorage.setItem("vouch-last-visit", nowVisit);
        await supabase.from("profiles").update({ last_visit: nowVisit }).eq("id", uid);
        // Load category preferences
        const { data: prof } = await supabase.from("profiles").select("categories, music_preference, queue_items, expand_previous_vouches").eq("id", uid).maybeSingle();
        if (prof?.music_preference) { setMusicPreference(prof.music_preference); musicPrefRef.current = prof.music_preference; }
        setExpandPreviousVouches(prof?.expand_previous_vouches !== false);
        if (!localStorage.getItem(FEATURES_ANNOUNCE_KEY)) {
          setShowFeaturesAnnounce(true);
        } else if (!localStorage.getItem("vouch-expand-vouches-prompted")) {
          setShowExpandVouchesPrompt(true);
        }
        if (prof?.categories && prof.categories.length > 0) {
          setUserCategories(prof.categories);
        } else if (prof && !prof.categories) {
          setOnboarding(true);
          setUserCategories(CATEGORIES.map(c => c.key));
        } else {
          setUserCategories(CATEGORIES.map(c => c.key));
        }
        loadVouchBoards(uid);
        // Load queue from localStorage
        try {
          // Try DB first, fall back to localStorage
          const { data: qProf } = await supabase.from("profiles").select("queue_items").eq("id", uid).maybeSingle();
          if (qProf?.queue_items) {
            const dbQueue = JSON.parse(qProf.queue_items);
            setQueue(dbQueue);
            localStorage.setItem("vouch-queue-" + uid, JSON.stringify(dbQueue));
          } else {
            const saved = JSON.parse(localStorage.getItem("vouch-queue-" + uid) || "[]");
            setQueue(saved);
            if (saved.length > 0) {
              supabase.from("profiles").update({ queue_items: JSON.stringify(saved) }).eq("id", uid).catch(() => {});
            }
          }
        } catch(e) {}
        try {
          const savedNotifs = JSON.parse(localStorage.getItem("vouch-past-notifs-" + uid) || "[]");
          setPastNotifications(savedNotifs);
        } catch(e) {}
        // Show welcome buddy notification if this is their first login
        if (localStorage.getItem("vouch-new-buddy-christian-" + uid)) {
          setNewBuddies(["Christian Wallis"]);
          localStorage.removeItem("vouch-new-buddy-christian-" + uid);
        }
        const params = new URLSearchParams(window.location.search);
        const inviteFrom = params.get("invite");
        if (inviteFrom && inviteFrom !== uid) {
          // Check if connection already exists in either direction before auto-buddying
          const { data: existingBuddy } = await supabase.from("buddies")
            .select("id")
            .or(`and(requester_id.eq.${inviteFrom},receiver_id.eq.${uid}),and(requester_id.eq.${uid},receiver_id.eq.${inviteFrom})`)
            .maybeSingle();
          if (!existingBuddy) {
            await supabase.from("buddies").insert({ requester_id: inviteFrom, receiver_id: uid, status: "accepted" });
          } else {
            await supabase.from("buddies").update({ status: "accepted" }).eq("id", existingBuddy.id);
          }
          window.history.replaceState({}, "", window.location.pathname);
          loadBuddies(uid);
        }
      } else {
        setUser(null); setUserId(null); setBoard({ ...EMPTY_BOARD });
      }
    };
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const now = Math.floor(Date.now() / 1000);
        if (session.expires_at && session.expires_at < now) {
          const { data: { session: refreshed } } = await supabase.auth.refreshSession();
          await setUserFromSession(refreshed);
        } else {
          await setUserFromSession(session);
        }
      } else {
        setUserFromSession(null);
      }
      setSessionChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") { setUser(null); setUserId(null); setBoard({ ...EMPTY_BOARD }); return; }
      // Ignore TOKEN_REFRESHED, SIGNED_IN from tab focus - only handle initial auth
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = async () => { await supabase.auth.signOut(); setUser(null); };

  useEffect(() => { musicPrefRef.current = musicPreference; }, [musicPreference]);

  const isMusicUrl = (url) => url && url.includes("open.spotify.com");

  const openMusicUrl = async (url, title, sub, catKey) => {
    if (!isMusicUrl(url)) { window.open(url, "_blank"); return; }
    const pref = musicPrefRef.current;
    if (pref === "spotify") { window.open(url, "_blank"); return; }
    if (pref === "apple_music") {
      const amUrl = await fetchAppleMusicUrl(title, sub, catKey);
      window.open(amUrl, "_blank");
      return;
    }
    setMusicPickerModal({ url, title, sub, catKey });
  };

  const saveMusicPreference = async (pref, url, title, sub) => {
    setMusicPreference(pref);
    musicPrefRef.current = pref;
    await supabase.from("profiles").update({ music_preference: pref }).eq("id", userId);
    setMusicPickerModal(null);
    if (pref === "spotify") window.open(url, "_blank");
    else {
      const amUrl = await fetchAppleMusicUrl(title, sub, musicPickerModal?.catKey);
      window.open(amUrl, "_blank");
    }
  };

  const saveExpandPreviousVouches = async (enabled) => {
    setExpandPreviousVouches(enabled);
    if (userId) await supabase.from("profiles").update({ expand_previous_vouches: enabled }).eq("id", userId);
  };

  const dismissFeaturesAnnounce = () => {
    localStorage.setItem(FEATURES_ANNOUNCE_KEY, "1");
    setShowFeaturesAnnounce(false);
    if (!localStorage.getItem("vouch-expand-vouches-prompted")) setShowExpandVouchesPrompt(true);
  };

  const dismissExpandVouchesPrompt = async (enabled) => {
    localStorage.setItem("vouch-expand-vouches-prompted", "1");
    setShowExpandVouchesPrompt(false);
    await saveExpandPreviousVouches(enabled);
  };

  const isOwn     = !viewing;
  const currBoard = isOwn ? board : viewBoard;
  const currName  = isOwn ? user?.displayName : viewing?.displayName || viewing?.username;
  const isBuddyWith = (otherUserId) => !!otherUserId && buddies.some(b => b.userId === otherUserId);

  const postTileComment = async (boardItemId, body) => {
    if (!userId || !boardItemId) return;
    const { data, error } = await supabase
      .from("vouch_tile_buddy_comments")
      .insert({ board_item_id: boardItemId, user_id: userId, body: body.trim() })
      .select("id, board_item_id, user_id, body, created_at")
      .single();
    if (error) {
      alert(error.message || "Could not post comment.");
      return;
    }
    setVouchTileComments(prev => ({
      ...prev,
      [boardItemId]: [...(prev[boardItemId] || []), { ...data, displayName: user?.displayName || "You" }],
    }));
  };

  const deleteTileComment = async (commentId, boardItemId) => {
    if (!userId) return;
    const { error } = await supabase.from("vouch_tile_buddy_comments").delete().eq("id", commentId).eq("user_id", userId);
    if (error) return;
    setVouchTileComments(prev => ({
      ...prev,
      [boardItemId]: (prev[boardItemId] || []).filter(c => c.id !== commentId),
    }));
  };

  const tileCommentProps = {
    tileCommentsByItemId: vouchTileComments,
    currentUserId: userId,
    onPostTileComment: postTileComment,
    onDeleteTileComment: deleteTileComment,
  };

  const notifBadgeCount = newAgreements.length + pendingIn.length + newBuddies.length + newCommentNotifs.length;

  const dismissAllNotifications = () => {
    const toSave = [
      ...newAgreements.map(r => ({ type: "agree", display_name: r.display_name, title: r.title, date: new Date().toISOString() })),
      ...newCommentNotifs.map(r => ({
        type: "comment",
        display_name: r.display_name,
        title: r.title,
        body: r.body,
        board_owner_name: r.board_owner_name,
        on_own_vouch: r.on_own_vouch,
        date: r.created_at || new Date().toISOString(),
      })),
      ...newBuddies.map(name => ({ type: "buddy", display_name: name, date: new Date().toISOString() })),
    ];
    const updated = [...toSave, ...pastNotifications].slice(0, 50);
    setPastNotifications(updated);
    localStorage.setItem("vouch-past-notifs-" + userId, JSON.stringify(updated));
    setShowNotifications(false);
    setNewAgreements([]);
    setNewCommentNotifs([]);
    setNewBuddies([]);
    const now = new Date().toISOString();
    localStorage.setItem("vouch-last-visit", now);
    supabase.from("profiles").update({ last_visit: now }).eq("id", userId);
  };

  const openCommentNotification = (n) => {
    if (!n?.board_owner_id) return;
    if (n.board_owner_id === userId) {
      setViewing(null);
      setTab("board");
      window.history.pushState({ tab: "board" }, "", "/");
    } else {
      const buddy = buddies.find(b => b.userId === n.board_owner_id);
      setViewing(buddy || { userId: n.board_owner_id, displayName: n.board_owner_name, username: "", avatarUrl: null });
      setTab("board");
      loadViewBoard(n.board_owner_id);
      loadBoardReactions(n.board_owner_id, true);
    }
    setShowNotifications(false);
    setShowAgreements(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const items = [];
    if (!viewing && activeBoard?.vouch_board_items?.length) items.push(...activeBoard.vouch_board_items);
    if (viewing && viewActiveBoard?.vouch_board_items?.length) items.push(...viewActiveBoard.vouch_board_items);
    if (!items.length) {
      setVouchTileComments({});
      return;
    }
    loadTileCommentsMap(items).then(setVouchTileComments);
  }, [activeBoard?.id, viewActiveBoard?.id, viewing?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewing || !boardArchive.length) return;
    const archiveItems = boardArchive.filter(b => !b.is_active && b.published_at).flatMap(b => b.vouch_board_items || []);
    if (!archiveItems.length) return;
    loadTileCommentsMap(archiveItems).then(map => setVouchTileComments(prev => ({ ...prev, ...map })));
  }, [boardArchive, viewing?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const dudeSame = async (item, overrideOwnerId) => {
    if (!userId) return;
    const ownerId = overrideOwnerId || viewing?.userId;
    if (!ownerId) return;
    if (ownerId === userId) return;
    const resolvedItemId = String(item.id || item.item_id);
    const already = myReactions.find(r => r.item_id === resolvedItemId && r.item_owner_id === ownerId);
    if (already) {
      await supabase.from("reactions").delete().eq("id", already.id);

    } else {
      await supabase.from("reactions").upsert({
        user_id: userId,
        item_owner_id: ownerId,
        item_id: resolvedItemId,
        category: item._cat || item.catKey || item.category || "",
        title: item.title,
        subtitle: item.sub || item.subtitle || item.artist || item.author || "",
        poster: item.poster || null,
        source_url: item.sourceUrl || item.source_url || null,
      }, { onConflict: "user_id,item_owner_id,item_id" });

    }
    await loadMyReactions(userId);
    await loadBoardReactions(ownerId);
  };

  const generateInviteLink = () => {
    const link = `${window.location.origin}/@${user.username}`;
    setInviteLink(link);
    navigator.clipboard?.writeText(link);
  };

  const generateShareCard = async (board = activeBoard) => {
    const shareUsername = viewing ? viewing.username : user.username;
    const shareName = viewing ? viewing.displayName : user.displayName;
    const shareUrl = `${window.location.origin}/@${shareUsername}`;

    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext("2d");

    const drawCard = async (posterImgs) => {
      const boardTheme = board?.theme && board.theme !== "Other" ? board.theme : (board?.name || "Vouch");
      const items = (board?.vouch_board_items || []).sort((a,b) => a.position - b.position).slice(0, 5);
      const tileCount = items.length;
      const catLabels = { movies: "Film", shows: "Show", albums: "Album", artists: "Artist", songs: "Song", books: "Book", podcasts: "Podcast" };
      const musicCats = ["albums", "artists", "songs", "podcasts"];
      const W = 1080, H = 1920;

      // Background — match site paper (T.bg)
      ctx.fillStyle = T.bg; ctx.fillRect(0, 0, W, H);

      // Vouch5 logo — full width at top
      const vLogo = new Image(); vLogo.crossOrigin = "anonymous";
      vLogo.src = window.location.origin + "/vouch5-logo.png";
      await new Promise(r => { vLogo.onload = r; vLogo.onerror = r; setTimeout(r, 2000); });
      const logoH = 200;
      if (vLogo.naturalWidth > 0) {
        const logoW = vLogo.naturalWidth * (logoH / vLogo.naturalHeight);
        ctx.drawImage(vLogo, (W - logoW) / 2, 60, logoW, logoH);
      }

      // Double rule
      ctx.strokeStyle = "#111008"; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(56, 290); ctx.lineTo(W-56, 290); ctx.stroke();
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(56, 300); ctx.lineTo(W-56, 300); ctx.stroke();

      // Theme name
      ctx.fillStyle = "#111008"; ctx.font = "900 64px 'Times New Roman', serif"; ctx.textAlign = "center";
      ctx.fillText(boardTheme.length > 22 ? boardTheme.slice(0,22)+"…" : boardTheme, 540, 372);
      ctx.fillStyle = "#7a7568"; ctx.font = "italic 400 30px Georgia";
      ctx.fillText("@" + shareUsername, 540, 414);
      ctx.textAlign = "left";

      // Draw poster helper — labelPos: "top" or "bottom"
      const dp = (img, x, y, w, h, item, labelPos="bottom") => {
        ctx.fillStyle = "#111008"; ctx.fillRect(x, y, w, h);
        if (img && img.naturalWidth > 0) {
          const ir = img.naturalWidth/img.naturalHeight, cr = w/h;
          let sx=0,sy=0,sw=img.naturalWidth,sh=img.naturalHeight;
          if (ir > cr) { sw = sh*cr; sx = (img.naturalWidth-sw)/2; }
          else { sh = sw/cr; sy = (img.naturalHeight-sh)/2; }
          ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
        }
        // Overlay label
        if (item && item.title) {
          // Build label lines based on category
          const cat = item.category || "";
          let line1 = item.title || "";
          let line2 = "";
          if (cat === "movies" || cat === "shows") {
            line2 = item.subtitle ? item.subtitle.slice(0,4) : "";
          } else if (cat === "albums") {
            line2 = item.subtitle || "";
          } else if (cat === "songs") {
            line2 = item.subtitle || "";
          } else if (cat === "artists") {
            line2 = "";
          } else {
            line2 = item.subtitle || "";
          }

          const barH = h * 0.3;
          const isTop = labelPos === "top";
          const grad = ctx.createLinearGradient(x, isTop ? y : y+h-barH, x, isTop ? y+barH : y+h);
          grad.addColorStop(0, isTop ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0)");
          grad.addColorStop(1, isTop ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.9)");
          ctx.fillStyle = grad; ctx.fillRect(x, isTop ? y : y+h-barH, w, barH);

          const maxW = w - 16;
          // Auto-fit line1
          let fs = Math.floor(w * 0.1);
          ctx.font = "700 " + fs + "px 'Times New Roman', serif";
          while (ctx.measureText(line1).width > maxW && fs > 10) { fs--; ctx.font = "700 " + fs + "px 'Times New Roman', serif"; }

          ctx.fillStyle = "#fff"; ctx.textAlign = "left";
          const textY = isTop ? y + barH*0.42 : y+h - barH*0.42;
          ctx.fillText(line1, x+8, textY);

          if (line2) {
            let fs2 = Math.max(10, Math.floor(fs * 0.78));
            ctx.font = "italic 400 " + fs2 + "px Georgia";
            while (ctx.measureText(line2).width > maxW && fs2 > 9) { fs2--; ctx.font = "italic 400 " + fs2 + "px Georgia"; }
            ctx.fillStyle = "rgba(255,255,255,0.75)";
            const subY = isTop ? y + barH*0.76 : y+h - barH*0.1;
            ctx.fillText(line2, x+8, subY);
          }
          ctx.textAlign = "left";
        }
      };

      // Draw label helper
      const dl = (item, cx, y) => {
        if (!item) return;
        const cat = catLabels[item.category] || "";
        const yr = item.subtitle ? " (" + item.subtitle.slice(0,4) + ")" : "";
        const ttl = ((item.title||"").slice(0,16) + ((item.title||"").length>16?"…":"")) + yr;
        ctx.fillStyle = "#111008"; ctx.font = "600 20px 'Times New Roman', serif"; ctx.textAlign = "center";
        ctx.fillText(ttl, cx, y);
        ctx.fillStyle = "rgba(17,16,8,0.4)"; ctx.font = "400 16px Georgia";
        ctx.fillText(cat.toUpperCase(), cx, y+22);
        ctx.textAlign = "left";
      };

      const PAD = 48, GAP = 10;
      const gridTop = 448;
      const gridW = W - PAD*2;

      if (tileCount === 1) {
        // Single large tile
        const isSq = musicCats.includes(items[0]?.category);
        const pw = gridW * 0.72, ph = isSq ? pw : pw * 1.4;
        const px = (W-pw)/2;
        dp(posterImgs[0], px, gridTop, pw, ph, items[0]);
        ctx.fillStyle = "#111008"; ctx.font = "900 48px 'Times New Roman', serif"; ctx.textAlign = "center";
        ctx.fillText((items[0]?.title||"").slice(0,22), 540, gridTop+ph+58);
        if (items[0]?.subtitle) { ctx.fillStyle="#7a7568"; ctx.font="italic 400 32px Georgia"; ctx.fillText(items[0].subtitle, 540, gridTop+ph+100); }
        ctx.textAlign = "left";

      } else if (tileCount === 2) {
        const tw = (gridW-GAP)/2, th = tw*1.5;
        dp(posterImgs[0], PAD, gridTop, tw, th, items[0]);
        dp(posterImgs[1], PAD+tw+GAP, gridTop, tw, th, items[1]);


      } else if (tileCount === 3) {
        const tw3 = (gridW-GAP*2)/3, th3 = tw3*1.5;
        for (let i=0;i<3;i++) {
          dp(posterImgs[i], PAD+i*(tw3+GAP), gridTop, tw3, th3, items[i]);
          dl(items[i], PAD+i*(tw3+GAP)+tw3/2, gridTop+th3+34);
        }

      } else if (tileCount === 4 || tileCount === 5) {
        // 2x2 corner grid — 5-tile adds hero overlapping center
        const cw = (gridW-GAP)/2 * 0.82, ch = cw*1.5;
        const row2 = gridTop + ch + GAP;
        const colOff = (gridW - cw*2 - GAP) / 2;
        const cornerIdx = tileCount === 5 ? [1, 2, 3, 4] : [0, 1, 2, 3];
        const cornerPos = [
          [PAD+colOff, gridTop, "top"],
          [PAD+colOff+cw+GAP, gridTop, "top"],
          [PAD+colOff, row2, "bottom"],
          [PAD+colOff+cw+GAP, row2, "bottom"],
        ];
        for (let i = 0; i < 4; i++) {
          const idx = cornerIdx[i];
          const [x, y, labelPos] = cornerPos[i];
          dp(posterImgs[idx], x, y, cw, ch, items[idx], labelPos);
        }
        ctx.strokeStyle="rgba(17,16,8,0.15)"; ctx.lineWidth=1.5;
        ctx.strokeRect(PAD+colOff, gridTop, cw, ch);
        ctx.strokeRect(PAD+colOff+cw+GAP, gridTop, cw, ch);
        ctx.strokeRect(PAD+colOff, row2, cw, ch);
        ctx.strokeRect(PAD+colOff+cw+GAP, row2, cw, ch);
        if (tileCount === 5) {
          const hw = cw*0.88, hh = ch*0.88;
          const hx = (W-hw)/2, hy = gridTop + (ch*2+GAP-hh)/2;
          ctx.save(); ctx.shadowColor="rgba(0,0,0,0.45)"; ctx.shadowBlur=36; ctx.shadowOffsetY=10;
          dp(posterImgs[0], hx, hy, hw, hh, items[0]);
          ctx.restore();
          ctx.strokeStyle="rgba(17,16,8,0.2)"; ctx.lineWidth=2;
          ctx.strokeRect(hx, hy, hw, hh);
        }
      }

      // CTA
      const ctaY = 1800;
      ctx.strokeStyle="rgba(17,16,8,0.2)"; ctx.lineWidth=1.5;
      ctx.beginPath(); ctx.moveTo(PAD, ctaY); ctx.lineTo(W-PAD, ctaY); ctx.stroke();
      ctx.fillStyle="#555"; ctx.font="italic 400 28px Georgia"; ctx.textAlign="center";
      ctx.fillText("What would you put your name behind right now?", 540, ctaY+40);
      ctx.fillStyle="#111008"; ctx.font="900 36px 'Times New Roman', serif";
      ctx.fillText("vouch5.com/@"+shareUsername, 540, ctaY+84);
      ctx.textAlign="left";
      ctx.fillStyle="#111008"; ctx.fillRect(0,1915,W,5);
    };
    const loadImg = async (url) => {
      if (!url) return null;
      try {
        const proxyUrl = `/api/imgproxy?url=${encodeURIComponent(url)}`;
        const response = await fetch(proxyUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        return await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => { URL.revokeObjectURL(objectUrl); resolve(img); };
          img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
          img.src = objectUrl;
        });
      } catch { return null; }
    };
    try {
      const activeBoardItems = (board?.vouch_board_items || []).sort((a,b) => a.position - b.position).slice(0, 5);
      const posterImgs = await Promise.all(activeBoardItems.map(item => loadImg(item.poster)));
      await drawCard(posterImgs);
    } catch {
      await drawCard([]);
    }
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error("Failed to generate share card")); return; }
        resolve({ blob, previewUrl: URL.createObjectURL(blob), shareUrl, shareName });
      }, "image/png");
    });
  };

  const resolveShareBoard = () => {
    if (activeBoard?.vouch_board_items?.length) return activeBoard;
    try {
      const draft = JSON.parse(localStorage.getItem("vouch-board-draft-v2") || "null");
      if (draft?.items?.length) {
        const finalName = draft.theme === "Other" ? draft.name : draft.theme;
        return {
          name: finalName || "Vouch",
          theme: draft.theme || "",
          description: draft.description || "",
          vouch_board_items: draft.items.map((item, i) => ({
            item_id: String(item.id || item.item_id || i),
            title: item.title,
            subtitle: item.sub || item.subtitle || "",
            poster: item.poster || null,
            source_url: item.sourceUrl || item.source_url || null,
            category: item.catKey || item.category || "",
            position: i,
          })),
        };
      }
    } catch {}
    const archived = (boardArchive || [])
      .filter(b => b.vouch_board_items?.length)
      .sort((a, b) => new Date(b.published_at || b.created_at || 0) - new Date(a.published_at || a.created_at || 0));
    return archived[0] || null;
  };

  const closeShareModal = () => {
    setShareModal(false);
    setShareCardGenerating(false);
    shareCardBlobRef.current = null;
    setShareCardUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const blobToJpegBlob = (blob) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = T.bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(b => b ? resolve(b) : reject(new Error("jpeg conversion failed")), "image/jpeg", 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
    img.src = url;
  });

  const getShareCardPhotoFile = async () => {
    const blob = shareCardBlobRef.current;
    if (!blob) return null;
    const jpeg = await blobToJpegBlob(blob);
    return new File([jpeg], "vouch-story.jpg", { type: "image/jpeg" });
  };

  const saveShareCardToPhotos = async () => {
    try {
      const file = await getShareCardPhotoFile();
      if (!file) return;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(file);
      a.download = "vouch-story.jpg";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      if (e.name !== "AbortError") throw e;
    }
  };

  const downloadShareCard = async () => {
    const blob = shareCardBlobRef.current;
    if (!blob) return;
    if (prefersPhotoSave) {
      try { await saveShareCardToPhotos(); } catch {}
      return;
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "vouch-board.png";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const shareCardNative = async () => {
    const blob = shareCardBlobRef.current;
    if (!blob) return;
    const shareUrl = `${window.location.origin}/@${user.username}`;
    const shareName = user.displayName;
    const file = prefersPhotoSave
      ? await getShareCardPhotoFile()
      : new File([blob], "vouch-board.png", { type: "image/png" });
    if (!file) return;
    try { await navigator.clipboard.writeText(shareUrl); } catch(e) {}
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: `${shareName}'s Vouch Board`, text: shareUrl });
        setTimeout(() => { try { navigator.clipboard.writeText(shareUrl); } catch(e) {} }, 800);
      } catch (e) {
        if (e.name !== "AbortError") await saveShareCardToPhotos();
      }
    } else {
      await downloadShareCard();
    }
  };

  const shareBoard = async () => {
    try {
      if (!shareCardBlobRef.current) {
        const board = resolveShareBoard();
        if (!board?.vouch_board_items?.length) return;
        setShareCardGenerating(true);
        const result = await generateShareCard(board);
        setShareCardUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return result.previewUrl;
        });
        shareCardBlobRef.current = result.blob;
        setShareCardGenerating(false);
      }
      await shareCardNative();
    } catch {
      setShareCardGenerating(false);
    }
  };

  useEffect(() => {
    if (!shareModal) return;
    const board = resolveShareBoard();
    if (!board?.vouch_board_items?.length) return;
    let cancelled = false;
    setShareCardGenerating(true);
    setShareCardUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    shareCardBlobRef.current = null;
    generateShareCard(board).then((result) => {
      if (cancelled) {
        URL.revokeObjectURL(result.previewUrl);
        return;
      }
      setShareCardUrl(result.previewUrl);
      shareCardBlobRef.current = result.blob;
      setShareCardGenerating(false);
    }).catch(() => {
      if (!cancelled) setShareCardGenerating(false);
    });
    return () => { cancelled = true; };
  }, [shareModal, activeBoard?.id, boardArchive.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCategories = async (cats) => {
    await supabase.from("profiles").update({ categories: cats }).eq("id", userId);
    setUserCategories(cats);
    setOnboarding(false);
  };

  const saveAvatar = async (file) => {
    const url = `/avatars/${file}.jpg`;
    await supabase.from("profiles").update({ avatar_url: url }).eq("id", userId);
    setUser(prev => ({ ...prev, avatarUrl: url }));
    setAvatarPicker(false);
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop();
    const path = `${userId}.${ext}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true, contentType: file.type });
    if (error) { console.error("Avatar upload error:", error); return; }
    const { data } = supabase.storage.from("avatars").getPublicUrl(path);
    const url = data.publicUrl;
    await supabase.from("profiles").update({ avatar_url: url }).eq("id", userId);
    setUser(prev => ({ ...prev, avatarUrl: url }));
    setAvatarPicker(false);
  };

  const sendBuddyRequest = async (receiverId) => {
    // Check if connection already exists in either direction
    const { data: existing } = await supabase.from("buddies")
      .select("id")
      .or(`and(requester_id.eq.${userId},receiver_id.eq.${receiverId}),and(requester_id.eq.${receiverId},receiver_id.eq.${userId})`)
      .maybeSingle();
    if (existing) return; // already connected, skip
    await supabase.from("buddies").insert({ requester_id: userId, receiver_id: receiverId, status: "pending" });
    loadBuddies(userId);
  };

  const acceptBuddy = async (buddyRowId) => {
    await supabase.from("buddies").update({ status: "accepted" }).eq("id", buddyRowId);
    loadBuddies(userId);
  };

  const removeBuddy = async (buddyRowId) => {
    await supabase.from("buddies").delete().eq("id", buddyRowId);
    loadBuddies(userId);
  };

  const viewBuddy = async (buddy) => { // eslint-disable-line no-unused-vars
    window.scrollTo(0, 0);
    setViewing(buddy);
    setTab("board");
    await loadViewBoard(buddy.userId);
    await loadBoardReactions(buddy.userId, true);
    // Load this buddy's own buddies for display at bottom of their board
    const { data } = await supabase.from("buddies")
      .select("requester_id, receiver_id")
      .or(`requester_id.eq.${buddy.userId},receiver_id.eq.${buddy.userId}`)
      .eq("status", "accepted");
    if (data && data.length > 0) {
      const ids = data.map(b => b.requester_id === buddy.userId ? b.receiver_id : b.requester_id);
      const { data: profiles } = await supabase.from("profiles").select("id, display_name, avatar_url").in("id", ids);
      setViewBuddies(profiles || []);
    } else {
      setViewBuddies([]);
    }
  };

  const addItem = async (catKey, item) => {
    const catLabel = { movies: "Film", albums: "Albums", artists: "Artists", songs: "Songs", books: "Books", shows: "Television", podcasts: "Podcasts" }[catKey] || catKey;

    // Check for duplicate vouch
    const alreadyVouched = board[catKey]?.find(i => String(i.id) === String(item.id) && i.vouched);
    if (alreadyVouched && item.vouched) {
      alert(item.title + " is already in your Vouch 5."); return;
    }

    // Check category limit (non-vouched only)
    const existingInCat = board[catKey]?.find(i => String(i.id) === String(item.id));
    if (!existingInCat) {
      const nonVouchedCount = (board[catKey] || []).filter(i => !i.vouched).length;
      if (nonVouchedCount >= 5) {
        alert(catLabel + " is full — you can have up to 5 mentions. Remove one to make room."); return;
      }
    }

    // Optimistically update UI immediately
    const optimisticItem = {
      id: item.id, title: item.title, sub: item.sub || "",
      poster: item.poster || null, comment: item.comment || "",
      vouched: item.vouched === true, sourceUrl: item.sourceUrl || null,
      dbId: null, // will be filled after DB save
    };

    if (existingInCat) {
      // Update existing item vouched flag
      setBoard(prev => ({
        ...prev,
        [catKey]: prev[catKey].map(i => String(i.id) === String(item.id) ? { ...i, vouched: item.vouched === true, comment: item.comment || i.comment } : i)
      }));
    } else {
      // Add new item
      setBoard(prev => ({
        ...prev,
        [catKey]: [...(prev[catKey] || []), optimisticItem]
      }));
    }

    // Sync with DB in background
    try {
      const { data: existing } = await supabase.from("endorsements")
        .select("id")
        .eq("user_id", userId)
        .eq("category", catKey)
        .eq("item_id", String(item.id))
        .maybeSingle();

      if (existing) {
        await supabase.from("endorsements")
          .update({ vouched: item.vouched === true, comment: item.comment || "" })
          .eq("id", existing.id);
      } else {
        await supabase.from("endorsements").insert({
          user_id: userId, category: catKey, item_id: String(item.id),
          title: item.title, subtitle: item.sub || "",
          poster: item.poster || null, comment: item.comment || "",
          vouched: item.vouched === true, source_url: item.sourceUrl || null,
        });
      }
      // Reload to get real dbId
      await loadBoard(userId);
    } catch(e) { console.error("addItem error:", e); await loadBoard(userId); }
  };

  // fromVouch5=true just un-vouches the item (keeps it in category section)
  // fromVouch5=false fully deletes it
  const removeItem = async (catKey, idx, fromVouch5 = false) => {
    const item = board[catKey]?.[idx];
    if (!item) return;

    // Optimistically update UI immediately - always fully remove from display
    if (fromVouch5) {
      setBoard(prev => ({
        ...prev,
        [catKey]: prev[catKey].map((it, i) => i === idx ? { ...it, vouched: false } : it)
      }));
    } else {
      setBoard(prev => ({
        ...prev,
        [catKey]: prev[catKey].filter((_, i) => i !== idx)
      }));
    }

    // Sync with DB in background
    try {
      if (!item.dbId) { await loadBoard(userId); return; }
      if (fromVouch5) {
        await supabase.from("endorsements").update({ vouched: false }).eq("id", item.dbId);
      } else {
        await supabase.from("endorsements").delete().eq("id", item.dbId);
      }
    } catch(e) { console.error("removeItem error:", e); await loadBoard(userId); }
  };

  const addToQueue = async (item) => {
    if (!userId) return;
    const itemId = String(item.item_id || item.id);
    const currentQueue = queueRef.current;
    const exists = currentQueue.find(q => String(q.id) === itemId);
    const newQ = exists
      ? currentQueue.filter(q => String(q.id) !== itemId)
      : [...currentQueue, { id: itemId, title: item.title, poster: item.poster || null, sub: item.sub || item.subtitle || "", sourceUrl: item.sourceUrl || item.source_url || null, category: item._cat || item.category || item.catKey || "" }];
    setQueue(newQ);
    localStorage.setItem("vouch-queue-" + userId, JSON.stringify(newQ));
    await supabase.from("profiles").update({ queue_items: JSON.stringify(newQ) }).eq("id", userId);
  };
  const removeFromQueue = async (id) => {
    const newQ = queueRef.current.filter(q => String(q.id) !== String(id));
    setQueue(newQ);
    localStorage.setItem("vouch-queue-" + userId, JSON.stringify(newQ));
    await supabase.from("profiles").update({ queue_items: JSON.stringify(newQ) }).eq("id", userId);
  };
  const vouchedCount = Object.values(board).flat().filter(item => item.vouched).length;
  const profileVouchCount = isOwn ? countPublishedVouches(boardArchive) : viewPublishCount;

  // PWA badge
  useEffect(() => {
    const count = notifBadgeCount;
    if (navigator.setAppBadge) {
      if (count > 0) navigator.setAppBadge(count);
      else navigator.clearAppBadge();
    }
  }, [newAgreements.length, newCommentNotifs.length, pendingIn.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const canPublish = (() => {
    if (isAdmin) return true;
    if (!activeBoard || !activeBoard.published_at) return true;
    const publishedAt = new Date(activeBoard.published_at);
    const pubDay = new Date(publishedAt.getFullYear(), publishedAt.getMonth(), publishedAt.getDate());
    const today = new Date();
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const daysSince = (todayDay - pubDay) / (1000 * 60 * 60 * 24);
    return daysSince >= 7;
  })();

  const nextPublishDate = (() => {
    if (!activeBoard?.published_at || canPublish) return null;
    const d = new Date(activeBoard.published_at);
    d.setDate(d.getDate() + 7);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  })();

  const handlePullRefresh = useCallback(async () => {
    if (!userId) return;
    const tasks = [
      loadBoard(userId, { silent: true }),
      loadBuddies(userId),
      loadMyReactions(userId),
      loadVouchBoards(userId),
      loadBoardReactions(userId, false),
    ];
    if (viewing?.userId) {
      tasks.push(loadViewBoard(viewing.userId));
      tasks.push(loadBoardReactions(viewing.userId, true));
    }
    await Promise.all(tasks);
    loadBadgesForUser(userId).then(setOwnItemBadges).catch(() => {});
    if (viewing?.userId) {
      loadBadgesForUser(viewing.userId).then(setViewItemBadges).catch(() => {});
    }
    setFeedRefreshKey(k => k + 1);
  }, [userId, viewing?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pullRefreshBlocked = boardEditor || showFeaturesAnnounce || showExpandVouchesPrompt || showNotifications || showAgreements || buddyModal || shareModal || musicPickerModal || removeVouchModal || avatarPicker || showContactModal;
  const { pull: pullRefreshDistance, refreshing: pullRefreshing, threshold: pullRefreshThreshold } = usePullToRefresh(!!userId && !pullRefreshBlocked, handlePullRefresh);

  const inviteParam  = new URLSearchParams(window.location.search).get("invite");
  // Support /@username clean URLs
  const pathUsername = window.location.pathname.startsWith("/@") ? window.location.pathname.slice(2) : null;
  const isStartPage = window.location.pathname === "/start";

  const [pathUserId, setPathUserId] = useState(null);
  useEffect(() => {
    if (pathUsername) {
      supabase.from("profiles").select("id").eq("username", pathUsername).maybeSingle()
        .then(({ data }) => { if (data) setPathUserId(data.id); });
    }
  }, [pathUsername]);

  const resolvedInviteId = inviteParam || pathUserId;

  // When logged in and visiting /@username, auto-load that person's board
  useEffect(() => {
    if (user && pathUserId && pathUserId !== userId) {
      supabase.from("profiles").select("id, display_name, avatar_url, username").eq("id", pathUserId).maybeSingle()
        .then(({ data }) => {
          if (data) {
            setViewing({ userId: data.id, username: data.username, displayName: data.display_name, avatarUrl: data.avatar_url });
            setTab("board");
            loadViewBoard(data.id);
            loadBoardReactions(data.id, true);
            scrollToTop();
          }
        });
    }
  }, [user, pathUserId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sessionChecked) return <><Styles /><div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#C8C2B4" }}><img src="/v5-icon.png" alt="Vouch5" style={{ height: "clamp(160px,40vw,320px)", display: "block" }} /></div></>;
  if (!user) {
    if (isStartPage) {
      return <StartPage onSignUp={() => {
        supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: window.location.origin } });
      }} />;
    }
    if (resolvedInviteId) {
      return <PublicBoard inviteUserId={resolvedInviteId} onSignUp={() => {
        supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: window.location.origin + `?invite=${resolvedInviteId}` } });
      }} />;
    }
    if (pathUsername && !pathUserId) return <><Styles /><div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#C8C2B4" }}><div style={{ fontFamily: "Georgia, serif", fontStyle: "italic", color: "#666" }}>Loading...</div></div></>;
    return <><Styles /><Auth /></>;
  }
  if (loading) return <><Styles /><div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg }}><div className="loading">Loading…</div></div></>;

  return (
    <>
      <Styles />
      <PullToRefreshIndicator pull={pullRefreshDistance} refreshing={pullRefreshing} threshold={pullRefreshThreshold} />
      <div className="app">
        <header className="masthead">
          <div className="masthead-meta" style={{ justifyContent: "space-between" }}>
              <span className="clickable" style={{ fontWeight: 700 }} onClick={() => setLegalPage("how")}>How it Works</span>
              <span className="clickable" onClick={() => setShowContactModal(true)}>Contact</span>
              <span className="clickable" onClick={() => { setTab("board"); setViewing(null); window.history.replaceState({}, "", "/"); scrollToTop(); }}>@{user.username}</span>
              <span className="clickable" onClick={signOut}>Sign out</span>
          </div>
          <div className="masthead-nameplate" onClick={() => { setTab("board"); setViewing(null); window.history.replaceState({}, "", "/"); scrollToTop(); }}>
            <img src="/vouch5-logo.png" alt="Vouch5" style={{height:"clamp(80px,16vw,160px)",display:"block",margin:"0 auto"}} />
          </div>
          <div className="masthead-rule-ornament"><span>—</span><span>✦</span><span>—</span></div>
          <div className="masthead-tagline">Love it? Vouch for it.</div>
          <nav className="nav">
            <button className={`nav-btn${tab === "home" ? " active" : ""}`} onClick={() => { setTab("home"); setViewing(null); window.history.pushState({tab:"home"}, "", "/"); scrollToTop(); }} style={{ position: "relative" }}>
              Home
              {newAgreements.length > 0 && tab !== "home" && <span style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: T.ink }} />}
              {newAgreements.length === 0 && newCommentNotifs.length > 0 && tab !== "home" && <span style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: T.ink }} />}
            </button>
            <button className={`nav-btn${tab === "board" && !viewing ? " active" : ""}`} onClick={() => { setTab("board"); setViewing(null); window.history.pushState({tab:"board"}, "", "/"); scrollToTop(); }}>My Board</button>
            <button className={`nav-btn${tab === "friends" ? " active" : ""}`} onClick={() => { setTab("friends"); setViewing(null); window.history.pushState({tab:"friends"}, "", "/"); scrollToTop(); }} style={{ position: "relative" }}>
              Buddies
              {pendingIn.length > 0 && <span style={{ position: "absolute", top: 5, right: 5, background: tab === "friends" ? T.bg : T.ink, borderRadius: "50%", width: 6, height: 6, display: "inline-block" }} />}
            </button>
            <button className={`nav-btn${tab === "settings" ? " active" : ""}`} onClick={() => { setTab("settings"); setViewing(null); window.history.pushState({tab:"settings"}, "", "/"); scrollToTop(); }}>Settings</button>

          </nav>
        </header>

        <main className="page">


          {tab === "home" && !viewing && (
            <div style={{ maxWidth: 680, margin: "0 auto", paddingTop: 24 }}>
              {newAgreements.length > 0 && (
                <div style={{ background: T.ink, color: T.bg, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setShowAgreements(true)}>
                  <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13 }}>
                    {newAgreements.length === 1
                      ? <><strong style={{ fontStyle: "normal", fontFamily: "'Spectral SC',serif", fontSize: 11 }}>{newAgreements[0].display_name}</strong> agreed with <strong style={{ fontStyle: "normal" }}>{newAgreements[0].title}</strong> →</>
                      : [...new Set(newAgreements.map(r => r.user_id))].length === 1
                        ? <><strong style={{ fontStyle: "normal", fontFamily: "'Spectral SC',serif", fontSize: 11 }}>{newAgreements[0].display_name}</strong> agreed with {newAgreements.length} of your titles — tap to see →</>
                        : <><strong style={{ fontStyle: "normal", fontFamily: "'Spectral SC',serif", fontSize: 11 }}>{[...new Set(newAgreements.map(r => r.user_id))].length} people</strong> agreed with your titles — tap to see →</>
                    }
                  </div>
                  <button onClick={e => { e.stopPropagation(); setNewAgreements([]); const now = new Date().toISOString(); localStorage.setItem("vouch-last-visit", now); supabase.from("profiles").update({ last_visit: now }).eq("id", userId); }} style={{ background: "transparent", border: "none", color: "rgba(200,194,180,0.5)", fontSize: 20, cursor: "pointer", padding: 0, flexShrink: 0 }}>×</button>
                </div>
              )}
              {newCommentNotifs.length > 0 && (
                <div style={{ background: T.ink, color: T.bg, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setShowNotifications(true)}>
                  <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13 }}>
                    {newCommentNotifs.length === 1 ? (
                      newCommentNotifs[0].on_own_vouch
                        ? <><strong style={{ fontStyle: "normal", fontFamily: "'Spectral SC',serif", fontSize: 11 }}>{newCommentNotifs[0].display_name}</strong> commented on <strong style={{ fontStyle: "normal" }}>{newCommentNotifs[0].title}</strong> →</>
                        : <><strong style={{ fontStyle: "normal", fontFamily: "'Spectral SC',serif", fontSize: 11 }}>{newCommentNotifs[0].display_name}</strong> replied on <strong style={{ fontStyle: "normal" }}>{newCommentNotifs[0].title}</strong> →</>
                    ) : (
                      <><strong style={{ fontStyle: "normal", fontFamily: "'Spectral SC',serif", fontSize: 11 }}>{newCommentNotifs.length} new comments</strong> on your vouches — tap to see →</>
                    )}
                  </div>
                  <button onClick={e => { e.stopPropagation(); setNewCommentNotifs([]); }} style={{ background: "transparent", border: "none", color: "rgba(200,194,180,0.5)", fontSize: 20, cursor: "pointer", padding: 0, flexShrink: 0 }}>×</button>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div className="board-name" style={{ fontSize: 28 }}>Home</div>
                {notifBadgeCount > 0 && (
                  <button onClick={() => setShowNotifications(true)} style={{ background: tab === "home" ? T.bg : T.ink, border: "none", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontFamily: "'Spectral SC',serif", fontSize: 9, fontWeight: 700, color: tab === "home" ? T.ink : T.bg, flexShrink: 0 }}>
                    {notifBadgeCount}
                  </button>
                )}
              </div>
              <div className="board-sub" style={{ marginBottom: 28 }}>Recent activity from your circle</div>
              <BuddyFeed buddies={buddies} selfId={userId} selfName={user?.displayName} selfAvatar={user?.avatarUrl} refreshKey={feedRefreshKey} onViewBuddy={(buddy) => { setViewing(buddy); setTab("board"); loadViewBoard(buddy.userId); loadBoardReactions(buddy.userId, true); window.scrollTo(0,0); }} onViewOwnBoard={() => { setViewing(null); setTab("board"); window.history.pushState({ tab: "board" }, "", "/"); scrollToTop(); }} onDudeSame={dudeSame} onAddToQueue={addToQueue} queue={queue} myReactions={myReactions} onShelfExtras={setShelfExtras} onMusicOpen={openMusicUrl} tileCommentProps={tileCommentProps} isBuddyWithUser={isBuddyWith} />
            </div>
          )}
          {tab === "settings" && !viewing && (
            <div style={{ maxWidth: 540, margin: "32px auto" }}>
              <div className="board-name" style={{ fontSize: 28, marginBottom: 8 }}>Settings</div>
              <div className="board-sub" style={{ marginBottom: 32 }}>Customize your Vouch experience</div>
              <div style={{ marginBottom: 40, borderBottom: `1px solid ${T.paperDark}`, paddingBottom: 32 }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.08em", marginBottom: 8 }}>Profile</div>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 20, lineHeight: 1.6 }}>
                  Change your avatar, display name, or username.
                </div>
                {!editingProfile ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                      <div onClick={() => setAvatarPicker(true)} style={{ cursor: "pointer", position: "relative", flexShrink: 0 }}>
                        <Avatar name={user?.displayName} size={56} avatarUrl={user?.avatarUrl} />
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(17,16,8,0.55)", fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.12em", color: "rgba(200,194,180,0.9)", textAlign: "center", padding: "3px 0" }}>edit</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "'Spectral',serif", fontSize: 14, marginBottom: 4 }}>
                          <span style={{ fontFamily: "'Spectral SC',serif", fontSize: 10, color: T.inkLight, letterSpacing: "0.12em" }}>Display Name</span><br />
                          {user?.displayName}
                        </div>
                        <div style={{ fontFamily: "'Spectral',serif", fontSize: 14 }}>
                          <span style={{ fontFamily: "'Spectral SC',serif", fontSize: 10, color: T.inkLight, letterSpacing: "0.12em" }}>Username</span><br />
                          @{user?.username}
                        </div>
                      </div>
                    </div>
                    <button className="btn btn-ghost" onClick={() => { setProfileDisplayName(user?.displayName || ""); setProfileUsername(user?.username || ""); setProfileSaveMsg(""); setEditingProfile(true); }}>Edit Profile</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: 14 }}>
                      <span className="comment-label">Display Name</span>
                      <input className="search-input" style={{ marginBottom: 0 }} value={profileDisplayName} onChange={e => setProfileDisplayName(e.target.value)} maxLength={60} placeholder="Your name" />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <span className="comment-label">Username</span>
                      <input className="search-input" style={{ marginBottom: 0 }} value={profileUsername} onChange={e => setProfileUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))} maxLength={30} placeholder="username" />
                      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, marginTop: 4 }}>vouch5.com/@{profileUsername}</div>
                    </div>
                    {profileSaveMsg && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 10 }}>{profileSaveMsg}</div>}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-solid" style={{ flex: 1 }} onClick={async () => {
                        if (!profileUsername.trim() || !profileDisplayName.trim()) { setProfileSaveMsg("Both fields are required."); return; }
                        const { data: existing } = await supabase.from("profiles").select("id").eq("username", profileUsername).neq("id", userId).maybeSingle();
                        if (existing) { setProfileSaveMsg("That username is taken — try another."); return; }
                        const { error } = await supabase.from("profiles").update({ username: profileUsername, display_name: profileDisplayName }).eq("id", userId);
                        if (error) {
                          console.error("Profile update failed:", error);
                          setProfileSaveMsg("Something went wrong saving — try again.");
                          return;
                        }
                        setUser(prev => ({ ...prev, username: profileUsername, displayName: profileDisplayName }));
                        setEditingProfile(false);
                        setProfileSaveMsg("");
                        window.history.replaceState({}, "", "/");
                      }}>Save</button>
                      <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => { setEditingProfile(false); setProfileSaveMsg(""); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 40, borderBottom: `1px solid ${T.paperDark}`, paddingBottom: 32 }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.08em", marginBottom: 8 }}>Profile Pages</div>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 16, lineHeight: 1.6 }}>
                  When others visit your profile, show your previous Vouches expanded by default.
                </div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={expandPreviousVouches} onChange={e => saveExpandPreviousVouches(e.target.checked)} style={{ marginTop: 3, accentColor: T.ink, width: 16, height: 16, flexShrink: 0 }} />
                  <span style={{ fontFamily: "'Spectral',serif", fontSize: 14, lineHeight: 1.5 }}>Expand previous Vouches on my profile</span>
                </label>
              </div>
              <div style={{ marginBottom: 40, borderBottom: `1px solid ${T.paperDark}`, paddingBottom: 32 }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.08em", marginBottom: 8 }}>Music App</div>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 16, lineHeight: 1.6 }}>Choose where music tiles open.</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {["spotify", "apple_music"].map(pref => (
                    <button key={pref} onClick={() => setMusicPreference(pref)} style={{ flex: 1, padding: "10px", fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.14em", border: `1px solid ${musicPreference === pref ? T.ink : T.paperDark}`, background: musicPreference === pref ? T.ink : "transparent", color: musicPreference === pref ? T.bg : T.inkMid, cursor: "pointer" }}>
                      {pref === "spotify" ? "Spotify" : "Apple Music"}
                    </button>
                  ))}
                </div>
                <button className="btn btn-solid" style={{ width: "100%" }} onClick={async () => { musicPrefRef.current = musicPreference; await supabase.from("profiles").update({ music_preference: musicPreference }).eq("id", userId); alert("Saved!"); }}>Save Music Preference</button>
              </div>
              <div style={{ marginBottom: 40 }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.08em", marginBottom: 8 }}>My Shelf Categories</div>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 20, lineHeight: 1.6 }}>
                  Choose which categories appear on your shelf.
                </div>
                <CategoryPicker selected={userCategories || CATEGORIES.map(c => c.key)} all={CATEGORIES} onSave={saveCategories} />
              </div>
              <div id="contact-form" style={{ borderTop: `1px solid ${T.paperDark}`, paddingTop: 28, marginTop: 28 }}>
                <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.08em", marginBottom: 8 }}>Contact & Feedback</div>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 16, lineHeight: 1.6 }}>Got feedback, a bug to report, or need help? Send a note.</div>
                <ContactForm userId={userId} userEmail={user?.username} />
              </div>
            </div>
          )}

          {tab === "board" && !viewing && pendingIn.length > 0 && (
            <div onClick={() => setTab("friends")} style={{ background: T.ink, color: T.bg, padding: "12px 16px", marginBottom: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14 }}>
                <strong style={{ fontStyle: "normal", fontFamily: "'Spectral SC',serif", fontSize: 11, letterSpacing: "0.12em" }}>{pendingIn.length} buddy request{pendingIn.length > 1 ? "s" : ""}</strong> waiting for you
              </div>
              <span style={{ fontFamily: "'Spectral SC',serif", fontSize: 10, letterSpacing: "0.12em" }}>Review →</span>
            </div>
          )}

          {tab === "friends" && !viewing
            ? <>
                <div style={{ padding: "30px 0 18px", borderBottom: `1px solid ${T.ink}`, marginBottom: 32 }}>
                  <div className="board-name" style={{ fontSize: 32, marginBottom: 4 }}>Buddies</div>
                  <div className="board-sub" style={{ marginBottom: 16 }}>{buddies.length} connection{buddies.length !== 1 ? "s" : ""}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowBuddyList(true)}>View Buddies ({buddies.length})</button>
                    <button className="btn btn-solid" style={{ flex: 1 }} onClick={() => setBuddyModal(true)}>+ Add Buddies</button>
                  </div>
                </div>
                {/* WELCOME NOTIFICATION - first buddy */}
                {newBuddies.length > 0 && newBuddies.includes("Christian Wallis") && (
                  <div style={{ marginBottom: 24, border: `2px solid ${T.ink}`, padding: "16px 18px" }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.18em", color: T.ink, marginBottom: 14, fontWeight: 700 }}>Your First Buddy</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => { setViewing({ userId: "bd7a4b83-c56c-438a-8ad0-d188f810fe70", username: "ctanner.wallis", displayName: "Christian Wallis", avatarUrl: null }); setTab("board"); loadViewBoard("bd7a4b83-c56c-438a-8ad0-d188f810fe70"); loadBoardReactions("bd7a4b83-c56c-438a-8ad0-d188f810fe70", true); window.scrollTo(0,0); }}>
                        <Avatar name="Christian Wallis" size={40} avatarUrl={null} />
                        <div>
                          <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 15 }}>Christian Wallis</div>
                          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: T.inkLight }}>@ctanner.wallis · Creator</div>
                        </div>
                      </div>
                      <button onClick={() => setNewBuddies([])} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: T.inkFaint, background: "transparent", border: "none", cursor: "pointer" }}>Dismiss</button>
                    </div>
                  </div>
                )}
                {/* PENDING REQUESTS - top of page */}
                {pendingIn.length > 0 && (
                  <div style={{ marginBottom: 24, border: `2px solid ${T.ink}`, padding: "16px 18px" }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.18em", color: T.ink, marginBottom: 14, fontWeight: 700 }}>
                      {pendingIn.length} Buddy Request{pendingIn.length !== 1 ? "s" : ""}
                    </div>
                    {pendingIn.map(b => (
                      acceptedBuddies.includes(b.buddyRowId)
                        ? <div key={b.buddyRowId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar name={b.displayName} size={40} avatarUrl={b.avatarUrl} />
                              <div>
                                <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 15 }}>{b.displayName}</div>
                                <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: T.inkLight }}>@{b.username}</div>
                                {buddies.filter(x => false).length > 0 && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight }}>mutual buddies</div>}
                              </div>
                            </div>
                            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: "#4a7c59", fontWeight: 700 }}>✓ Added</div>
                          </div>
                        : <div key={b.buddyRowId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar name={b.displayName} size={40} avatarUrl={b.avatarUrl} />
                              <div>
                                <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 15 }}>{b.displayName}</div>
                                <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: T.inkLight }}>@{b.username}</div>
                                {buddies.filter(x => false).length > 0 && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight }}>mutual buddies</div>}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button className="btn btn-solid" style={{ padding: "5px 14px" }} onClick={() => { acceptBuddy(b.buddyRowId); setAcceptedBuddies(prev => [...prev, b.buddyRowId]);
                          const buddyNotif = { type: "buddy", display_name: b.displayName, date: new Date().toISOString() };
                          setPastNotifications(prev => { const updated = [buddyNotif, ...prev].slice(0,50); localStorage.setItem("vouch-past-notifs-" + userId, JSON.stringify(updated)); return updated; }); }}>Accept</button>
                              <button className="btn btn-ghost" style={{ padding: "5px 14px" }} onClick={() => removeBuddy(b.buddyRowId)}>Decline</button>
                            </div>
                          </div>
                    ))}
                  </div>
                )}


                {/* GROUP VOUCH - top of page */}
                {groupVouchItems.length > 0 && (
                  <GroupVouchSlideshow items={groupVouchItems} isMobile={isMobileGlobal} onAddToQueue={addToQueue} queue={queue} onDudeSame={dudeSame} onMusicOpen={openMusicUrl} />
                )}

                <BuddiesBin allBuddyBoards={allBuddyBoards} buddies={buddies} onViewBuddy={(buddy) => { setViewing(buddy); setTab("board"); loadViewBoard(buddy.userId); loadBoardReactions(buddy.userId, true); window.scrollTo(0,0); }} onAddToQueue={addToQueue} queue={queue} onDudeSame={dudeSame} myReactions={myReactions} userId={userId} onMusicOpen={openMusicUrl} />





              </>
            : (tab === "board" || viewing) ? <>
                <div style={{ marginBottom: 8, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, paddingTop: 16 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      {!viewing && (
                        <div onClick={() => setAvatarPicker(true)} style={{ cursor: "pointer", flexShrink: 0, position: "relative", display: "inline-block" }}>
                          <Avatar name={user.displayName} size={56} avatarUrl={user.avatarUrl} />
                          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(17,16,8,0.55)", fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.12em", color: "rgba(200,194,180,0.9)", textAlign: "center", padding: "3px 0", pointerEvents: "none" }}>edit</div>
                        </div>
                      )}
                      {viewing && viewing.avatarUrl && (
                        <div onClick={() => setAvatarLightbox(viewing.avatarUrl)} style={{ cursor: "zoom-in", flexShrink: 0 }}>
                          <Avatar name={viewing.displayName} size={56} avatarUrl={viewing.avatarUrl} />
                        </div>
                      )}
                      {viewing && !viewing.avatarUrl && (
                        <Avatar name={viewing.displayName} size={56} avatarUrl={null} />
                      )}
                      <div className="board-name" style={{ fontSize: 28, marginBottom: 0 }}>{viewing ? currName : currName}</div>
                    </div>
                    <div className="board-sub" style={{ marginBottom: 10 }}>@{viewing ? viewing.username : user.username}</div>
                    <ProfileBadgeBar badges={isOwn ? ownUserBadges : viewUserBadges} ownerName={isOwn ? "You" : currName} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.12em", color: T.inkLight }}>
                        <span style={{ fontWeight: 700, color: T.ink, fontSize: "12px", fontFamily: "'Spectral',serif" }}>{profileVouchCount}</span>
                        {" "}{profileVouchCount === 1 ? "vouch" : "vouches"}
                      </div>
                      {(isOwn ? buddies.length : viewBuddies.length) > 0 && (
                        <div onClick={() => setShowBuddyList(true)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.12em", color: T.inkLight, cursor: "pointer" }}>
                          <span style={{ fontWeight: 700, color: T.ink, fontSize: "12px", fontFamily: "'Spectral',serif" }}>{isOwn ? buddies.length : viewBuddies.length}</span>
                          {" "}{(isOwn ? buddies.length : viewBuddies.length) === 1 ? "buddy" : "buddies"}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 4, flexShrink: 0 }}>
                    {viewing && viewing.userId !== userId && !buddies.find(b => b.userId === viewing.userId) && (() => {
                      const isSentToViewing = sentRequests.includes(viewing.userId);
                      return isSentToViewing
                        ? <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: T.inkFaint }}>Sent</span>
                        : <button onClick={() => { sendBuddyRequest(viewing.userId); setSentRequests(prev => [...prev, viewing.userId]); }} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", padding: "6px 14px", background: "transparent", color: T.ink, border: `1px solid ${T.ink}`, cursor: "pointer", whiteSpace: "nowrap" }}>+ Add Buddies</button>;
                    })()}
                    {!viewing && (
                      <button onClick={() => setShareModal(true)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", padding: "6px 14px", background: T.ink, color: T.bg, border: "none", cursor: "pointer", whiteSpace: "nowrap" }}>Share</button>
                    )}
                  </div>
                </div>
                {viewing && (
                  <div style={{ marginBottom: 16 }}>
                    <button className="btn btn-ghost" onClick={() => { setViewing(null); setTab("friends"); window.history.replaceState({}, "", "/"); scrollToTop(); }}>← Back to Buddies</button>
                  </div>
                )}
                <div className="ornament"><span>—</span><span>✦</span><span>—</span></div>

                {isOwn && (() => {
                  const hasDraft = (() => { try { const d = JSON.parse(localStorage.getItem("vouch-board-draft-v2") || "null"); return d && d.items && d.items.length > 0; } catch(e) { return false; } })();
                  return (
                    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                      <button onClick={() => { setEditingBoard(null); setBoardEditor(true); }} style={{ flex: 1, fontFamily: "'Spectral SC',serif", fontSize: "10px", fontWeight: 700, letterSpacing: "0.2em", padding: "14px", background: T.ink, color: T.bg, border: "none", cursor: "pointer" }}>
                        {hasDraft ? "Continue Draft" : "Create a New Vouch"}
                      </button>
                    </div>
                  );
                })()}

                {isOwn ? (
                  <div className={getVouchSectionClassName(boardThemeName(activeBoard))} style={{ marginBottom: 52 }}>
                    <div className="vouch-section-header" style={{ flexDirection: "column", alignItems: "stretch", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
                        <div className="vouch-section-label" style={getVouchThemeLabelStyle(boardThemeName(activeBoard))}>{boardThemeName(activeBoard)}</div>
                        {activeBoard && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                          <button onClick={() => setShareModal(true)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.14em", padding: "4px 12px", border: "none", background: "linear-gradient(180deg, #E0E0E0 0%, #C0C0C0 60%, #909090 100%)", color: T.ink, cursor: "pointer", fontWeight: 700 }}>Share</button>
                          <button onClick={() => { setEditingBoard(activeBoard); setBoardEditor(true); }} style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.14em", padding: "4px 12px", border: "1px solid rgba(200,194,180,0.25)", background: "transparent", color: "rgba(200,194,180,0.5)", cursor: "pointer" }}>Edit</button>
                          <button onClick={() => setRemoveVouchModal(true)} style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.14em", padding: "4px 12px", border: "1px solid rgba(200,194,180,0.25)", background: "transparent", color: "rgba(200,194,180,0.5)", cursor: "pointer" }}>Remove</button>
                        </div>}
                        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.4)", marginTop: activeBoard ? 10 : 3 }}>Vouch</div>
                        {activeBoard?.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 10, color: "rgba(200,194,180,0.4)", marginTop: 2 }}>{activeBoard.description}</div>}
                        {activeBoard?.published_at && <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.1em", color: "rgba(200,194,180,0.3)", marginTop: 4 }}>Published {new Date(activeBoard.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · Current Vouch</div>}
                      </div>
                    </div>
                    {activeBoard?.vouch_board_items?.length > 0 ? (
                      <VouchSection board={(() => {
                        const b = { movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [] };
                        (activeBoard.vouch_board_items || []).sort((a,b) => a.position - b.position).slice(0,5).forEach(item => {
                          if (b[item.category]) b[item.category].push(vouchItemToDisplay(item));
                        });
                        return b;
                      })()} isOwn={true} onCard={(k, i) => {}} onAdd={() => {}} onRemove={() => {}} onDudeSame={() => {}} myReactions={[]} hideHeader={true} onMusicOpen={openMusicUrl} itemBadges={ownItemBadges} badgeOwnerName={user.displayName} canCommentTiles={true} {...tileCommentProps} />
                    ) : (
                      <div style={{ height: 220, border: "1px dashed rgba(200,194,180,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, cursor: "pointer" }} onClick={() => { setEditingBoard(null); setBoardEditor(true); }}>
                        <span style={{ fontSize: 28, color: "rgba(200,194,180,0.4)" }}>+</span>
                        <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.4)" }}>Create Your Vouch</span>
                      </div>
                    )}
                  </div>
                ) : viewActiveBoard?.vouch_board_items?.length > 0 ? (
                  <div className={getVouchSectionClassName(boardThemeName(viewActiveBoard))} style={{ marginBottom: 52 }}>
                    <div className="vouch-section-header">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="vouch-section-label" style={getVouchThemeLabelStyle(boardThemeName(viewActiveBoard))}>{boardThemeName(viewActiveBoard)}</div>
                        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.18em", color: "rgba(200,194,180,0.4)", marginTop: 3 }}>Vouch</div>
                        {viewActiveBoard.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: "rgba(200,194,180,0.45)", marginTop: 5 }}>{viewActiveBoard.description}</div>}
                        {viewActiveBoard.published_at && <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.1em", color: "rgba(200,194,180,0.3)", marginTop: 4 }}>Published {new Date(viewActiveBoard.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>}
                      </div>
                    </div>
                    <VouchSection board={(() => {
                      const brd = { movies: [], albums: [], artists: [], songs: [], books: [], shows: [], podcasts: [] };
                      (viewActiveBoard.vouch_board_items || []).sort((a,x) => a.position - x.position).slice(0,5).forEach(item => {
                        if (brd[item.category]) brd[item.category].push(vouchItemToDisplay(item));
                      });
                      return brd;
                    })()} isOwn={false} onCard={(k,i)=>{}} onAdd={()=>{}} onRemove={()=>{}} onDudeSame={dudeSame} myReactions={myReactions.filter(r => viewing && r.item_owner_id === viewing.userId).map(r => r.item_id)} hideHeader={true} onAddToQueue={addToQueue} queue={queue} ownerId={viewing?.userId} onMusicOpen={openMusicUrl} itemBadges={viewItemBadges} badgeOwnerName={viewing?.displayName} canCommentTiles={isBuddyWith(viewing?.userId)} {...tileCommentProps} />
                  </div>
                ) : null}
                {viewing && !isOwn && (
                  <PreviousVouches key={viewing.userId} userId={viewing.userId} onDudeSame={dudeSame} myReactions={myReactions} queue={queue} onAddToQueue={addToQueue} onMusicOpen={openMusicUrl} defaultOpen={viewExpandPreviousVouches} tileComments={vouchTileComments} currentUserId={userId} onPostTileComment={postTileComment} onDeleteTileComment={deleteTileComment} />
                )}
                {isOwn && boardArchive.filter(b => !b.is_active && b.published_at).length > 0 && (
                  <OwnArchive boards={boardArchive} canPublish={canPublish} onRepublish={republishBoard} onMusicOpen={openMusicUrl} defaultOpen={expandPreviousVouches} tileComments={vouchTileComments} currentUserId={userId} onPostTileComment={postTileComment} onDeleteTileComment={deleteTileComment} onDelete={async (b) => { await revokeClaimsForBoard(b.id).catch(() => {}); await supabase.from("vouch_board_items").delete().eq("board_id", b.id); await supabase.from("vouch_boards").delete().eq("id", b.id); setBoardArchive(prev => prev.filter(x => x.id !== b.id)); loadBadgesForUser(userId).then(setOwnItemBadges).catch(() => {}); }} />
                )}

                {(() => {
                  const cats = isOwn
                    ? (userCategories ? CATEGORIES.filter(c => userCategories.includes(c.key)).sort((a,b) => userCategories.indexOf(a.key) - userCategories.indexOf(b.key)) : CATEGORIES)
                    : [...CATEGORIES].sort((a, b) => {
                        const aLen = (currBoard[a.key] || []).length;
                        const bLen = (currBoard[b.key] || []).length;
                        return bLen - aLen;
                      });
                  const visibleCats = cats.filter(cat => isOwn || (currBoard[cat.key] || []).length > 0);
                  if (visibleCats.length === 0) return null;
                  return <>
                    <div style={{ marginBottom: 28, borderBottom: `2px solid ${T.ink}`, paddingBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ fontFamily: "'Spectral SC', serif", fontWeight: 700, fontSize: 32, color: T.ink, letterSpacing: "0.08em" }}>
                          {isOwn ? (shelfView === "queue" ? "My Queue" : "My Shelf") : `${(currName || "").split(" ")[0]}'s Shelf`}
                        </div>
                        {isOwn && (
                          <div style={{ display: "flex", gap: 0, border: `1px solid ${T.ink}`, flexShrink: 0 }}>
                            <button onClick={() => setShelfView("shelf")} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.14em", padding: "5px 12px", background: shelfView === "shelf" ? T.ink : "transparent", color: shelfView === "shelf" ? T.bg : T.inkMid, border: "none", cursor: "pointer" }}>Shelf</button>
                            <button onClick={() => setShelfView("queue")} style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.14em", padding: "5px 12px", background: shelfView === "queue" ? T.ink : "transparent", color: shelfView === "queue" ? T.bg : T.inkMid, border: "none", cursor: "pointer" }}>Queue {queue.length > 0 ? `(${queue.length})` : ""}</button>
                          </div>
                        )}
                      </div>
                      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkLight, marginTop: 3 }}>
                        {isOwn
                          ? shelfView === "queue"
                            ? "Things you want to get to — add from buddy shelves and Group Shelf."
                            : "The stuff on your shelf — films, albums, books, shows worth putting your name behind."
                          : `What ${(currName || "").split(" ")[0]} has on their shelf.`}
                      </div>
                    </div>
                    {isOwn && shelfView === "queue" ? (
                      queue.length === 0
                        ? <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkLight, padding: "24px 0" }}>Nothing in your queue yet — hit "Add to Queue" on any buddy's shelf or Group Shelf tile.</div>
                        : <div>
                            {CATEGORIES.filter(cat => queue.some(q => q.category === cat.key)).map(cat => {
                              const catItems = queue.filter(q => q.category === cat.key);
                              return (
                                <div key={cat.key} className="cat-section">
                                  <div className="cat-header">
                                    <div className="cat-label">{cat.label}</div>
                                    <div className="cat-count">{catItems.length}</div>
                                  </div>
                                  <div className="cards-row">
                                    {catItems.map(item => (
                                      <div key={item.id} className="card" style={{ position: "relative" }} onClick={() => openTileLink(item, { catKey: item.category, onMusicOpen: openMusicUrl })}>
                                        <button onClick={e => { e.stopPropagation(); removeFromQueue(item.id); }} style={{ position: "absolute", top: 4, right: 4, zIndex: 3, background: "rgba(17,16,8,0.85)", border: "none", color: "#C8C2B4", width: 26, height: 26, cursor: "pointer", fontSize: 15, lineHeight: "26px", textAlign: "center", borderRadius: 2 }}>×</button>
                                        <TileMedia
                                          item={item}
                                          catKey={item.category}
                                          onOpen={() => openTileLink(item, { catKey: item.category, onMusicOpen: openMusicUrl })}
                                          poster={item.poster}
                                          title={item.title}
                                          className="card-poster"
                                          badgeSize="sm"
                                        >
                                          {item.poster
                                            ? <img src={item.poster} alt={item.title} className="card-poster" onError={e => e.target.style.display = "none"} />
                                            : <div className="card-poster-placeholder">{item.title}</div>}
                                        </TileMedia>
                                        <div className="card-title">{item.title}</div>
                                        {item.sub && <div className="card-sub">{item.sub}</div>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                    ) : (
                      visibleCats.map(cat => {
                        const items = currBoard[cat.key] || [];
                        return <CatSection key={cat.key} catKey={cat.key} label={cat.label} items={items} isOwn={isOwn} onCard={(k, i) => setLightbox({ catKey: k, idx: i })} onAdd={(key) => {  setAddModal(key); }} onRemove={removeItem} onDudeSame={dudeSame} myReactions={myReactions.filter(r => viewing && r.item_owner_id === viewing.userId).map(r => r.item_id)} onAddToQueue={addToQueue} queue={queue} onMusicOpen={openMusicUrl} itemBadges={isOwn ? ownItemBadges : viewItemBadges} badgeOwnerName={currName} />;
                      })
                    )}
                  </>;
                })()}

                <MutualMentions reactions={isOwn ? boardReactions : viewerReactions} myReactions={myReactions} isOwn={isOwn} boardOwnerName={currName} buddies={buddies} onViewBuddy={(b) => { setViewing(b); setTab("board"); loadViewBoard(b.userId); loadBoardReactions(b.userId, true); window.scrollTo(0, 0); }} />


              </>
            : null
          }
        </main>

        {lightbox && (() => {
          const items = currBoard[lightbox.catKey] || [];
          if (!items.length) return null;
          return <Lightbox items={items} start={lightbox.idx} catLabel={CATEGORIES.find(c => c.key === lightbox.catKey)?.label} onClose={() => setLightbox(null)} />;
        })()}

        {buddyModal && (
          <BuddyModal userId={userId} onClose={() => { setBuddyModal(false); setInviteLink(null); }} onSendRequest={sendBuddyRequest} onGenerateLink={generateInviteLink} inviteLink={inviteLink} existingBuddyIds={buddies.map(b => b.userId)} onViewProfile={(r) => { setBuddyModal(false); setViewing({ userId: r.id, username: r.username, displayName: r.display_name, avatarUrl: r.avatar_url }); setTab("board"); loadViewBoard(r.id); loadBoardReactions(r.id, true); window.scrollTo(0,0); }} />
        )}

        {vouchModal && (
          <UniversalSearchModal
            used={vouchedCount}
            onClose={() => setVouchModal(false)}
            onAdd={(catKey, item) => {
              return addItem(catKey, { ...item, vouched: true });
            }}
          />
        )}

        {addModal && (
          <AddModal catKey={addModal} catLabel={CATEGORIES.find(c => c.key === addModal)?.label} used={(board[addModal] || []).filter(i => !i.vouched).length} onClose={() => setAddModal(null)} onAdd={addItem} />
        )}

        {legalPage === "how" && <LegalModal onClose={() => setLegalPage(null)} />}

        {showBuddyList && (
          <div className="modal-overlay" onClick={() => setShowBuddyList(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">{isOwn ? "Your Buddies" : currName + "'s Buddies"}</div>
                <button className="modal-x" onClick={() => setShowBuddyList(false)}>×</button>
              </div>
              <div className="modal-body">
                <input className="search-input" placeholder="Search buddies…" value={buddySearch || ""} onChange={e => setBuddySearch(e.target.value)} style={{ marginBottom: 14 }} />
                {(isOwn ? buddies : viewBuddies).length === 0 && (
                  <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight }}>No buddies yet.</div>
                )}
                {(isOwn ? buddies.map(b => ({ id: b.userId, display_name: b.displayName, username: b.username, avatar_url: b.avatarUrl, buddyRowId: b.buddyRowId })) : viewBuddies).filter(b => !buddySearch || (b.display_name || b.displayName || "").toLowerCase().includes(buddySearch.toLowerCase()) || (b.username || "").toLowerCase().includes(buddySearch.toLowerCase())).map((b, i) => {
                  const bid = b.id || b.userId;
                  const bname = b.display_name || b.displayName;
                  const buser = b.username;
                  const bavatar = b.avatar_url || b.avatarUrl;
                  const isAlreadyBuddy = buddies.find(x => x.userId === bid);
                  const isSelf = bid === userId;
                  const isSent = sentRequests.includes(bid);
                  return (
                    <div key={bid || i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => { setShowBuddyList(false); setViewing({ userId: bid, username: buser, displayName: bname, avatarUrl: bavatar }); setTab("board"); loadViewBoard(bid); loadBoardReactions(bid, true); window.scrollTo(0, 0); }}>
                        <Avatar name={bname} size={56} avatarUrl={bavatar} />
                        <div>
                          <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 15, borderBottom: `1px solid ${T.paperDark}` }}>{bname}</div>
                          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.1em", color: T.inkLight }}>@{buser}</div>
                        </div>
                      </div>
                      {!isSelf && !isAlreadyBuddy && (
                        isSent
                          ? <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.12em", color: T.inkFaint }}>Sent</span>
                          : <button className="btn btn-solid" style={{ padding: "4px 12px" }} onClick={() => { sendBuddyRequest(bid); setSentRequests(prev => [...prev, bid]); }}>Add</button>
                      )}
                      {isAlreadyBuddy && <span style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.12em", color: T.inkFaint }}>Buddies</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <IOSInstallBanner />

        {avatarLightbox && (
          <div onClick={() => setAvatarLightbox(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}>
            <img src={avatarLightbox} alt="avatar" style={{ width: "min(90vw, 90vh)", height: "min(90vw, 90vh)", objectFit: "cover", filter: "grayscale(100%)" }} onClick={e => e.stopPropagation()} />
          </div>
        )}

        {avatarPicker && (
          <div className="modal-overlay" onClick={() => setAvatarPicker(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Choose Your Avatar</div>
                <button className="modal-x" onClick={() => setAvatarPicker(false)}>×</button>
              </div>
              <div className="modal-body">

                {/* Upload own photo */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9.5px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>Upload a Photo</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                    <div style={{ width: 56, height: 56, background: T.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {user.avatarUrl
                        ? <img src={user.avatarUrl} alt="avatar" style={{ width: 56, height: 56, objectFit: "cover", filter: "grayscale(100%)" }} />
                        : <span style={{ fontFamily: "'Times New Roman',serif", fontWeight: 900, fontSize: 20, color: T.bg }}>{(user.displayName || "").split(" ").map(w => w[0]).join("").slice(0,2)}</span>
                      }
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Spectral',serif", fontSize: 13, color: T.ink, borderBottom: `1px solid ${T.paperDark}` }}>Choose from camera roll</div>
                      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, marginTop: 4 }}>JPG or PNG</div>
                    </div>
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { uploadAvatar(e); }} />
                  </label>
                </div>

                <div style={{ borderBottom: `1px solid ${T.paperDark}`, marginBottom: 16 }} />

                {/* Stock photo options */}
                <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9.5px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>Or Pick One</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                  {AVATAR_OPTIONS.map(av => (
                    <div key={av.file} onClick={() => saveAvatar(av.file)} style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <img
                        src={`/avatars/${av.file}.jpg`}
                        alt={av.label}
                        style={{ width: "100%", aspectRatio: "1", objectFit: "cover", border: user.avatarUrl === `/avatars/${av.file}.jpg` ? `2px solid ${T.ink}` : `1px solid ${T.paperDark}` }}
                      />
                      <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "7px", letterSpacing: "0.1em", color: T.inkMid, textAlign: "center" }}>{av.label}</div>
                    </div>
                  ))}
                </div>

              </div>
            </div>
          </div>
        )}

        {boardEditor && (
          <BoardEditorModal
            onClose={() => { setBoardEditor(false); setEditingBoard(null); }}
            onPublish={publishBoard}
            existing={editingBoard}
            categories={CATEGORIES}
            themes={getTimedBoardThemes()}
            userId={userId}
            canPublish={canPublish}
            nextPublishDate={nextPublishDate}
          />
        )}

        {archivePage && (
          <div className="modal-overlay" onClick={() => setArchivePage(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
              <div className="modal-head">
                <div className="modal-title">Your Vouch Archive</div>
                <button className="modal-x" onClick={() => setArchivePage(false)}>×</button>
              </div>
              <div className="modal-body" style={{ overflowY: "auto", flex: 1 }}>
                {boardArchive.length === 0 && <div style={{ fontFaily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight }}>No archived boards yet.</div>}
                {(() => {
                  const grouped = {};
                  boardArchive.forEach(b => {
                    const d = b.published_at ? new Date(b.published_at) : null;
                    const key = d ? `${d.toLocaleString("en-US", { month: "long" })} ${d.getFullYear()}` : "Unpublished";
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(b);
                  });
                  return Object.entries(grouped).map(([monthYear, boards]) => (
                    <div key={monthYear} style={{ marginBottom: 32 }}>
                      <div style={{ fontFamily: "'Spectral SC',serif", fontWeight: 700, fontSize: 10, letterSpacing: "0.18em", color: T.inkMid, borderBottom: `1px solid ${T.paperDark}`, paddingBottom: 8, marginBottom: 16 }}>{monthYear}</div>
                      {boards.map(b => (
                        <div key={b.id} style={{ marginBottom: 24 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                            <div>
                              <div style={{ fontFamily: "'Times New Roman',Times,serif", fontWeight: 900, fontSize: 20, color: T.ink }}>{(b.theme && b.theme !== "Other") ? b.theme : (b.name || "Untitled Vouch")}</div>
                              <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.12em", color: T.inkLight, marginTop: 2 }}>
                                {b.published_at ? new Date(b.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                                {b.is_active && <span style={{ marginLeft: 8, color: "#c9a820", fontWeight: 700 }}>· Active</span>}
                              </div>
                              {b.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fntSize: 11, color: T.inkMid, marginTop: 3 }}>{b.description}</div>}
                            </div>
                            {!b.is_active && (
                              <button className="btn btn-solid" style={{ padding: "4px 12px", fontSize: 10, flexShrink: 0, opacity: canPublish ? 1 : 0.35, cursor: canPublish ? "pointer" : "not-allowed" }} onClick={() => { if (canPublish) { republishBoard(b); setArchivePage(false); } }}>
                                {canPublish ? "Republish" : "Locked"}
                              </button>
                            )}
                          </div>
                          {b.vouch_board_items?.length > 0 && (
                            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                              {b.vouch_board_items.sort((a,x) => a.position - x.position).slice(0,5).map((item, i, arr) => (
                                <ArchiveTile key={i} item={item} onMusicOpen={openMusicUrl} itemCount={arr.length} compact style={{ width: 70 }} />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}
        {showNotifications && (
          <div className="modal-overlay" onClick={() => setShowNotifications(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Notifications</div>
                <button className="modal-x" onClick={() => setShowNotifications(false)}>×</button>
              </div>
              <div className="modal-body">
                {newAgreements.length === 0 && pendingIn.length === 0 && newCommentNotifs.length === 0 && newBuddies.filter(n => n !== "Christian Wallis").length === 0 && (
                  <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight }}>No new notifications.</div>
                )}
                {newBuddies.filter(n => n !== "Christian Wallis").length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>New Connections</div>
                    {newBuddies.filter(n => n !== "Christian Wallis").map((name, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                        <div style={{ fontFamily: "'Spectral',serif", fontSize: 14 }}>
                          <strong>{name}</strong> <span style={{ fontStyle: "italic", color: T.inkMid }}>and you are now buddies</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {pendingIn.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>Buddy Requests</div>
                    {pendingIn.map(b => (
                      <div key={b.buddyRowId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar name={b.displayName} size={40} avatarUrl={b.avatarUrl} />
                          <div>
                            <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 14 }}>{b.displayName}</div>
                            <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: T.inkLight }}>wants to connect</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn btn-solid" style={{ padding: "4px 10px" }} onClick={() => { acceptBuddy(b.buddyRowId); setAcceptedBuddies(prev => [...prev, b.buddyRowId]);
                          const buddyNotif = { type: "buddy", display_name: b.displayName, date: new Date().toISOString() };
                          setPastNotifications(prev => { const updated = [buddyNotif, ...prev].slice(0,50); localStorage.setItem("vouch-past-notifs-" + userId, JSON.stringify(updated)); return updated; }); }}>Accept</button>
                          <button className="btn btn-ghost" style={{ padding: "4px 10px" }} onClick={() => removeBuddy(b.buddyRowId)}>Decline</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {newCommentNotifs.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>Comments</div>
                    {newCommentNotifs.map((r, i) => (
                      <div key={r.id || i} onClick={() => openCommentNotification(r)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.paperDark}`, cursor: "pointer" }}>
                        <div style={{ width: 36, height: 36, background: T.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span style={{ fontFamily: "'Times New Roman',serif", fontWeight: 900, fontSize: 13, color: T.bg }}>{(r.display_name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}</span>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 14 }}>{r.display_name}</div>
                          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkMid, lineHeight: 1.45 }}>
                            {r.on_own_vouch
                              ? <>commented on <strong style={{ fontStyle: "normal" }}>{r.title}</strong></>
                              : <>replied on <strong style={{ fontStyle: "normal" }}>{r.title}</strong> on {r.board_owner_name?.split(" ")[0]}'s vouch</>}
                          </div>
                          {r.body && <div style={{ fontFamily: "'Spectral',serif", fontSize: 11, color: T.inkLight, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>"{r.body}"</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {newAgreements.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>New Agrees</div>
                    {newAgreements.map((r, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                        <div style={{ width: 36, height: 36, background: T.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span style={{ fontFamily: "'Times New Roman',serif", fontWeight: 900, fontSize: 13, color: T.bg }}>{(r.display_name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}</span>
                        </div>
                        <div>
                          <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 14 }}>{r.display_name}</div>
                          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkMid }}>agreed with <strong style={{ fontStyle: "normal" }}>{r.title}</strong></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(newAgreements.length > 0 || newCommentNotifs.length > 0 || newBuddies.filter(n => n !== "Christian Wallis").length > 0) && (
                  <button className="btn btn-ghost" style={{ width: "100%", marginTop: 12 }} onClick={dismissAllNotifications}>Dismiss All</button>
                )}
                {pastNotifications.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkFaint, marginBottom: 10, borderTop: `1px solid ${T.paperDark}`, paddingTop: 16 }}>Previous</div>
                    {pastNotifications.slice(0, 20).map((n, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                        <div style={{ fontFamily: "'Spectral',serif", fontSize: 12, color: T.inkMid }}>
                          <strong style={{ color: T.ink }}>{n.display_name}</strong>
                          {n.type === "agree" && <span style={{ fontStyle: "italic" }}> agreed with <strong style={{ fontStyle: "normal" }}>{n.title}</strong></span>}
                          {n.type === "comment" && (
                            <span style={{ fontStyle: "italic" }}>
                              {n.on_own_vouch
                                ? <> commented on <strong style={{ fontStyle: "normal" }}>{n.title}</strong></>
                                : <> replied on <strong style={{ fontStyle: "normal" }}>{n.title}</strong> on {n.board_owner_name?.split(" ")[0]}'s vouch</>}
                              {n.body && <span style={{ color: T.inkLight }}> — "{String(n.body).slice(0, 60)}{String(n.body).length > 60 ? "…" : ""}"</span>}
                            </span>
                          )}
                          {n.type === "buddy" && <span style={{ fontStyle: "italic" }}> added you as a buddy</span>}
                        </div>
                      </div>
                    ))}
                    <button onClick={() => { setPastNotifications([]); localStorage.removeItem("vouch-past-notifs-" + userId); }} style={{ fontFamily: "'Spectral SC',serif", fontSize: "8px", letterSpacing: "0.15em", background: "transparent", border: "none", color: T.inkFaint, cursor: "pointer", marginTop: 8, padding: 0 }}>Clear History</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {showShareNudge && (
          <div className="modal-overlay" onClick={() => setShowShareNudge(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Share Your Vouch</div>
                <button className="modal-x" onClick={() => setShowShareNudge(false)}>×</button>
              </div>
              <div className="modal-body" style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkMid, marginBottom: 16 }}>Your Vouch is live — share it with your circle.</div>
                {/* Share card preview */}
                <div style={{ background: T.ink, color: T.bg, marginBottom: 16, position: "relative", overflow: "hidden", ...vouchShareBorderStyle(boardThemeName(activeBoard)) }}>
                  {(() => {
                    const shareTheme = boardThemeName(activeBoard);
                    const coverItem = (activeBoard?.vouch_board_items || []).sort((a,b) => a.position - b.position)[0];
                    return coverItem?.poster ? (
                      <div style={{ position: "relative" }}>
                        <img src={coverItem.poster} alt={coverItem.title} style={{ width: "100%", aspectRatio: "2/3", objectFit: "cover", display: "block", maxHeight: 280 }} onError={e => e.target.style.display="none"} />
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(to top, rgba(17,16,8,0.95) 0%, rgba(17,16,8,0) 100%)", padding: "40px 16px 16px" }}>
                          <div style={{ fontFamily: "'Times New Roman',serif", fontWeight: 900, fontSize: 22, color: "#C8C2B4", ...getVouchThemeLabelStyle(shareTheme) }}>{shareTheme}</div>
                          <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: "rgba(200,194,180,0.6)" }}>vouched by {user?.displayName}</div>
                        </div>
                        <div style={{ position: "absolute", top: 8, left: 8, fontFamily: "'Spectral SC',serif", fontSize: 8, letterSpacing: "0.2em", color: "rgba(200,194,180,0.5)" }}>EST. 2026 · VOUCH</div>
                      </div>
                    ) : (
                      <div style={{ padding: "24px 20px" }}>
                        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: 9, letterSpacing: "0.3em", color: "rgba(200,194,180,0.4)", marginBottom: 8 }}>EST. 2026 · VOUCH</div>
                        <div style={{ fontFamily: "'Times New Roman',serif", fontWeight: 900, fontSize: 26, color: "#C8C2B4", marginBottom: 4, ...getVouchThemeLabelStyle(shareTheme) }}>{shareTheme}</div>
                        <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: "rgba(200,194,180,0.5)" }}>vouched by {user?.displayName}</div>
                      </div>
                    );
                  })()}
                  <div style={{ padding: "8px 16px", fontFamily: "'Spectral SC',serif", fontSize: 8, letterSpacing: "0.2em", color: "rgba(200,194,180,0.35)" }}>{window.location.origin}/@{user?.username}</div>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button className="btn btn-solid" style={{ flex: 1, padding: "12px", fontSize: "10px", letterSpacing: "0.15em" }} onClick={() => { navigator.clipboard?.writeText(window.location.origin + "/@" + user?.username); alert("Link copied!"); }}>Copy Link</button>
                  {shareCardUrl && (
                    <div style={{ marginBottom: 16, textAlign: "center" }}>
                      <img src={shareCardUrl} alt="Share card preview" style={{ width: "100%", maxWidth: 320, border: `1px solid ${T.paperDark}`, display: "block", margin: "0 auto" }} />
                    </div>
                  )}
                  <button className="btn btn-ghost" style={{ flex: 1, padding: "12px", fontSize: "10px", letterSpacing: "0.15em" }} onClick={() => { shareBoard(); }}>Download Card</button>
                </div>
                {navigator.share && <button className="btn btn-ghost" style={{ width: "100%", padding: "12px", fontSize: "10px", letterSpacing: "0.15em" }} onClick={() => { navigator.share({ title: "Check out my Vouch", url: window.location.origin + "/@" + user?.username }); setShowShareNudge(false); }}>Share via...</button>}
              </div>
            </div>
          </div>
        )}
        {shelfExtras && (
          <div className="modal-overlay" onClick={() => setShelfExtras(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Also Added to Shelf</div>
                <button className="modal-x" onClick={() => setShelfExtras(null)}>×</button>
              </div>
              <div className="modal-body">
                {shelfExtras.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.paperDark}`, cursor: tileIsClickable(s, s.category) ? "pointer" : "default" }} onClick={() => openTileLink(s, { catKey: s.category, onMusicOpen: openMusicUrl })}>
                    <TileMedia
                      item={s}
                      catKey={s.category}
                      onOpen={() => openTileLink(s, { catKey: s.category, onMusicOpen: openMusicUrl })}
                      poster={s.poster}
                      title={s.title}
                      badgeSize="sm"
                      style={{ width: 48, flexShrink: 0 }}
                    >
                      {s.poster
                        ? <img src={s.poster} alt={s.title} style={{ width: 48, height: 66, objectFit: "cover", border: `1px solid ${T.paperDark}`, display: "block" }} onError={e => e.target.style.display="none"} />
                        : <div style={{ width: 48, height: 66, background: T.paperDark, flexShrink: 0 }} />}
                    </TileMedia>
                    <div>
                      <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 14 }}>{s.title}</div>
                      {s.subtitle && <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", color: T.inkLight, marginTop: 2 }}>{s.subtitle}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {showAgreements && (
          <div className="modal-overlay" onClick={() => setShowAgreements(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">New Agreements</div>
                <button className="modal-x" onClick={() => setShowAgreements(false)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkLight, marginBottom: 16 }}>Since your last visit</div>
                {newAgreements.map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.paperDark}` }}>
                    <div style={{ width: 36, height: 36, background: T.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontFamily: "'Times New Roman',serif", fontWeight: 900, fontSize: 13, color: T.bg }}>{(r.display_name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}</span>
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Spectral',serif", fontWeight: 600, fontSize: 14 }}>{r.display_name.split(" ")[0]} {r.display_name.split(" ")[1]?.[0]}.</div>
                      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 12, color: T.inkMid, marginTop: 1 }}>agreed with <strong style={{ fontStyle: "normal" }}>{r.title}</strong></div>
                    </div>
                  </div>
                ))}
                <button className="btn btn-ghost" style={{ width: "100%", marginTop: 16 }} onClick={() => { setShowAgreements(false); setNewAgreements([]); }}>Dismiss</button>
              </div>
            </div>
          </div>
        )}

        {onboarding && (
          <div className="modal-overlay">
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head"><div className="modal-title">Welcome to Vouch5</div></div>
              <div className="modal-body">
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkMid, marginBottom: 24, lineHeight: 1.7 }}>
                  Pick which categories you want on your shelf and put them in the order that feels right for you.
                </div>
                <CategoryPicker selected={userCategories || CATEGORIES.map(c => c.key)} all={CATEGORIES} onSave={saveCategories} isOnboarding={true} />
              </div>
            </div>
          </div>
        )}

        {boardEditor && (
          <BoardEditorModal
            onClose={() => { setBoardEditor(false); setEditingBoard(null); }}
            onPublish={publishBoard}
            existing={editingBoard}
            categories={CATEGORIES}
            themes={getTimedBoardThemes()}
            userId={userId}
            canPublish={canPublish}
            nextPublishDate={nextPublishDate}
          />
        )}

        {editingMeta && activeBoard && (
          <div className="modal-overlay" onClick={() => setEditingMeta(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Edit Vouch Details</div>
                <button className="modal-x" onClick={() => setEditingMeta(false)}>×</button>
              </div>
              <EditMetaForm board={activeBoard} themes={getTimedBoardThemes()} onSave={async (updates) => {
                await supabase.from("vouch_boards").update(updates).eq("id", activeBoard.id);
                await loadVouchBoards(userId);
                setEditingMeta(false);
              }} onClose={() => setEditingMeta(false)} />
            </div>
          </div>
        )}

        {archivePage && (
          <div className="modal-overlay" onClick={() => setArchivePage(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Your Vouch Archive</div>
                <button className="modal-x" onClick={() => setArchivePage(false)}>×</button>
              </div>
              <div className="modal-body">
                {boardArchive.length === 0 && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight }}>No archived boards yet.</div>}
                {boardArchive.map(b => (
                  <div key={b.id} style={{ borderBottom: `1px solid ${T.paperDark}`, padding: "14px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <div>
                        <div style={{ fontFamily: "'Spectral',serif", fontWeight: 700, fontSize: 15 }}>{b.name || "Untitled Vouch"}</div>
                        <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.12em", color: T.inkLight, marginTop: 2 }}>
                          {b.theme}{b.published_at ? ` · ${new Date(b.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                          {b.is_active && <span style={{ marginLeft: 8, color: T.ink, fontWeight: 700 }}>· Active</span>}
                        </div>
                        {b.description && <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkMid, marginTop: 3 }}>{b.description}</div>}
                      </div>
                      {!b.is_active && canPublish && (
                        <button className="btn btn-solid" style={{ padding: "4px 12px", fontSize: 10 }} onClick={() => { republishBoard(b); setArchivePage(false); }}>Republish</button>
                      )}
                    </div>
                    {b.vouch_board_items?.length > 0 && (
                      <div style={{ display: "flex", gap: 6 }}>
                        {b.vouch_board_items.sort((a,b) => a.position - b.position).slice(0,5).map((item, i, arr) => (
                          <ArchiveTile key={i} item={item} onMusicOpen={openMusicUrl} itemCount={arr.length} compact style={{ width: 70 }} titleBelow={false} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {shareModal && (() => {
          const shareUsername = user.username;
          const shareUrl = `${window.location.origin}/@${shareUsername}`;
          const shareBoardData = resolveShareBoard();
          const hasShareContent = !!shareBoardData?.vouch_board_items?.length;
          return (
            <div className="modal-overlay" onClick={closeShareModal}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: "90vh" }}>
                <div className="modal-head">
                  <div className="modal-title">{activeBoard?.published_at ? "Your Vouch is Live! Share it." : "Share Your Vouch"}</div>
                  <button className="modal-x" onClick={closeShareModal}>×</button>
                </div>
                <div className="modal-body">

                  {/* Story card preview — generated on open */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9.5px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 10 }}>Your Story Card</div>
                    {!hasShareContent ? (
                      <div style={{ padding: "24px 16px", textAlign: "center", background: T.paperDark, border: `1px solid ${T.paperDark}` }}>
                        <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkMid, marginBottom: 12, lineHeight: 1.6 }}>Add tiles to a Vouch first — then your story card will appear here.</div>
                        <button className="btn btn-solid" style={{ padding: "10px 16px", fontSize: 10 }} onClick={() => { closeShareModal(); setEditingBoard(null); setBoardEditor(true); }}>Create a Vouch</button>
                      </div>
                    ) : shareCardGenerating ? (
                      <div style={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center", background: T.paperDark, border: `1px solid ${T.paperDark}` }}>
                        <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight }}>Generating preview…</div>
                      </div>
                    ) : shareCardUrl ? (
                      <img src={shareCardUrl} alt="Story card preview" style={{ width: "100%", maxWidth: 280, border: `1px solid ${T.paperDark}`, display: "block", margin: "0 auto" }} />
                    ) : (
                      <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", background: T.paperDark, border: `1px solid ${T.paperDark}` }}>
                        <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight }}>Preview unavailable</div>
                      </div>
                    )}
                  </div>

                  {hasShareContent && (
                    <>
                      <div style={{ display: "flex", gap: 8, marginBottom: prefersPhotoSave ? 8 : 24 }}>
                        <button className="btn btn-solid" style={{ flex: 1, padding: "12px", fontSize: 11, letterSpacing: "0.12em" }} disabled={shareCardGenerating || !shareCardUrl} onClick={shareCardNative}>
                          Share to Story
                        </button>
                        <button className="btn btn-ghost" style={{ flex: 1, padding: "12px", fontSize: 11, letterSpacing: "0.12em" }} disabled={shareCardGenerating || !shareCardUrl} onClick={saveShareCardToPhotos}>
                          {prefersPhotoSave ? "Save to Photos" : "Download"}
                        </button>
                      </div>
                      {prefersPhotoSave && (
                        <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, textAlign: "center", marginBottom: 24, lineHeight: 1.5 }}>
                          Tap Save to Photos, then choose <strong style={{ fontStyle: "normal" }}>Save Image</strong> in the menu.
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ borderBottom: `1px solid ${T.paperDark}`, marginBottom: 20 }} />

                  {/* Copy link section */}
                  <div>
                    <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9.5px", letterSpacing: "0.18em", color: T.inkMid, marginBottom: 8 }}>Your Vouch Link</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ flex: 1, fontFamily: "'Spectral',serif", fontSize: 13, background: T.paperDark, padding: "10px 12px", color: T.ink, letterSpacing: "0.02em" }}>{shareUrl}</div>
                      <button className="btn btn-solid" style={{ padding: "0 16px", whiteSpace: "nowrap" }} onClick={() => { navigator.clipboard.writeText(shareUrl); }}>Copy</button>
                    </div>
                    <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, marginTop: 10, lineHeight: 1.6 }}>
                      Post your story card, then add this link to your Instagram bio so followers can find your Vouch.
                    </div>
                  </div>

                </div>
              </div>
            </div>
          );
        })()}

        {removeVouchModal && (
          <div className="modal-overlay" onClick={() => setRemoveVouchModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Remove Your Vouch</div>
                <button className="modal-x" onClick={() => setRemoveVouchModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkMid, marginBottom: 24, lineHeight: 1.7 }}>
                  What would you like to do with this Vouch?
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button className="btn btn-solid" onClick={() => removeActiveVouch("archive")} style={{ padding: "14px", textAlign: "left" }}>
                    <div style={{ fontSize: 11 }}>Move to Archive</div>
                    <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontWeight: 300, fontSize: 11, letterSpacing: 0, textTransform: "none", marginTop: 3, opacity: 0.7 }}>Saves it to your archive. You can republish it later.</div>
                  </button>
                  <button onClick={() => removeActiveVouch("delete")} style={{ padding: "14px", textAlign: "left", background: "transparent", border: `1px solid ${T.paperDark}`, fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.2em", cursor: "pointer", color: T.inkMid }}>
                    <div style={{ fontSize: 11 }}>Delete Permanently</div>
                    <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontWeight: 300, fontSize: 11, letterSpacing: 0, textTransform: "none", marginTop: 3, opacity: 0.7 }}>Removes it completely. This cannot be undone.</div>
                  </button>
                  <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 11, color: T.inkLight, textAlign: "center", marginTop: 4 }}>Either option resets your publish timer so you can post a new Vouch right away.</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showFeaturesAnnounce && (
          <FeaturesAnnounceModal onDismiss={dismissFeaturesAnnounce} />
        )}

        {showExpandVouchesPrompt && (
          <div className="modal-overlay">
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Previous Vouches</div>
              </div>
              <div className="modal-body">
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 14, color: T.inkMid, marginBottom: 20, lineHeight: 1.6 }}>
                  Would you like previous Vouches on <strong style={{ fontStyle: "normal" }}>your profile</strong> to stay expanded when others visit? You can change this anytime in Settings.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button className="btn btn-solid" style={{ padding: "14px" }} onClick={() => dismissExpandVouchesPrompt(true)}>
                    Yes — expand on my profile
                  </button>
                  <button className="btn btn-ghost" style={{ padding: "14px" }} onClick={() => dismissExpandVouchesPrompt(false)}>
                    No — collapse on my profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {musicPickerModal && (
          <div className="modal-overlay" onClick={() => setMusicPickerModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Open In</div>
                <button className="modal-x" onClick={() => setMusicPickerModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkMid, marginBottom: 20 }}>
                  Where would you like to open <strong style={{ fontStyle: "normal" }}>{musicPickerModal.title}</strong>?
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  <button className="btn btn-solid" style={{ padding: "14px", display: "flex", alignItems: "center", gap: 12 }} onClick={() => saveMusicPreference("spotify", musicPickerModal.url, musicPickerModal.title, musicPickerModal.sub)}>
                    <span style={{ fontSize: 20 }}>🎵</span>
                    <div style={{ textAlign: "left" }}>
                      <div>Spotify</div>
                      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontWeight: 300, fontSize: 10, letterSpacing: 0, textTransform: "none", opacity: 0.7, marginTop: 2 }}>Remember this choice</div>
                    </div>
                  </button>
                  <button onClick={() => saveMusicPreference("apple_music", musicPickerModal.url, musicPickerModal.title, musicPickerModal.sub)} style={{ padding: "14px", display: "flex", alignItems: "center", gap: 12, background: "transparent", border: `1px solid ${T.ink}`, fontFamily: "'Spectral SC',serif", fontSize: "10px", letterSpacing: "0.2em", cursor: "pointer", color: T.ink }}>
                    <span style={{ fontSize: 20 }}>🎵</span>
                    <div style={{ textAlign: "left" }}>
                      <div>Apple Music</div>
                      <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontWeight: 300, fontSize: 10, letterSpacing: 0, textTransform: "none", opacity: 0.7, marginTop: 2 }}>Remember this choice</div>
                    </div>
                  </button>
                </div>
                <button onClick={() => setMusicPickerModal(null)} style={{ width: "100%", background: "transparent", border: "none", fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.15em", color: T.inkFaint, cursor: "pointer", padding: "8px 0" }}>Maybe later</button>
              </div>
            </div>
          </div>
        )}

        {showContactModal && (
          <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Contact & Feedback</div>
                <button className="modal-x" onClick={() => setShowContactModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ fontFamily: "'Spectral',serif", fontStyle: "italic", fontSize: 13, color: T.inkLight, marginBottom: 16, lineHeight: 1.6 }}>Got feedback, a bug to report, or need help? Send a note.</div>
                <ContactForm userId={userId} userEmail={user?.username} onSent={() => setShowContactModal(false)} />
              </div>
            </div>
          </div>
        )}
        <footer style={{ borderTop: `3px double ${T.ink}`, marginTop: 64, padding: "24px 28px" }}>
          <div style={{ fontFamily: "'Spectral SC',serif", fontSize: "9px", letterSpacing: "0.18em", color: T.inkMid }}>
            © {new Date().getFullYear()} Vouch. All Rights Reserved.
          </div>
        </footer>
      </div>
    </>
  );
}